import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import usePermissions from './usePermissions';
import {
  RefreshCw, User, DollarSign, Check, X, ChevronDown, Search, 
  Filter, Trash2, Eye, Plus, Save, Download, Printer, CheckSquare, 
  Settings, CreditCard, ArrowUp, ArrowDown, File, PieChart, AlertCircle, // تأكد من وجودها
  Calendar, Building, Info, Clock, Clipboard, Activity, TrendingUp,
  UserCheck, FileText, Edit, Landmark, ChevronsUp, ChevronsDown, BarChart2,
  FileSpreadsheet
} from 'lucide-react';
import { toast } from 'react-hot-toast'; // Assumes react-hot-toast is installed

// Placeholder for imported components that should be created separately
import CommissionSettings from './CommissionSettings';
import CommissionStatusModal from './CommissionStatusModal';
import config from '../config';

// URLs للـ APIs الجديدة
// في بداية ملف DelegateAccounts.js، أضف:
const API_ENDPOINTS = {
  delegates: `${config.API_BASE_URL}/api/delegates`,
  delegateBalance: (id) => `${config.API_BASE_URL}/api/delegates/${id}/balance`,
  delegateClients: (id) => `${config.API_BASE_URL}/api/delegates/${id}/clients`,
  delegateTransactions: (id) => `${config.API_BASE_URL}/api/delegates/${id}/transactions`,
  payCommission: `${config.API_BASE_URL}/api/delegate-commissions/pay`,
  paymentMethods: `${config.API_BASE_URL}/api/payment-methods`,
  bankAccounts: `${config.API_BASE_URL}/api/bank-accounts`,
  accountCategories: `${config.API_BASE_URL}/api/account-categories`,
  companies: `${config.API_BASE_URL}/api/companies`,
  simpleSettings: `${config.API_BASE_URL}/api/simple-settings`,
  commissionSettings: `${config.API_BASE_URL}/api/commission-settings/all`,
  fixCommissionStatus: `${config.API_BASE_URL}/api/fix-commission-status`,
  clientDetails: (id) => `${config.API_BASE_URL}/api/clients/${id}/details`
};


const mobileModalClass = 'max-md:fixed max-md:inset-0 max-md:m-0 max-md:w-full max-md:max-w-none max-md:h-[100dvh] max-md:rounded-none max-md:border-0 max-md:flex max-md:flex-col';
const mobileModalBodyClass = 'max-md:flex-1 max-md:overflow-y-auto max-md:p-4 max-md:pb-28';

const CommissionConflictWarning = ({ client, onReassign, onViewHistory }) => {
  if (!client.commissionHistory?.isPaidByOtherDelegate) return null;
  
  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-yellow-400" />
        </div>
        <div className="mr-3 flex-1">
          <h3 className="text-sm font-medium text-yellow-800">
            تحذير: عمولة مدفوعة مسبقاً
          </h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>
              تم دفع عمولة لهذا العميل عند المندوب: 
              <span className="font-medium"> {client.commissionHistory.delegateName}</span>
            </p>
            <p>
              تاريخ الدفع: {new Date(client.commissionHistory.paidDate).toLocaleDateString('ar-EG')}
            </p>
            <p>
              المبلغ المدفوع: {client.commissionHistory.amount} ج.م
            </p>
          </div>
          <div className="mt-4 flex space-x-2 rtl:space-x-reverse">
            <button
              onClick={() => onViewHistory(client.PassportID)}
              className="text-sm bg-white border border-yellow-300 text-yellow-800 px-3 py-1 rounded hover:bg-yellow-50"
            >
              عرض سجل التحويلات
            </button>
            <button
              onClick={() => onReassign(client)}
              className="text-sm bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700"
            >
              إعادة تخصيص العمولة
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// مكون نافذة سجل التحويلات
const ClientCommissionTransactionsModal = ({ isOpen, onClose, clientId, clientName }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (isOpen && clientId) {
      fetchCommissionTransactions();
    }
  }, [isOpen, clientId]);
  
  const fetchCommissionTransactions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.API_BASE_URL}/api/client-commission-transactions/${clientId}`);
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setTransactions(data);
      } else {
        console.warn('البيانات المستلمة ليست مصفوفة:', data);
        setTransactions([]);
      }
    } catch (error) {
      console.error('خطأ في جلب سجل معاملات العمولة:', error);
      toast.error('خطأ في جلب سجل معاملات العمولة');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center max-md:items-end max-md:p-0">
      <div className={`relative mx-auto p-0 border w-full max-w-5xl shadow-lg rounded-lg bg-white overflow-hidden ${mobileModalClass}`}>
        <div className="bg-gradient-to-r from-green-600 to-green-700 py-4 px-6">
          <div className="flex justify-between items-center max-md:flex-col max-md:items-stretch max-md:gap-3">
            <h3 className="text-lg font-medium text-white">
              سجل معاملات العمولة للعميل: {clientName}
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        <div className={`p-6 ${mobileModalBodyClass}`}>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
              <span className="mr-3">جاري تحميل سجل المعاملات...</span>
            </div>
          ) : (!Array.isArray(transactions) || transactions.length === 0) ? (
            <div className="text-center py-8">
              <div className="flex flex-col items-center justify-center">
                <div className="text-gray-400 rounded-full bg-gray-100 p-3 mb-4">
                  <CreditCard size={30} />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">لا يوجد سجل معاملات</h3>
                <p className="text-sm text-gray-500">لم يتم دفع أي عمولة لهذا العميل حتى الآن</p>
              </div>
            </div>
          ) : (
            <div>
              {/* ملخص المعاملات */}
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="text-lg font-medium text-green-800 mb-2">ملخص المعاملات</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-green-600 font-medium">عدد المعاملات: </span>
                    <span className="font-bold">{transactions.length}</span>
                  </div>
                  <div>
                    <span className="text-green-600 font-medium">إجمالي المبالغ: </span>
                    <span className="font-bold">
                      {transactions.reduce((sum, tx) => sum + (tx.ClientCommissionAmount || 0), 0).toFixed(2)} ج.م
                    </span>
                  </div>
                  <div>
                    <span className="text-green-600 font-medium">آخر معاملة: </span>
                    <span className="font-bold">
                      {new Date(transactions[0]?.TransactionDate).toLocaleDateString('ar-EG')}
                    </span>
                  </div>
                </div>
              </div>

              {/* جدول المعاملات */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">رقم المعاملة</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">التاريخ</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">المندوب</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">مبلغ العمولة</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">إجمالي المعاملة</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">عدد العملاء</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الملاحظات</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">تاريخ الإنشاء</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transactions.map((transaction, index) => (
                      <tr key={transaction.TransactionID} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              #{transaction.TransactionID}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {new Date(transaction.TransactionDate).toLocaleDateString('ar-EG')}
                          <div className="text-xs text-gray-500">
                            {new Date(transaction.TransactionDate).toLocaleTimeString('ar-EG')}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center ml-3">
                              <User size={16} className="text-green-600" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {transaction.DelegateName}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID: {transaction.DelegateID}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
                            {transaction.ClientCommissionAmount?.toFixed(2) || '0.00'} ج.م
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {transaction.Amount?.toFixed(2)} ج.م
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {transaction.TotalClientsInTransaction || 1} عميل
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">
                          {transaction.Notes || '-'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(transaction.CreatedAt).toLocaleDateString('ar-EG')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


const formatReceiptDate = (dateString) => {
  if (!dateString) return 'غير محدد';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'غير محدد';
    return date.toLocaleString('ar-EG', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch (e) {
    return 'غير محدد';
  }
};

const parseTransactionRelatedData = (transaction) => {
  try {
    if (!transaction?.RelatedClients) return {};
    return typeof transaction.RelatedClients === 'string'
      ? JSON.parse(transaction.RelatedClients)
      : transaction.RelatedClients;
  } catch (e) {
    console.error('خطأ في قراءة تفاصيل المعاملة:', e);
    return {};
  }
};

const DelegateTransactionDetailsModal = ({ transaction, delegateName, onClose }) => {
  const relatedData = parseTransactionRelatedData(transaction);
  const clients = Array.isArray(relatedData.clients) ? relatedData.clients : [];
  const paymentMethod = relatedData.paymentMethod || {};
  const money = (value) => {
    const normalized = String(value ?? 0)
      .replace(/[٠-٩]/g, digit => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
      .replace(/[۰-۹]/g, digit => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
      .replace(/٬/g, '')
      .replace(/٫/g, '.')
      .replace(/,/g, '');
    const numeric = Number(normalized);
    const safe = Number.isFinite(numeric) ? numeric : 0;
    return `${safe.toLocaleString('en-US', {
      minimumFractionDigits: Number.isInteger(safe) ? 0 : 2,
      maximumFractionDigits: 2
    })} ج.م`;
  };
  const valueOrUnknown = (value) => value || 'غير محدد';
  const clientStatus = (client) => client?.clientStatus || client?.oldNewStatus || (client?.isOldClient || client?.isPreviousCompany ? 'قديم' : 'جديد');
  const operationType = relatedData.operationType || relatedData.financialOperationType || '';
  const isDebtOperation = ['advance', 'repay_advance', 'manual_debt'].includes(operationType)
    || relatedData.createdFrom === 'delegate_financial_operation'
    || relatedData.createdFrom === 'manual_debt_without_treasury'
    || String(transaction.TransactionType || '').includes('سلفة')
    || String(transaction.TransactionType || '').includes('مديونية');
  const txType = relatedData.operationLabel || transaction.TransactionType || 'معاملة';
  const operationDetails = Array.isArray(relatedData.operationDetails) && relatedData.operationDetails.length > 0
    ? relatedData.operationDetails
    : (Array.isArray(relatedData.debtDetails) && relatedData.debtDetails.length > 0
      ? relatedData.debtDetails
      : (Array.isArray(relatedData.paymentDetails) ? relatedData.paymentDetails : []));
  const commissionRequested = relatedData.commissionRequested ?? relatedData.amount ?? transaction.Amount;
  const debtReduction = relatedData.debtReduction || 0;
  const actualPayment = relatedData.actualPayment ?? transaction.Amount;
  const paidDebtsCount = operationDetails.length;
  const totalRemainingAfterOperation = operationDetails.reduce((sum, item) => {
    const value = item.afterRemainingAmount ?? item.remainingAmount;
    const numeric = Number(String(value ?? 0).replace(/,/g, ''));
    return sum + (Number.isFinite(numeric) ? numeric : 0);
  }, 0);
  const detailType = (detail) => detail.debtType || (detail.sourceOperationType === 'manual_debt' ? 'مديونية' : 'سلفة');
  const detailAmount = (detail) => detail.paidAmount ?? detail.deductedAmount ?? detail.amount ?? detail.originalAmount ?? 0;
  const detailBefore = (detail) => detail.beforeRemainingAmount ?? detail.previousRemainingAmount ?? null;
  const detailAfter = (detail) => detail.afterRemainingAmount ?? detail.remainingAmount ?? null;
  const operationSubtitle = isDebtOperation
    ? 'تفاصيل عملية سلفة/مديونية مع توضيح أثرها على الخزينة وحساب المندوب'
    : 'نافذة واضحة مع إمكانية تصدير أو نسخ صورة العمولة مباشرة';
  const summaryCards = isDebtOperation ? [
    ['نوع العملية', txType, '#eff6ff'],
    [operationType === 'repay_advance' ? 'المبلغ المستلم من المندوب' : 'قيمة البند', money(transaction.Amount), operationType === 'repay_advance' ? '#ecfdf5' : '#fff7ed'],
    ['أثر الخزينة', relatedData.treasuryEffect || (relatedData.doesAffectTreasury === false ? 'لا توجد حركة خزينة' : (relatedData.bankDirection === 'out' ? 'سحب من الحساب' : 'إيداع في الحساب')), '#f8fafc'],
    [operationType === 'repay_advance' ? 'عدد البنود المسددة' : 'المتبقي على المندوب', operationType === 'repay_advance' ? `${paidDebtsCount} بند` : money(totalRemainingAfterOperation || transaction.Amount), '#f0fdf4']
  ] : [
    ['نوع المعاملة', txType, '#eff6ff'],
    ['إجمالي العمولة/المبلغ', money(commissionRequested), '#f0fdf4'],
    ['خصم السلف/المديونيات', money(debtReduction), '#fff7ed'],
    ['الصافي الفعلي للمندوب', money(actualPayment), '#ecfdf5']
  ];

  const buildCanvas = async () => {
    const width = 1500;
    const rowHeight = 72;
    const detailRowsCount = isDebtOperation ? Math.max(operationDetails.length, 1) : Math.max(clients.length, 1);
    const clientsHeight = detailRowsCount * rowHeight;
    const height = 760 + clientsHeight;
    const canvas = document.createElement('canvas');
    const scale = 2;
    canvas.width = width * scale;
    canvas.height = height * scale;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);
    ctx.direction = 'rtl';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';

    const roundRect = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    };

    const drawText = (text, x, y, font = '30px Arial', color = '#111827', align = 'right') => {
      ctx.font = font;
      ctx.fillStyle = color;
      ctx.textAlign = align;
      ctx.fillText(String(text ?? ''), x, y);
      ctx.textAlign = 'right';
    };

    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, width, height);

    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    gradient.addColorStop(0, '#2563eb');
    gradient.addColorStop(1, '#0f172a');
    ctx.fillStyle = gradient;
    roundRect(50, 40, width - 100, 145, 28);
    ctx.fill();

    drawText(isDebtOperation ? 'تفاصيل عملية سلفة / مديونية' : 'تفاصيل عمولة / معاملة مندوب', width - 90, 90, 'bold 42px Arial', '#ffffff');
    drawText(`#${transaction.TransactionID}`, 90, 90, 'bold 34px Arial', '#dbeafe', 'left');
    drawText(`التاريخ: ${formatReceiptDate(transaction.Date)}`, width - 90, 145, '26px Arial', '#dbeafe');
    drawText(`المندوب: ${delegateName || 'غير محدد'}`, 90, 145, '26px Arial', '#dbeafe', 'left');

    const cards = summaryCards;
    cards.forEach((card, index) => {
      const x = 50 + index * 350;
      ctx.fillStyle = card[2];
      roundRect(x, 220, 320, 115, 22);
      ctx.fill();
      drawText(card[0], x + 285, 255, '22px Arial', '#64748b');
      drawText(card[1], x + 285, 300, 'bold 30px Arial', '#111827');
    });

    ctx.fillStyle = '#ffffff';
    roundRect(50, 370, width - 100, 120, 22);
    ctx.fill();
    drawText('طريقة الدفع / الحساب', width - 90, 410, 'bold 26px Arial', '#111827');
    drawText(valueOrUnknown(paymentMethod.name || paymentMethod.Name), width - 90, 455, '25px Arial', '#334155');
    drawText('أضيف بواسطة', 520, 410, 'bold 24px Arial', '#111827');
    drawText(valueOrUnknown(transaction.CreatedByFullName || transaction.CreatedBy), 520, 455, '24px Arial', '#334155');
    drawText('الرصيد التراكمي', 220, 410, 'bold 24px Arial', '#111827');
    drawText(money(transaction.Balance), 220, 455, '24px Arial', '#16a34a');

    ctx.fillStyle = '#ffffff';
    roundRect(50, 525, width - 100, clientsHeight + 115, 22);
    ctx.fill();
    drawText(isDebtOperation ? 'تفاصيل السلفة/المديونية أو السداد' : 'العملاء المرتبطون بالمعاملة', width - 90, 570, 'bold 28px Arial', '#111827');

    ctx.fillStyle = '#e2e8f0';
    roundRect(80, 610, width - 160, 52, 14);
    ctx.fill();

    if (isDebtOperation) {
      drawText('البند', width - 115, 636, 'bold 21px Arial', '#334155');
      drawText('النوع', width - 300, 636, 'bold 21px Arial', '#334155');
      drawText('البيان', width - 520, 636, 'bold 21px Arial', '#334155');
      drawText('قبل السداد', width - 820, 636, 'bold 21px Arial', '#334155');
      drawText('المبلغ', width - 1040, 636, 'bold 21px Arial', '#334155');
      drawText('بعد السداد', width - 1260, 636, 'bold 21px Arial', '#334155');

      if (operationDetails.length === 0) {
        drawText('لا توجد تفاصيل سداد مرتبطة بهذه العملية', width - 120, 710, '26px Arial', '#64748b');
      } else {
        operationDetails.forEach((detail, index) => {
          const y = 700 + index * rowHeight;
          ctx.fillStyle = index % 2 === 0 ? '#ffffff' : '#f8fafc';
          ctx.fillRect(80, y - 32, width - 160, rowHeight);
          drawText(`#${detail.debtId || '-'}`, width - 115, y, '22px Arial', '#111827');
          drawText(detailType(detail), width - 300, y, 'bold 21px Arial', detailType(detail) === 'مديونية' ? '#7e22ce' : '#c2410c');
          drawText(String(detail.reason || detail.causeInfo || detail.actionLabel || 'غير محدد').slice(0, 35), width - 520, y, '20px Arial', '#111827');
          drawText(detailBefore(detail) === null ? '-' : money(detailBefore(detail)), width - 820, y, '21px Arial', '#111827');
          drawText(money(detailAmount(detail)), width - 1040, y, 'bold 21px Arial', '#15803d');
          drawText(detailAfter(detail) === null ? '-' : money(detailAfter(detail)), width - 1260, y, '21px Arial', '#111827');
        });
      }
    } else {
      drawText('الاسم', width - 115, 636, 'bold 21px Arial', '#334155');
      drawText('الحالة', width - 335, 636, 'bold 21px Arial', '#334155');
      drawText('الشركة', width - 535, 636, 'bold 21px Arial', '#334155');
      drawText('تاريخ السفر', width - 785, 636, 'bold 21px Arial', '#334155');
      drawText('مدة/مهنة السفر', width - 1055, 636, 'bold 21px Arial', '#334155');
      drawText('رقم الجواز', width - 1290, 636, 'bold 21px Arial', '#334155');

      if (clients.length === 0) {
        drawText('لا توجد بيانات عملاء مرتبطة بهذه المعاملة', width - 120, 710, '26px Arial', '#64748b');
      } else {
        clients.forEach((client, index) => {
          const y = 700 + index * rowHeight;
          ctx.fillStyle = index % 2 === 0 ? '#ffffff' : '#f8fafc';
          ctx.fillRect(80, y - 32, width - 160, rowHeight);
          const travelDate = client.travelDateFormatted || (client.travelDate ? formatReceiptDate(client.travelDate) : 'غير محدد');
          const profession = [client.travelDuration, client.travelProfession].filter(Boolean).join(' / ') || 'غير محدد';
          drawText(valueOrUnknown(client.name), width - 115, y, '22px Arial', '#111827');
          drawText(clientStatus(client), width - 335, y, 'bold 21px Arial', clientStatus(client) === 'قديم' ? '#b45309' : '#15803d');
          drawText(valueOrUnknown(client.companyName), width - 535, y, '22px Arial', '#111827');
          drawText(travelDate, width - 785, y, '22px Arial', '#111827');
          drawText(profession, width - 1055, y, '21px Arial', '#111827');
          drawText(valueOrUnknown(client.passportNumber), width - 1290, y, '21px Arial', '#111827');
        });
      }
    }

    const notesY = 670 + clientsHeight + 95;
    ctx.fillStyle = '#fff7ed';
    roundRect(50, notesY, width - 100, 105, 22);
    ctx.fill();
    drawText('ملاحظات المعاملة', width - 90, notesY + 35, 'bold 25px Arial', '#9a3412');
    const cleanNotes = String(transaction.Notes || relatedData.updatedNotes || 'لا توجد ملاحظات').slice(0, 170);
    drawText(cleanNotes, width - 90, notesY + 75, '22px Arial', '#7c2d12');

    drawText('تم إنشاء هذه الصورة من نظام مسار - حسابات المناديب', width / 2, height - 35, '20px Arial', '#64748b', 'center');
    return canvas;
  };

  const exportAsImage = async () => {
    try {
      const canvas = await buildCanvas();
      const link = document.createElement('a');
      link.download = `delegate-transaction-${transaction.TransactionID}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
      toast.success('تم تصدير الصورة بجودة عالية');
    } catch (error) {
      console.error('خطأ في تصدير الصورة:', error);
      toast.error('تعذر تصدير الصورة');
    }
  };

  const copyImage = async () => {
    try {
      const canvas = await buildCanvas();
      canvas.toBlob(async (blob) => {
        try {
          if (!navigator.clipboard || !window.ClipboardItem) {
            toast.error('نسخ الصور غير مدعوم في هذا المتصفح. استخدم زر التصدير كصورة.');
            return;
          }
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
          toast.success('تم نسخ صورة المعاملة، يمكنك لصقها مباشرة');
        } catch (copyError) {
          console.error('خطأ في النسخ:', copyError);
          toast.error('المتصفح منع النسخ. جرّب فتح الموقع عبر HTTPS أو استخدم تصدير كصورة.');
        }
      }, 'image/png', 1.0);
    } catch (error) {
      console.error('خطأ في تجهيز الصورة:', error);
      toast.error('تعذر تجهيز الصورة للنسخ');
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 overflow-y-auto h-full w-full z-[90] flex items-center justify-center max-md:items-end max-md:p-0" dir="rtl">
      <div className={`relative mx-auto p-0 border w-full max-w-6xl shadow-2xl rounded-3xl bg-white overflow-hidden ${mobileModalClass}`}>
        <div className="bg-gradient-to-r from-slate-900 via-blue-800 to-indigo-700 py-5 px-6">
          <div className="flex justify-between items-start gap-4 max-md:flex-col max-md:items-stretch">
            <div>
              <h3 className="text-xl font-bold text-white">تفاصيل المعاملة #{transaction.TransactionID}</h3>
              <p className="text-blue-100 text-sm mt-1">{operationSubtitle}</p>
            </div>
            <div className="flex items-center gap-2 max-md:grid max-md:grid-cols-3">
              <button onClick={copyImage} className="bg-white text-blue-700 hover:bg-blue-50 px-4 py-2 rounded-xl text-sm font-bold flex items-center justify-center gap-2">
                <Clipboard size={16} /> نسخ الصورة
              </button>
              <button onClick={exportAsImage} className="bg-emerald-500 text-white hover:bg-emerald-600 px-4 py-2 rounded-xl text-sm font-bold flex items-center justify-center gap-2">
                <Download size={16} /> تصدير كصورة
              </button>
              <button onClick={onClose} className="bg-white/10 text-white hover:bg-white/20 px-3 py-2 rounded-xl flex items-center justify-center">
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        <div className={`p-6 ${mobileModalBodyClass}`}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {summaryCards.map((card, index) => (
              <div key={index} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                <p className="text-xs text-slate-500 mb-1">{card[0]}</p>
                <p className="font-bold text-slate-900">{card[1]}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                {isDebtOperation ? <AlertCircle size={18} className="text-orange-600" /> : <UserCheck size={18} className="text-blue-600" />}
                <h4 className="font-bold text-gray-900">{isDebtOperation ? 'تفاصيل السلفة/المديونية والسداد' : 'العملاء المرتبطون'}</h4>
              </div>

              {isDebtOperation ? (
                operationDetails.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">لا توجد تفاصيل سداد أو بند مرتبطة بهذه العملية</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">رقم البند</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">النوع</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">البيان</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">قبل السداد</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">مبلغ العملية</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">بعد السداد</th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">الحالة</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {operationDetails.map((detail, index) => (
                          <tr key={`${detail.debtId || 'detail'}-${detail.paymentId || index}`} className="hover:bg-orange-50/40">
                            <td className="px-4 py-3 text-sm font-bold text-gray-900">#{detail.debtId || '-'}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${detailType(detail) === 'مديونية' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                                {detailType(detail)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 max-w-xs">
                              <div className="font-medium text-gray-900">{detail.actionLabel || (operationType === 'repay_advance' ? 'رد سلفة / سداد مديونية' : txType)}</div>
                              <div className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{detail.reason || detail.causeInfo || transaction.Notes || 'غير محدد'}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">{detailBefore(detail) === null ? '-' : money(detailBefore(detail))}</td>
                            <td className="px-4 py-3 text-sm font-bold text-green-700">{money(detailAmount(detail))}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{detailAfter(detail) === null ? '-' : money(detailAfter(detail))}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${(detail.newStatus || '').includes('مسددة') || Number(detailAfter(detail) || 0) === 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {detail.newStatus || (Number(detailAfter(detail) || 0) === 0 ? 'مسددة' : 'جزئية/معلقة')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : clients.length === 0 ? (
                <div className="p-6 text-center text-gray-500">لا توجد بيانات عملاء مرتبطة بهذه المعاملة</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">العميل</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">الشركة</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">الحالة</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">تاريخ السفر</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">مدة/مهنة السفر</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">الجواز</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {clients.map((client, index) => {
                        const travelDate = client.travelDateFormatted || (client.travelDate ? formatReceiptDate(client.travelDate) : 'غير محدد');
                        const profession = [client.travelDuration, client.travelProfession].filter(Boolean).join(' / ') || 'غير محدد';
                        return (
                          <tr key={client.id || index} className="hover:bg-blue-50/40">
                            <td className="px-4 py-3 text-sm font-bold text-gray-900">{valueOrUnknown(client.name)}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{valueOrUnknown(client.companyName)}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${clientStatus(client) === 'قديم' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                {clientStatus(client)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">{travelDate}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{profession}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{valueOrUnknown(client.passportNumber)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><CreditCard size={18} /> الدفع والحساب</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between gap-3"><span className="text-gray-500">طريقة الدفع</span><span className="font-bold text-gray-900">{valueOrUnknown(paymentMethod.name || paymentMethod.Name)}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-gray-500">تاريخ المعاملة</span><span className="font-bold text-gray-900">{formatReceiptDate(transaction.Date)}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-gray-500">أضيف بواسطة</span><span className="font-bold text-gray-900">{valueOrUnknown(transaction.CreatedByFullName || transaction.CreatedBy)}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-gray-500">الرصيد التراكمي</span><span className="font-bold text-green-700">{money(transaction.Balance)}</span></div>
                </div>
              </div>

              {Array.isArray(relatedData.debtDetails || relatedData.paymentDetails) && (relatedData.debtDetails || relatedData.paymentDetails).length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4">
                  <h4 className="font-bold text-orange-900 mb-3">تفاصيل السداد/الخصم</h4>
                  {(relatedData.debtDetails || relatedData.paymentDetails).map((detail, index) => (
                    <div key={index} className="text-sm border-b border-orange-100 last:border-0 py-2">
                      <div className="font-bold text-orange-800">{money(detail.deductedAmount || detail.paidAmount)} تم خصمها/سدادها</div>
                      <div className="text-orange-700">{detail.reason || detail.causeInfo || 'غير محدد'}</div>
                      <div className="text-xs text-orange-600">المتبقي: {money(detail.remainingAmount)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <h4 className="font-bold text-amber-900 mb-2">ملاحظات</h4>
            <p className="text-sm text-amber-900 whitespace-pre-wrap">{transaction.Notes || relatedData.updatedNotes || 'لا توجد ملاحظات'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};




const DelegateAccounts = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canDo = (permission) => hasPermission('حسابات المناديب - الصلاحيات', permission);

  const getCurrentUser = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.Username || 'System';
    } catch { return 'System'; }
  };


  const toSafeNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const arabicDigits = '٠١٢٣٤٥٦٧٨٩';
    const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
    let text = String(value).trim()
      .replace(/[٠-٩]/g, digit => String(arabicDigits.indexOf(digit)))
      .replace(/[۰-۹]/g, digit => String(persianDigits.indexOf(digit)))
      .replace(/٬/g, '')
      .replace(/٫/g, '.')
      .replace(/\s+/g, '');
    if (text.includes(',') && (!text.includes('.') || text.lastIndexOf('.') > text.lastIndexOf(','))) {
      text = text.replace(/,/g, '');
    } else if (text.includes(',') && !text.includes('.')) {
      text = text.replace(',', '.');
    }
    const num = Number(text.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(num) ? Math.round(num * 100) / 100 : 0;
  };

  const formatMoneyValue = (value) => {
    const safe = toSafeNumber(value);
    return `${safe.toLocaleString('en-US', {
      minimumFractionDigits: Number.isInteger(safe) ? 0 : 2,
      maximumFractionDigits: 2
    })} ج.م`;
  };

  const mobileModalClass = 'max-md:fixed max-md:inset-0 max-md:m-0 max-md:w-full max-md:max-w-none max-md:h-[100dvh] max-md:rounded-none max-md:border-0 max-md:flex max-md:flex-col';
  const mobileModalBodyClass = 'max-md:flex-1 max-md:overflow-y-auto max-md:p-4 max-md:pb-28';

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
const [exportOptions, setExportOptions] = useState({
  format: 'excel', // excel, csv, pdf
  dataType: 'clients', // clients, transactions, stats
  includeFilters: true,
  includeSummary: true,
  dateRange: 'all', // all, filtered, custom
  customStartDate: '',
  customEndDate: '',
  selectedStatuses: ['all'], // all, traveled, paid, unpaid
});
  
  // Main state
  const [delegates, setDelegates] = useState([]);
  const [selectedDelegate, setSelectedDelegate] = useState(null);
  const [delegateInfo, setDelegateInfo] = useState(null);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [delegateTransactions, setDelegateTransactions] = useState([]);
  
// أضف هذه الحالات مع باقي useState في بداية المكون
const [showEditTransactionModal, setShowEditTransactionModal] = useState(false);
const [editingTransaction, setEditingTransaction] = useState(null);
const [editTransactionForm, setEditTransactionForm] = useState({
  type: '',
  amount: '',
  notes: '',
  date: '',
  paymentMethodId: null,
  clientDetails: {
    name: '',
    nationalId: '',
    companyName: '',
    delegateName: '',
    phoneNumbers: ''
  }
});


  // إضافة هذا مع باقي الحالات في بداية المكون
const [showConfirmDialog, setShowConfirmDialog] = useState(false);
const [confirmMessage, setConfirmMessage] = useState('');
const [confirmCallback, setConfirmCallback] = useState(null);



  // UI state
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('clients'); // 'clients', 'transactions', 'stats'
  const [showFilters, setShowFilters] = useState(false);
  const [activeView, setActiveView] = useState('table'); // 'card', 'table'  
  // Modals and forms state
  const [showCommissionSettings, setShowCommissionSettings] = useState(false);
  const [showCommissionForm, setShowCommissionForm] = useState(false);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [showCommissionDetails, setShowCommissionDetails] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showCommissionStatusModal, setShowCommissionStatusModal] = useState(false);
  // إضافة هذه المتغيرات مع باقي state variables في بداية المكون
const [showCommissionTransactionsModal, setShowCommissionTransactionsModal] = useState(false);
const [selectedClientForTransactions, setSelectedClientForTransactions] = useState(null);
const [showReassignModal, setShowReassignModal] = useState(false);
const [selectedClientForReassign, setSelectedClientForReassign] = useState(null);
const [showDebtConfirmModal, setShowDebtConfirmModal] = useState(false);
const [debtConfirmData, setDebtConfirmData] = useState(null);






  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);


  const [categories, setCategories] = useState([]);  // قائمة التصنيفات
  const [selectedCategoryId, setSelectedCategoryId] = useState(null); // التصنيف المحدد
  const [filteredPaymentMethods, setFilteredPaymentMethods] = useState([]); // طرق الدفع مفلترة حسب التصنيف



  const [transactionDateFilter, setTransactionDateFilter] = useState({
    from: '',
    to: ''
  });
  const [showTransactionFilters, setShowTransactionFilters] = useState(false);
   
// متغيرات حالة تحديد المعاملات
const [selectedTransactions, setSelectedTransactions] = useState([]);
const [allTransactionsSelected, setAllTransactionsSelected] = useState(false);


  // Selection state
  const [selectedClients, setSelectedClients] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);


  // إضافة هذه المتغيرات مع باقي المتغيرات
const [showDebtsModal, setShowDebtsModal] = useState(false);
const [debts, setDebts] = useState([]);
const [debtsHistory, setDebtsHistory] = useState([]);
const [showAddDebtForm, setShowAddDebtForm] = useState(false);
const [editingDebt, setEditingDebt] = useState(null);
const [newDebt, setNewDebt] = useState({
  amount: '',
  reason: '',
  priority: 1,
  debtType: 'مديونية',
  date: new Date().toISOString().split('T')[0]
});
const [debtOperationForm, setDebtOperationForm] = useState({
  operationType: 'advance',
  amount: '',
  notes: '',
  date: new Date().toISOString().split('T')[0],
  categoryId: '',
  paymentMethodId: '',
  debtId: ''
});
const [debtOperationPaymentMethods, setDebtOperationPaymentMethods] = useState([]);
const [isDebtOperationLoading, setIsDebtOperationLoading] = useState(false);
  
  // Filter state
// تحديث متغير filters في بداية المكون
const [filters, setFilters] = useState({
  showAll: false,
  onlyTraveled: false,
  onlyPaid: false,
  onlyUnpaid: false,
  onlyTraveledUnpaid: true // الوضع الافتراضي الجديد
});
  
  // Settings state
  const [settings, setSettings] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [globalSettings, setGlobalSettings] = useState({
    contractCommission: 200,
    taqeebCommission: 150,
    useGlobalSettings: true
  });
  
  // Stats state
  const [stats, setStats] = useState({
    totalClients: 0,
    traveledClients: 0,
    paidCommission: 0,
    unpaidCommission: 0,
    totalCommission: 0
  });
  
  // Form state
  const [commissionForm, setCommissionForm] = useState({
    amount: '',
    notes: '',
    date: new Date().toISOString().split('T')[0] // فقط التاريخ للنموذج، سيتم إضافة الوقت عند الإرسال
  });
  
  const [transactionForm, setTransactionForm] = useState({
    type: 'إيداع',
    amount: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });
  
  const [commissionDetails, setCommissionDetails] = useState({
    clients: [],
    totalAmount: 0
  });




  // دالة جلب السلف
const fetchDebts = async () => {
  try {
    if (!selectedDelegate) return;
    
const [activeResponse, historyResponse] = await Promise.all([
  fetch(`${config.API_BASE_URL}/api/delegates/${selectedDelegate.DelegateID}/debts`),
  fetch(`${config.API_BASE_URL}/api/delegates/${selectedDelegate.DelegateID}/debts-history`)
]);
    
    if (activeResponse.ok) {
      const activeData = await activeResponse.json();
      setDebts(activeData);
    }
    
    if (historyResponse.ok) {
      const historyData = await historyResponse.json();
      setDebtsHistory(historyData);
    }
  } catch (error) {
    console.error('خطأ في جلب السلف:', error);
    toast.error('حدث خطأ أثناء جلب السلف');
  }
};

// دالة إضافة مديونية يدوية بدون خزينة
const handleAddDebt = async (e) => {
  e.preventDefault();
  
  if (!newDebt.amount || !newDebt.reason) {
    toast.error('يرجى ملء جميع الحقول المطلوبة');
    return;
  }
  
  try {
    const response = await fetch(`${config.API_BASE_URL}/api/debts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        delegateId: selectedDelegate.DelegateID,
        amount: toSafeNumber(newDebt.amount),
        reason: newDebt.reason,
        debtType: newDebt.debtType || 'مديونية',
        priority: newDebt.priority,
        date: newDebt.date,
        createdBy: getCurrentUser()
      })
    });
    const data = await response.json().catch(() => ({}));
    
    if (response.ok) {
      toast.success(data.message || 'تم إضافة المديونية بدون أي حركة في الخزينة بنجاح');
      setNewDebt({ amount: '', reason: '', priority: 1, debtType: 'مديونية', date: new Date().toISOString().split('T')[0] });
      setShowAddDebtForm(false);
      await Promise.all([
        fetchDebts(),
        fetchDelegateInfo(selectedDelegate.DelegateID),
        fetchDelegateTransactions(selectedDelegate.DelegateID)
      ]);
    } else {
      throw new Error(data.details || data.error || 'فشل في إضافة المديونية');
    }
  } catch (error) {
    console.error('خطأ في إضافة المديونية:', error);
    toast.error(error.message || 'حدث خطأ أثناء إضافة المديونية');
  }
};

// دالة تعديل سلفة/مديونية
const handleEditDebt = async (debtId, updatedData) => {
  try {
    const response = await fetch(`${config.API_BASE_URL}/api/debts/${debtId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedData)
    });
    
    const data = await response.json().catch(() => ({}));
    if (response.ok) {
      toast.success(data.message || 'تم تحديث السلفة/المديونية بنجاح');
      setEditingDebt(null);
      await Promise.all([
        fetchDebts(),
        fetchDelegateInfo(selectedDelegate.DelegateID),
        fetchDelegateTransactions(selectedDelegate.DelegateID)
      ]);
    } else {
      throw new Error(data.details || data.error || 'فشل في تحديث السلفة/المديونية');
    }
  } catch (error) {
    console.error('خطأ في تحديث السلفة/المديونية:', error);
    toast.error('حدث خطأ أثناء تحديث السلفة/المديونية');
  }
};

// دالة حذف سلفة/مديونية
const handleDeleteDebt = async (debtId) => {
  try {
    const response = await fetch(`${config.API_BASE_URL}/api/debts/${debtId}`, {
      method: 'DELETE'
    });
    const data = await response.json().catch(() => ({}));
    
    if (response.ok) {
      toast.success(data.message || 'تم حذف السلفة/المديونية بنجاح');
      await Promise.all([
        fetchDebts(),
        fetchDelegateInfo(selectedDelegate.DelegateID),
        fetchDelegateTransactions(selectedDelegate.DelegateID)
      ]);
    } else {
      throw new Error(data.details || data.error || 'فشل في حذف السلفة/المديونية');
    }
  } catch (error) {
    console.error('خطأ في حذف السلفة/المديونية:', error);
    toast.error(error.message || 'حدث خطأ أثناء حذف السلفة/المديونية');
  }
};


// جلب طرق الدفع الخاصة بعمليات السلفة بدون التأثير على نموذج دفع العمولة
const fetchDebtOperationPaymentMethods = async (categoryId = '') => {
  try {
    setIsDebtOperationLoading(true);
    const methods = await loadBankAccountsSafely(categoryId || null);
    setDebtOperationPaymentMethods(methods);
  } catch (error) {
    console.error('خطأ في جلب طرق دفع عمليات السلفة:', error.details || error);
    toast.error('تعذر تحميل طرق الدفع لهذه العملية');
    setDebtOperationPaymentMethods([]);
  } finally {
    setIsDebtOperationLoading(false);
  }
};

const handleDebtOperationCategoryChange = async (categoryId) => {
  setDebtOperationForm(prev => ({ ...prev, categoryId, paymentMethodId: '' }));
  await fetchDebtOperationPaymentMethods(categoryId);
};

const handleDelegateFinancialOperation = async (e) => {
  e.preventDefault();
  if (!selectedDelegate) return;

  if (!['advance', 'repay_advance'].includes(debtOperationForm.operationType)) {
    toast.error('نوع العملية غير مسموح به. المتاح فقط: أخذ سلفة أو رد سلفة/سداد مديونية');
    setDebtOperationForm(prev => ({ ...prev, operationType: 'advance', debtId: '' }));
    return;
  }

  const amount = toSafeNumber(debtOperationForm.amount);
  if (!amount || amount <= 0) {
    toast.error('يرجى إدخال مبلغ صحيح');
    return;
  }
  if (!debtOperationForm.paymentMethodId) {
    toast.error('يرجى اختيار طريقة الدفع / الحساب');
    return;
  }

  const selectedMethod = debtOperationPaymentMethods.find(method => String(method.id) === String(debtOperationForm.paymentMethodId));
  if (!selectedMethod) {
    toast.error('طريقة الدفع المحددة غير صحيحة');
    return;
  }

  try {
    setIsDebtOperationLoading(true);
    const response = await fetch(`${config.API_BASE_URL}/api/delegates/${selectedDelegate.DelegateID}/financial-operation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        operationType: debtOperationForm.operationType,
        amount,
        notes: debtOperationForm.notes,
        date: debtOperationForm.date,
        paymentMethod: selectedMethod,
        debtId: debtOperationForm.debtId || null,
        createdBy: getCurrentUser()
      })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.details || data.error || 'فشل تنفيذ العملية المالية');
    }

    toast.success(data.message || 'تم تنفيذ العملية المالية بنجاح');
    setDebtOperationForm({
      operationType: 'advance',
      amount: '',
      notes: '',
      date: new Date().toISOString().split('T')[0],
      categoryId: debtOperationForm.categoryId,
      paymentMethodId: '',
      debtId: ''
    });

    await Promise.all([
      fetchDebts(),
      fetchDelegateInfo(selectedDelegate.DelegateID),
      fetchDelegateTransactions(selectedDelegate.DelegateID)
    ]);
  } catch (error) {
    console.error('خطأ في تنفيذ عملية مالية للمندوب:', error);
    toast.error(error.message || 'حدث خطأ أثناء تنفيذ العملية المالية');
  } finally {
    setIsDebtOperationLoading(false);
  }
};


  // Helper Functions
  const fetchWithRetry = async (url, options = {}, maxRetries = 3) => {
    let lastError;
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        console.log(`محاولة الاتصال ${i+1} لـ ${url}`);
        const response = await fetch(url, options);
        if (!response.ok) {
          throw new Error(`الاستجابة غير ناجحة: ${response.status}`);
        }
        return response;
      } catch (error) {
        console.warn(`فشل المحاولة ${i+1}:`, error.message);
        lastError = error;
        // انتظار قبل إعادة المحاولة
        if (i < maxRetries - 1) {
          await new Promise(r => setTimeout(r, 1000 * (i + 1)));
        }
      }
    }
    
    throw lastError;
  };

  
  // دالة للتحقق من تاريخ العمولة
// دالة للتحقق من تاريخ العمولة
// في DelegateAccounts.js - استبدل دالة checkCommissionHistory
const checkCommissionHistory = async (clientId) => {
  try {
    if (!selectedDelegate || !selectedDelegate.DelegateID) {
      console.log('لا يوجد مندوب محدد');
      return { isPaidByOtherDelegate: false };
    }
    
    const response = await fetch(`${config.API_BASE_URL}/api/clients/${clientId}/commission-history`);
    
    if (!response.ok) {
      console.log('فشل في جلب تاريخ العمولة');
      return { isPaidByOtherDelegate: false };
    }
    
    const data = await response.json();
    console.log('بيانات تاريخ العمولة:', data);
    
    // تحسين الشرط للتحقق
    if (data.commissionPaidBy && parseInt(data.commissionPaidBy) !== parseInt(selectedDelegate.DelegateID)) {
      return {
        isPaidByOtherDelegate: true,
        delegateName: data.delegateName,
        delegateId: data.commissionPaidBy,
        paidDate: data.paidDate,
        amount: data.amount
      };
    }
    
    return { isPaidByOtherDelegate: false };
  } catch (error) {
    console.error('خطأ في التحقق من تاريخ العمولة:', error);
    return { isPaidByOtherDelegate: false };
  }
};



  const getClientData = (clientId) => {
    // محاولة العثور على العميل في قائمة clients
    let clientData = null;
    
    // التحقق من وجود قائمة clients و filteredClients
    if (Array.isArray(clients)) {
      clientData = clients.find(c => c.PassportID === clientId);
    }
    
    // البحث في filteredClients إذا لم يتم العثور عليه
    if (!clientData && Array.isArray(filteredClients)) {
      clientData = filteredClients.find(c => c.PassportID === clientId);
    }
    
    if (clientData) {
      // محاولة معرفة ما إذا كان العميل قديم شركات
      let isPreviousCompany = false;
      
      try {
        // فحص خاصية PreviousCompanies
        if (clientData.PreviousCompanies) {
          let previousCompanies;
          
          if (typeof clientData.PreviousCompanies === 'string') {
            previousCompanies = JSON.parse(clientData.PreviousCompanies);
          } else {
            previousCompanies = clientData.PreviousCompanies;
          }
          
          // التحقق من أن الشركة الحالية موجودة في الشركات السابقة
          if (Array.isArray(previousCompanies)) {
            isPreviousCompany = previousCompanies.some(prevCompanyId => 
              String(prevCompanyId) === String(clientData.CompanyID)
            );
          }
        } else {
          // فحص حقل قديم_شركات إذا كان موجودًا
          isPreviousCompany = clientData.قديم_شركات || false;
        }
      } catch (error) {
        console.error("خطأ في تحديد حالة قديم شركات:", error);
        isPreviousCompany = false;
      }
      
      // تحديد العمولة المستحقة، مع مراعاة مختلف أسماء الحقول المحتملة
      const commission = 
        clientData.calculatedCommission || 
        clientData.العمولة_المستحقة || 
        (typeof calculateCommission === 'function' ? calculateCommission(clientData) : null);
      
      return {
        name: clientData.NameArabic || "غير محدد",
        companyName: clientData.CompanyName || "غير محدد",
        travelDate: clientData.TravelDate ? new Date(clientData.TravelDate).toLocaleDateString('ar-EG') : "غير محدد",
        commission: commission || "غير محدد",
        isPreviousCompany: isPreviousCompany
      };
    }
    
    return null;
  };

  // Main API Functions
  
  // جلب قائمة المندوبين
  const fetchDelegates = async () => {
    try {
      setLoading(true);
     const response = await fetch(`${config.API_BASE_URL}/api/delegates`);

      if (!response.ok) throw new Error('فشل في جلب بيانات المندوبين');
      const data = await response.json();
      setDelegates(data);
      setLoading(false);
    } catch (error) {
      console.error('خطأ في جلب المندوبين:', error);
      toast.error('حدث خطأ أثناء تحميل بيانات المندوبين');
      setLoading(false);
    }
  };

  // جلب قائمة الشركات
  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/companies`);

      if (!response.ok) throw new Error('فشل في جلب بيانات الشركات');
      const data = await response.json();
      setAllCompanies(data);
    } catch (error) {
      console.error('خطأ في جلب الشركات:', error);
      toast.error('حدث خطأ أثناء تحميل بيانات الشركات');
    }
  };

  // جلب الإعدادات العامة
  const fetchGlobalSettings = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/simple-settings`);

      
      if (!response.ok) {
        throw new Error(`خطأ في استرجاع الإعدادات: ${response.status}`);
      }
      
      const data = await response.json();
      
      // التأكد من صحة القيم
      const contractCommission = parseFloat(data.contractCommission) || 200;
      const taqeebCommission = parseFloat(data.taqeebCommission) || 150;
      
      setGlobalSettings({
        contractCommission: contractCommission,
        taqeebCommission: taqeebCommission,
        useGlobalSettings: true
      });
      
      // تحديث العمولات بعد استرجاع الإعدادات
      updateCommissionValues();
      
      return { contractCommission, taqeebCommission };
    } catch (error) {
      console.error('خطأ في جلب الإعدادات العامة:', error);
      // استخدام القيم الافتراضية في حالة الخطأ
      setGlobalSettings({
        contractCommission: 200,
        taqeebCommission: 150,
        useGlobalSettings: true
      });
      
      return { contractCommission: 200, taqeebCommission: 150 };
    }
  };

  // جلب إعدادات العمولات
  const fetchCommissionSettings = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/commission-settings/all`);

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        
        // إذا كان هناك مندوب محدد، قم بتحديث قائمة العملاء
        if (selectedDelegate) {
          fetchDelegateClients(selectedDelegate.DelegateID);
        }
      } else {
        console.error("خطأ في جلب إعدادات العمولات:", await response.text());
        toast.error('حدث خطأ أثناء تحميل إعدادات العمولات');
      }
    } catch (error) {
      console.error('خطأ في جلب إعدادات العمولات:', error);
      toast.error('حدث خطأ أثناء تحميل إعدادات العمولات');
    }
  };

  // تحديث العمولات المستحقة
  const updateCommissionValues = () => {
    // نسخة جديدة من العملاء مع حساب العمولة المحدثة
    if (clients.length > 0) {      
      const updatedClients = clients.map(client => {
        const calculatedCommission = calculateCommission(client);
        
        return {
          ...client,
          calculatedCommission: calculatedCommission
        };
      });
      
      setClients(updatedClients);
      setFilteredClients(updatedClients);
    }
  };

  // حساب العمولة المستحقة
  const calculateCommission = (client) => {
    // التحقق من وجود معرف الشركة
    if (!client.CompanyID) {
      return null;
    }
    
    // تحويل معرف الشركة إلى رقم للمقارنة الصحيحة
    const companyId = parseInt(client.CompanyID);
    
    // البحث عن إعدادات العمولة للشركة المحددة
    const companySetting = settings.find(s => parseInt(s.companyId) === companyId);
    
    // إذا لم يتم العثور على إعدادات للشركة أو كان الحساب التلقائي معطل
    if (!companySetting || !companySetting.useAutoCalculation) {
      return null;
    }

    // التحقق بدقة مما إذا كان العميل قديم أم جديد
    let isOldClient = false;
    
    // التحقق من وجود بيانات الشركات السابقة
    if (client.PreviousCompanies) {
      try {
        // محاولة تحليل البيانات
        let previousCompanies;
        
        if (typeof client.PreviousCompanies === 'string') {
          previousCompanies = JSON.parse(client.PreviousCompanies);
        } else {
          previousCompanies = client.PreviousCompanies;
        }
        
        // التحقق من أن الشركة الحالية موجودة في الشركات السابقة
        if (Array.isArray(previousCompanies)) {
          isOldClient = previousCompanies.some(prevCompanyId => 
            String(prevCompanyId) === String(client.CompanyID)
          );
        }
      } catch (error) {
        console.error("خطأ في تحليل بيانات الشركات السابقة:", error);
        isOldClient = false;
      }
    }

    // تحديد نوع العمولة بناءً على نوع العميل ومدة السفر
    let commission;
    
    if (client.TravelDuration === 'عقد') {
      // استخدام الإعدادات العامة للعقد
      commission = globalSettings.contractCommission;
    } else if (client.TravelDuration === 'تعقيب') {
      // استخدام الإعدادات العامة للتعقيب
      commission = globalSettings.taqeebCommission;
    } else if (isOldClient) {
      // استخدام إعدادات الشركة للعميل القديم
      commission = companySetting.oldClientCommission;
    } else {
      // استخدام إعدادات الشركة للعميل الجديد
      commission = companySetting.newClientCommission;
    }
    
    return commission;
  };

  // جلب معلومات المندوب (الرصيد والإحصائيات)
  const fetchDelegateInfo = async (delegateId) => {
    try {
      setLoading(true);
     const response = await fetch(`${config.API_BASE_URL}/api/delegates/${delegateId}/balance`);

      if (!response.ok) throw new Error('فشل في جلب معلومات المندوب');
      const data = await response.json();
      setDelegateInfo(data);
      setLoading(false);
    } catch (error) {
      console.error('خطأ في جلب معلومات المندوب:', error);
      toast.error('حدث خطأ أثناء تحميل معلومات المندوب');
      setLoading(false);
    }
  };

  // جلب معاملات المندوب
  const fetchDelegateTransactions = async (delegateId) => {
    try {
      setLoading(true);
      const response = await fetch(`${config.API_BASE_URL}/api/delegates/${delegateId}/transactions`);

      if (!response.ok) throw new Error('فشل في جلب معاملات المندوب');
      const data = await response.json();
      setDelegateTransactions(data);
      setLoading(false);
    } catch (error) {
      console.error('خطأ في جلب معاملات المندوب:', error);
      toast.error('حدث خطأ أثناء تحميل معاملات المندوب');
      setLoading(false);
    }
  };

  // جلب عملاء المندوب
 // جلب عملاء المندوب
// تحسين دالة جلب عملاء المندوب - تقليل البحث المتكرر
const fetchDelegateClients = async (delegateId) => {
  try {
    setLoading(true);
    
    const clientsResponse = await fetch(`${config.API_BASE_URL}/api/delegates/${delegateId}/clients`);
    if (!clientsResponse.ok) throw new Error('فشل في جلب عملاء المندوب');
    const clientsData = await clientsResponse.json();
    
    // جلب معلومات العمولات
    let commissionsData = [];
    try {
      const commissionsResponse = await fetch(`${config.API_BASE_URL}/api/delegates/${delegateId}/commissions`);
      if (commissionsResponse.ok) {
        commissionsData = await commissionsResponse.json();
      }
    } catch (commError) {
      console.warn('فشل جلب بيانات العمولات:', commError.message);
    }
    
    // معالجة العملاء مع التحقق من العمولات المدفوعة
    const enrichedClients = await Promise.all(clientsData.map(async (client) => {
      const clientCommissions = commissionsData.filter(comm => 
        parseInt(comm.PassportID) === parseInt(client.PassportID)
      );
      
      const totalPaid = clientCommissions.reduce((sum, comm) => sum + parseFloat(comm.Amount || 0), 0);
      
      // التحقق من تاريخ العمولة لكل عميل
      const commissionHistory = await checkCommissionHistory(client.PassportID);
      
      let commissionStatus = '-';
      if (client.TravelDate) {
        if (client.CommissionStatus) {
          commissionStatus = client.CommissionStatus;
        } else if (clientCommissions.length > 0) {
          commissionStatus = 'مدفوعة';
        } else {
          commissionStatus = 'غير مدفوعة';
        }
      }
      
      const calculatedCommission = calculateCommission(client);
      
      return {
        ...client,
        commissions: clientCommissions,
        totalPaidCommission: totalPaid,
        commissionStatus: commissionStatus,
        calculatedCommission: calculatedCommission,
        commissionHistory: commissionHistory
      };
    }));

    setClients(enrichedClients);
    setFilteredClients(enrichedClients);
    
    // حساب الإحصائيات
    const totalClients = enrichedClients.length;
    const traveledClients = enrichedClients.filter(client => client.TravelDate).length;
    const paidCommission = enrichedClients.filter(client => client.commissionStatus === 'مدفوعة').length;
    const unpaidCommission = enrichedClients.filter(client => client.commissionStatus === 'غير مدفوعة').length;
    const totalCommission = enrichedClients.reduce((sum, client) => sum + (client.totalPaidCommission || 0), 0);

    setStats({
      totalClients,
      traveledClients,
      paidCommission,
      unpaidCommission,
      totalCommission
    });

    setLoading(false);
  } catch (error) {
    console.error('خطأ في جلب عملاء المندوب:', error);
    toast.error('حدث خطأ أثناء تحميل عملاء المندوب');
    setLoading(false);
  }
};

  // تحديث البيانات
  const handleRefresh = async () => {
    setRefreshing(true);
    if (selectedDelegate) {
      await Promise.all([
        fetchDelegateClients(selectedDelegate.DelegateID),
        fetchDelegateInfo(selectedDelegate.DelegateID),
        fetchDelegateTransactions(selectedDelegate.DelegateID)
      ]);
      toast.success('تم تحديث البيانات بنجاح');
    } else {
      await fetchDelegates();
      toast.success('تم تحديث قائمة المندوبين بنجاح');
    }
    setRefreshing(false);
  };




// دالة تعديل المعاملة مع المزامنة
const handleEditTransaction = async (e) => {
  e.preventDefault();
  
  if (!editTransactionForm.amount || editTransactionForm.amount <= 0) {
    toast.error('يرجى إدخال مبلغ صحيح');
    return;
  }
  
  const relatedForEdit = parseTransactionRelatedData(editingTransaction);
  const editOpType = relatedForEdit.operationType || relatedForEdit.financialOperationType || '';
  const editHasTreasuryEffect = !(
    relatedForEdit.doesAffectTreasury === false ||
    editOpType === 'manual_debt' ||
    relatedForEdit.createdFrom === 'manual_debt_without_treasury'
  );

  if (editHasTreasuryEffect && !editTransactionForm.paymentMethodId) {
    toast.error('يرجى اختيار حساب مصرفي لأن هذه العملية مرتبطة بالخزينة');
    return;
  }

  if (editHasTreasuryEffect && String(editTransactionForm.paymentMethodId).toLowerCase() === 'treasury') {
    toast.error('الخزينة الرئيسية غير مسموحة هنا. اختر حسابًا مصرفيًا فقط.');
    return;
  }

  try {
    setLoading(true);
    
    // تحديث المعاملة في نظام المناديب
    const response = await fetch(`${config.API_BASE_URL}/api/delegates/transactions/${editingTransaction.TransactionID}/update`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        TransactionType: editTransactionForm.type,
        Amount: toSafeNumber(editTransactionForm.amount),
        Notes: editTransactionForm.notes,
        Date: editTransactionForm.date,
        UpdatedBy: getCurrentUser(),
        paymentMethodId: editTransactionForm.paymentMethodId || null
      })
    });

    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(responseData.details || responseData.error || 'فشل في تحديث معاملة المندوب');
    }

    // تمت مزامنة الخزينة والسلف داخل API تحديث معاملة المندوب

    // إعادة تحميل البيانات
    await Promise.all([
      fetchDelegateInfo(selectedDelegate.DelegateID),
      fetchDelegateTransactions(selectedDelegate.DelegateID)
    ]);
    
    // إغلاق النافذة وإعادة تعيين النموذج
    setShowEditTransactionModal(false);
    setEditingTransaction(null);
    resetEditTransactionForm();
    
    toast.success('تم تحديث المعاملة بنجاح مع المزامنة');
    
  } catch (error) {
    console.error('خطأ في تعديل المعاملة:', error);
    toast.error(`حدث خطأ: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

// دالة لبدء تعديل المعاملة
const startEditTransaction = async (transaction) => {
  setEditingTransaction(transaction);

  const relatedForStart = parseTransactionRelatedData(transaction);
  const opTypeForStart = relatedForStart.operationType || relatedForStart.financialOperationType || '';
  const transactionAffectsTreasury = !(
    relatedForStart.doesAffectTreasury === false ||
    opTypeForStart === 'manual_debt' ||
    relatedForStart.createdFrom === 'manual_debt_without_treasury'
  );

  // نحمّل الحسابات فقط للمعاملات التي لها أثر خزينة، ولا نعطل فتح نافذة التعديل إذا فشل التحميل
  if (transactionAffectsTreasury && (!paymentMethods || paymentMethods.length === 0)) {
    await fetchPaymentMethods();
  }
  
  // تعبئة النموذج ببيانات المعاملة الحالية
  let selectedPaymentMethod = null;
  try {
    if (transaction.RelatedClients) {
      const parsedData = typeof transaction.RelatedClients === 'string' 
        ? JSON.parse(transaction.RelatedClients) 
        : transaction.RelatedClients;
      
      if (parsedData.paymentMethod) {
        const savedMethodId = parsedData.paymentMethod.id;
        const savedMethodType = String(parsedData.paymentMethod.type || '').toLowerCase();
        const savedMethodName = String(parsedData.paymentMethod.name || parsedData.paymentMethod.Name || '');
        if (
          savedMethodId &&
          String(savedMethodId).toLowerCase() !== 'treasury' &&
          savedMethodType !== 'treasury' &&
          !savedMethodName.includes('الخزينة') &&
          !savedMethodName.includes('خزينة')
        ) {
          selectedPaymentMethod = savedMethodId;
        }
      }
    }
  } catch (e) {
    console.error('خطأ في تحليل بيانات المعاملة:', e);
  }
  
  setEditTransactionForm({
    type: transaction.TransactionType,
    amount: transaction.Amount.toString(),
    notes: transaction.Notes || '',
    date: new Date(transaction.Date).toISOString().split('T')[0],
    paymentMethodId: selectedPaymentMethod,
    clientDetails: {
      name: `المندوب: ${selectedDelegate?.DelegateName}`,
      nationalId: `معاملة رقم: ${transaction.TransactionID}`,
      companyName: 'عمولة مناديب',
      delegateName: selectedDelegate?.DelegateName || '',
      phoneNumbers: ''
    }
  });
  
  setShowEditTransactionModal(true);
};

// دالة إعادة تعيين نموذج التعديل
const resetEditTransactionForm = () => {
  setEditTransactionForm({
    type: '',
    amount: '',
    notes: '',
    date: '',
    paymentMethodId: null,
    clientDetails: {
      name: '',
      nationalId: '',
      companyName: '',
      delegateName: '',
      phoneNumbers: ''
    }
  });
};





  


// دوال موحدة لجلب الحسابات المصرفية بدون الاعتماد على مسار واحد فقط
const buildUrlWithCategory = (baseUrl, apiPath, categoryId = null) => {
  const safeBase = (baseUrl || '').replace(/\/$/, '');
  if (!safeBase) return null;
  const query = categoryId ? `?categoryId=${encodeURIComponent(categoryId)}` : '';
  return `${safeBase}${apiPath}${query}`;
};

const isRealBankAccountPaymentMethod = (account) => {
  if (!account) return false;
  const id = String(account.id ?? account.AccountID ?? account.ID ?? '').trim().toLowerCase();
  const type = String(account.type ?? account.Type ?? '').trim().toLowerCase();
  const name = String(account.name ?? account.Name ?? account.accountName ?? account.AccountName ?? '').trim();
  const categoryName = String(account.categoryName ?? account.CategoryName ?? '').trim();

  if (!id || id === 'treasury' || id === 'cash' || id === 'main_treasury') return false;
  if (['treasury', 'cash', 'wallet'].includes(type)) return false;
  if (name.includes('الخزينة') || name.includes('خزينة') || categoryName.includes('الخزينة') || categoryName.includes('خزينة')) return false;
  return true;
};

const normalizeBankAccountsPayload = (payload) => {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.paymentMethods)
      ? payload.paymentMethods
      : Array.isArray(payload?.bankAccounts)
        ? payload.bankAccounts
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

  return source
    .map((account) => {
      const id = account.id ?? account.AccountID ?? account.ID;
      const rawType = account.type ?? account.Type ?? 'bank';
      const displayName = account.name ?? account.Name ?? account.accountName ?? account.AccountName ?? 'حساب غير محدد';
      return {
        id,
        name: displayName,
        Name: account.Name ?? account.name ?? account.accountName ?? account.AccountName ?? 'حساب غير محدد',
        accountNumber: account.accountNumber ?? account.AccountNumber ?? account.number ?? account.AdditionalDetails ?? '',
        type: rawType,
        categoryId: account.categoryId ?? account.CategoryID ?? null,
        CategoryID: account.CategoryID ?? account.categoryId ?? null,
        balance: account.balance ?? account.Balance ?? 0
      };
    })
    .filter((account) => {
      const id = String(account.id ?? '').trim().toLowerCase();
      const type = String(account.type ?? '').trim().toLowerCase();
      const name = String(account.name ?? account.Name ?? '').trim();

      // في حسابات المناديب لا نسمح إلا بالحسابات المصرفية المسجلة فعليًا في BankAccounts.
      // لذلك نستبعد أي fallback قديم مثل "الخزينة الرئيسية" أو أي طريقة دفع من نوع treasury/cash/wallet.
      if (!id || id === 'treasury' || id === 'cash' || id === 'main_treasury') return false;
      if (['treasury', 'cash', 'wallet'].includes(type)) return false;
      if (name.includes('الخزينة') || name.includes('خزينة')) return false;
      return true;
    });
};
const getBankAccountsCandidateUrls = (categoryId = null) => {
  const urls = [
    buildUrlWithCategory(config.API_BASE_URL, '/api/banks/bank-accounts', categoryId),
    buildUrlWithCategory(config.API_BASE_URL, '/api/bank-accounts', categoryId),
    buildUrlWithCategory(config.API_BASE_URL, '/api/delegate-accounts/bank-accounts', categoryId),
    buildUrlWithCategory(config.DELEGATES_API_URL, '/api/bank-accounts', categoryId),
    buildUrlWithCategory(config.DELEGATES_API_URL, '/api/delegate-accounts/bank-accounts', categoryId)
  ].filter(Boolean);

  return [...new Set(urls)];
};

const loadBankAccountsSafely = async (categoryId = null) => {
  const urls = getBankAccountsCandidateUrls(categoryId);
  const errors = [];

  for (const url of urls) {
    try {
      console.log('محاولة جلب الحسابات المصرفية من:', url);
      const response = await axios.get(url, { timeout: 10000 });
      const accounts = normalizeBankAccountsPayload(response.data);
      console.log(`تم جلب ${accounts.length} حساب من:`, url);
      return accounts;
    } catch (error) {
      errors.push({ url, message: error.response?.data?.error || error.message });
      console.warn('فشل مسار جلب الحسابات:', url, error.response?.data || error.message);
    }
  }

  const error = new Error('فشل في جلب الحسابات المصرفية من كل المسارات المتاحة');
  error.details = errors;
  throw error;
};

// جلب طرق الدفع/الحسابات المستخدمة في حسابات المناديب
const fetchPaymentMethods = async ({ silent = false } = {}) => {
  try {
    console.log('بدء جلب طرق الدفع...');
    setIsLoadingPaymentMethods(true);

    const bankAccounts = await loadBankAccountsSafely();
    setPaymentMethods(bankAccounts);
    setFilteredPaymentMethods(bankAccounts);

    return bankAccounts;
  } catch (error) {
    console.error('خطأ في جلب طرق الدفع:', error.details || error);
    setPaymentMethods([]);
    setFilteredPaymentMethods([]);

    if (!silent) {
      toast.error('فشل في جلب الحسابات المصرفية. تأكد من تشغيل مسار الخزينة /api/banks/bank-accounts');
    }

    return [];
  } finally {
    setIsLoadingPaymentMethods(false);
  }
};

const normalizeCategoriesPayload = (payload) => {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.categories)
      ? payload.categories
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

  return source.map((category) => ({
    id: category.id ?? category.CategoryID,
    name: category.name ?? category.Name,
    description: category.description ?? category.Description ?? '',
    createdAt: category.createdAt ?? category.CreatedAt
  })).filter((category) => category.id !== undefined && category.id !== null);
};

const fetchCategories = async () => {
  const urls = [
    buildUrlWithCategory(config.API_BASE_URL, '/api/banks/account-categories'),
    buildUrlWithCategory(config.API_BASE_URL, '/api/account-categories'),
    buildUrlWithCategory(config.API_BASE_URL, '/api/delegate-accounts/account-categories'),
    buildUrlWithCategory(config.DELEGATES_API_URL, '/api/account-categories'),
    buildUrlWithCategory(config.DELEGATES_API_URL, '/api/delegate-accounts/account-categories')
  ].filter(Boolean);

  for (const url of [...new Set(urls)]) {
    try {
      const response = await axios.get(url, { timeout: 10000 });
      const data = normalizeCategoriesPayload(response.data);
      setCategories(data);
      return data;
    } catch (error) {
      console.warn('فشل مسار جلب التصنيفات:', url, error.response?.data || error.message);
    }
  }

  setCategories([]);
  toast.error('حدث خطأ أثناء جلب تصنيفات الحسابات');
  return [];
};

// جلب الحسابات حسب التصنيف من نفس مسار الخزينة الصحيح
const handleCategoryChange = async (categoryId) => {
  console.log('تم اختيار التصنيف:', categoryId);
  const normalizedCategoryId = categoryId || '';
  setSelectedCategoryId(normalizedCategoryId);
  setSelectedPaymentMethod(null);
  setIsLoadingPaymentMethods(true);
  setFilteredPaymentMethods([]);

  try {
    const accounts = await loadBankAccountsSafely(normalizedCategoryId || null);

    if (normalizedCategoryId && accounts.length === 0) {
      toast('لا توجد حسابات مرتبطة بهذا التصنيف', { icon: 'ℹ️', duration: 4000 });
    }

    setFilteredPaymentMethods(accounts);
    setPaymentMethods((prev) => {
      const map = new Map();
      [...(Array.isArray(prev) ? prev : []), ...accounts].forEach((account) => {
        if (account?.id !== undefined && account?.id !== null) {
          map.set(String(account.id), account);
        }
      });
      return Array.from(map.values());
    });
  } catch (error) {
    console.error('خطأ في جلب الحسابات حسب التصنيف:', error.details || error);
    toast.error('حدث خطأ أثناء جلب الحسابات');
    setFilteredPaymentMethods([]);
  } finally {
    setIsLoadingPaymentMethods(false);
  }
};

// دالة اختيار الحساب
const handleAccountSelect = (accountId) => {
  if (!accountId) {
    setSelectedPaymentMethod(null);
    return;
  }
  
  // البحث عن الحساب المحدد في قائمة طرق الدفع
  const selectedAccount = paymentMethods.find(method => 
    String(method.id) === String(accountId)
  );
  
  if (selectedAccount) {
    setSelectedPaymentMethod(selectedAccount);
  }
};

// دالة مساعدة لعرض نوع طريقة الدفع بشكل مقروء
const getPaymentMethodTypeName = (type) => {
  switch(type) {
    case 'bank':
      return 'حساب بنكي';
    default:
      return 'حساب مصرفي';
  }
};




const handleSelectDelegate = (delegate) => {
  setSelectedDelegate(delegate);
  setSelectedClients([]);
  setAllSelected(false);
  
  // تغيير الفلترة الافتراضية للمسافرين غير المدفوعة عمولتهم
  setFilters({
    showAll: false,
    onlyTraveled: false,
    onlyPaid: false,
    onlyUnpaid: false,
    onlyTraveledUnpaid: true // إضافة فلتر جديد
  });
  
  setSearch('');
  setActiveTab('clients');

  // بدء التحميل
  setLoading(true);

  // تنفيذ جميع الطلبات معًا
  Promise.all([
    fetchDelegateClients(delegate.DelegateID),
    fetchDelegateInfo(delegate.DelegateID),
    fetchDelegateTransactions(delegate.DelegateID)
  ]).then(() => {
    // إيقاف التحميل بعد اكتمال جميع الطلبات
    setLoading(false);
  }).catch(error => {
    console.error('خطأ في تحميل بيانات المندوب:', error);
    setLoading(false);
    toast.error('حدث خطأ أثناء تحميل البيانات');
  });
};

  // تغيير عوامل التصفية
  const handleFilterChange = (filterName) => {
    const newFilters = {
      showAll: false,
      onlyTraveled: false,
      onlyPaid: false,
      onlyUnpaid: false,
      onlyTraveledUnpaid: false, // إضافة الفلتر الجديد
      [filterName]: true
    };
    
    setFilters(newFilters);
    
    // تطبيق الفلتر على العملاء
    let filtered = [...clients];
    
    if (filterName === 'onlyTraveled') {
      // كل المسافرين
      filtered = filtered.filter(client => client.TravelDate);
    } else if (filterName === 'onlyPaid') {
      // العمولة مدفوعة فقط
      filtered = filtered.filter(client => 
        client.TravelDate && client.commissionStatus === 'مدفوعة'
      );
    } else if (filterName === 'onlyUnpaid') {
      // العمولة غير مدفوعة فقط
      filtered = filtered.filter(client => 
        client.TravelDate && client.commissionStatus === 'غير مدفوعة'
      );
    } else if (filterName === 'onlyTraveledUnpaid') {
      // المسافرين اللي عمولتهم غير مدفوعة - الوضع الافتراضي الجديد
      filtered = filtered.filter(client => 
        client.TravelDate && 
        (client.commissionStatus === 'غير مدفوعة' || !client.commissionStatus)
      );
    }
    // إذا كان showAll فسيعرض كل العملاء
    
    // تطبيق البحث أيضا
    if (search) {
      filtered = filtered.filter(client => 
        client.NameArabic.includes(search) ||
        (client.CompanyName && client.CompanyName.includes(search))
      );
    }
    
    setFilteredClients(filtered);
  };

  // البحث في العملاء
  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearch(searchValue);
    
    let filtered = [...clients];
    
    // تطبيق الفلترة أولاً حسب النوع المحدد
    if (filters.onlyTraveledUnpaid) {
      filtered = filtered.filter(client => 
        client.TravelDate && 
        (client.commissionStatus === 'غير مدفوعة' || !client.commissionStatus)
      );
    } else if (filters.onlyTraveled) {
      filtered = filtered.filter(client => client.TravelDate);
    } else if (filters.onlyPaid) {
      filtered = filtered.filter(client => 
        client.TravelDate && client.commissionStatus === 'مدفوعة'
      );
    } else if (filters.onlyUnpaid) {
      filtered = filtered.filter(client => 
        client.TravelDate && (client.commissionStatus === 'غير مدفوعة' || !client.commissionStatus)
      );
    }
    // إذا كان showAll فلا نطبق أي فلتر
    
    // ثم تطبيق البحث
    if (searchValue) {
      filtered = filtered.filter(client => 
        client.NameArabic.includes(searchValue) ||
        (client.CompanyName && client.CompanyName.includes(searchValue))
      );
    }
    
    setFilteredClients(filtered);
  };

  // تحديد كل العملاء
  const handleSelectAllClients = () => {
    if (allSelected) {
      setSelectedClients([]);
    } else {
      // تحديد فقط العملاء المسافرين الذين لم تدفع لهم عمولة
      const eligibleClients = filteredClients
        .filter(client => client.TravelDate && client.commissionStatus === 'غير مدفوعة')
        .map(client => client.PassportID);
      setSelectedClients(eligibleClients);
    }
    setAllSelected(!allSelected);
  };

  // تحديد عميل واحد
  const handleSelectClient = (clientId) => {
    if (selectedClients.includes(clientId)) {
      setSelectedClients(prev => prev.filter(id => id !== clientId));
    } else {
      setSelectedClients(prev => [...prev, clientId]);
    }
  };
// تصحيح حالة العمولة
const handleFixCommissionStatus = async () => {
  if (!selectedDelegate) {
    toast.error('يرجى اختيار مندوب أولاً');
    return;
  }
  
  // عرض النافذة المخصصة لتصحيح حالة العمولة بدلاً من التأكيد المباشر
  setShowCommissionStatusModal(true);
};
  
// دالة التعامل مع اكتمال عملية تصحيح حالة العمولة
const handleFixCommissionComplete = (result) => {
  console.log('تم تصحيح حالة العمولة:', result);
  
  if (result.success) {
    toast.success(result.message);
    // إعادة تحميل بيانات العملاء
    fetchDelegateClients(selectedDelegate.DelegateID);
  }
};




const showCustomConfirm = (message, callback) => {
  setConfirmMessage(message);
  setConfirmCallback(() => callback);
  setShowConfirmDialog(true);
};

// دالة التعامل مع نافذة التأكيد
const handleConfirmResponse = (confirmed) => {
  setShowConfirmDialog(false);
  if (confirmed && confirmCallback) {
    confirmCallback();
  }
  setConfirmMessage('');
  setConfirmCallback(null);
};

const proceedWithCommission = (clientsWithPaidCommission) => {
  // إزالة العملاء اللي عندهم عمولة مدفوعة من القائمة
  const validClients = selectedClients.filter(clientId => 
    !clientsWithPaidCommission.some(paid => paid.id === clientId)
  );
  
  if (validClients.length === 0) {
    toast.error('جميع العملاء المحددين لديهم عمولة مدفوعة مسبقاً');
    return;
  }
  
  // تحديث قائمة العملاء المحددين
  setSelectedClients(validClients);
  
  // تحديث نموذج العمولة للعملاء الصالحين فقط
  let totalValidAmount = 0;
  validClients.forEach(clientId => {
    const clientInfo = clients.find(c => c.PassportID === clientId);
    if (clientInfo) {
      const calculatedAmount = calculateCommission(clientInfo);
      if (calculatedAmount) {
        totalValidAmount += calculatedAmount;
      }
    }
  });
  
  setCommissionForm(prev => ({
    ...prev,
    amount: totalValidAmount.toString()
  }));
  
  toast.success(`تم استبعاد ${clientsWithPaidCommission.length} عميل وسيتم دفع العمولة لـ ${validClients.length} عميل فقط`);
};


const handleCommissionSubmit = async (e) => {
  e.preventDefault();
  
  // التحقق من العملاء اللي عندهم عمولة مدفوعة مسبقاً
  const clientsWithPaidCommission = [];
 
  for (const clientId of selectedClients) {
    const client = clients.find(c => c.PassportID === clientId);
    if (client?.commissionHistory?.isPaidByOtherDelegate) {
      clientsWithPaidCommission.push({
        id: clientId,
        name: client.NameArabic,
        delegateName: client.commissionHistory.delegateName,
        paidDate: client.commissionHistory.paidDate,
        amount: client.commissionHistory.amount
      });
    }
  }
 
  // إذا وجدت عملاء لهم عمولة مدفوعة مسبقاً
  if (clientsWithPaidCommission.length > 0) {
    const warningMessage = `⚠️ تحذير: العملاء التالية لديهم عمولة مدفوعة مسبقاً:\n\n${
      clientsWithPaidCommission.map(client => 
        `• ${client.name}\n  مدفوعة عند: ${client.delegateName}\n  تاريخ الدفع: ${new Date(client.paidDate).toLocaleDateString('ar-EG')}\n  المبلغ: ${client.amount} ج.م\n`
      ).join('\n')
    }\nهل تريد المتابعة مع العملاء الآخرين فقط؟`;
    
    // استخدام نافذة التأكيد المخصصة
    showCustomConfirm(warningMessage, () => {
      proceedWithCommission(clientsWithPaidCommission);
    });
    return;
  }
  
  if (selectedClients.length === 0) {
    toast.error('يرجى تحديد عميل واحد على الأقل');
    return;
  }
  
  if (!commissionForm.amount || toSafeNumber(commissionForm.amount) <= 0) {
    toast.error('يرجى إدخال مبلغ العمولة');
    return;
  }
  
  // التحقق من اختيار طريقة الدفع
  if (!selectedPaymentMethod) {
    toast.error('يرجى اختيار طريقة الدفع');
    return;
  }
 
  try {
    setLoading(true);
    
    // 1. التحقق من وجود سلف/مديونيات للمندوب
    const debtsResponse = await fetch(`${config.API_BASE_URL}/api/delegate-accounts/delegates/${selectedDelegate.DelegateID}/total-debts`);
    let debtsInfo = { TotalDebts: 0, DebtsCount: 0 };
    
    if (debtsResponse.ok) {
      debtsInfo = await debtsResponse.json();
    }
    
    let finalCommissionAmount = toSafeNumber(commissionForm.amount);
    let deductionAmount = 0;
    let netPaymentAmount = finalCommissionAmount;
    
    // 2. إذا كانت هناك سلف/مديونيات، اعرض تأكيد الخصم
    if (debtsInfo.TotalDebts > 0) {
      deductionAmount = Math.min(finalCommissionAmount, debtsInfo.TotalDebts);
      netPaymentAmount = finalCommissionAmount - deductionAmount;
      
      const deductionConfirmed = window.confirm(
        `💰 تنبيه: يوجد سلف/مديونيات على هذا المندوب\n\n` +
        `📊 تفاصيل العملية:\n` +
        `• عدد البنود: ${debtsInfo.DebtsCount}\n` +
        `• إجمالي السلف/المديونيات: ${formatMoneyValue(debtsInfo.TotalDebts)}\n` +
        `• إجمالي العمولة: ${formatMoneyValue(finalCommissionAmount)}\n` +
        `• مبلغ الخصم: ${formatMoneyValue(deductionAmount)}\n` +
        `• صافي المبلغ المدفوع: ${formatMoneyValue(netPaymentAmount)}\n\n` +
        `${netPaymentAmount <= 0 ? '⚠️ تحذير: العمولة لن تكفي لسداد كامل السلف/المديونيات\n' : ''}` +
        `هل تريد المتابعة مع خصم السلف/المديونيات تلقائياً؟`
      );
      
      if (!deductionConfirmed) {
        setLoading(false);
        return;
      }
    }
    
    // 3. إنشاء التاريخ والوقت الحالي بصيغة صحيحة
    const currentDateTime = new Date();
    
    // إذا كان المستخدم اختار تاريخ معين، استخدم هذا التاريخ مع الوقت الحالي
    let transactionDateTime;
    if (commissionForm.date && commissionForm.date !== currentDateTime.toISOString().split('T')[0]) {
      // المستخدم اختار تاريخ مختلف، استخدم هذا التاريخ مع الوقت الحالي
      const selectedDate = new Date(commissionForm.date);
      selectedDate.setHours(currentDateTime.getHours());
      selectedDate.setMinutes(currentDateTime.getMinutes());
      selectedDate.setSeconds(currentDateTime.getSeconds());
      selectedDate.setMilliseconds(currentDateTime.getMilliseconds());
      transactionDateTime = selectedDate;
    } else {
      // استخدم التاريخ والوقت الحالي
      transactionDateTime = currentDateTime;
    }
    
    // طباعة بيانات الطلب للتشخيص
    console.log("بيانات طلب دفع العمولة:", {
      delegateId: selectedDelegate.DelegateID,
      clientIds: selectedClients,
      amount: finalCommissionAmount,
      deductionAmount: deductionAmount,
      netPaymentAmount: netPaymentAmount,
      notes: commissionForm.notes,
      date: transactionDateTime.toISOString(), // إرسال التاريخ والوقت كاملاً
      paymentMethod: selectedPaymentMethod ? {
        id: selectedPaymentMethod.id,
        type: selectedPaymentMethod.type,
        name: selectedPaymentMethod.Name || selectedPaymentMethod.name
      } : null
    });
    
    // 4. إنشاء طلب دفع العمولة مع إضافة معلومات السلف/المديونيات
    const commissionData = {
      delegateId: selectedDelegate.DelegateID,
      clientIds: selectedClients,
      amount: finalCommissionAmount,
      deductionAmount: deductionAmount,
      netPaymentAmount: netPaymentAmount,
      notes: deductionAmount > 0 
        ? `${commissionForm.notes || 'دفع عمولة'} - تم خصم ${formatMoneyValue(deductionAmount)} من السلف/المديونيات`
        : commissionForm.notes,
      date: transactionDateTime.toISOString(), // إرسال التاريخ والوقت كاملاً بصيغة ISO
      paymentMethod: selectedPaymentMethod ? {
        id: selectedPaymentMethod.id,
        type: selectedPaymentMethod.type,
        name: selectedPaymentMethod.Name || selectedPaymentMethod.name
      } : null,
      hasDebtDeduction: deductionAmount > 0,
      createdBy: getCurrentUser()
    };
 
    // 5. إرسال الطلب إلى الخادم
    const response = await fetch(`${config.API_BASE_URL}/api/delegate-commissions/pay`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(commissionData)
    });
 
    // معالجة الاستجابة
    const responseData = await response.json();
    console.log("استجابة الخادم:", responseData);
 
    if (!response.ok) {
      throw new Error(responseData.error || responseData.details || 'فشل في تسجيل دفع العمولة');
    }
 
    // 6. إعادة تعيين النموذج
    setCommissionForm({
      amount: '',
      notes: '',
      date: new Date().toISOString().split('T')[0] // إعادة ضبط التاريخ للتاريخ الحالي
    });
    
    // إغلاق النموذج وإعادة تعيين التحديدات
    setSelectedClients([]);
    setAllSelected(false);
    setShowCommissionForm(false);
    
    // 7. تحديث حالة العمولة محلياً لجميع العملاء المحددين
    setClients(prevClients => 
      prevClients.map(client => {
        if (selectedClients.includes(client.PassportID)) {
          const commissionPerClient = finalCommissionAmount / selectedClients.length;
          return {
            ...client,
            commissionStatus: 'مدفوعة',
            totalPaidCommission: (client.totalPaidCommission || 0) + commissionPerClient,
            commissions: [
              ...(client.commissions || []),
              {
                Amount: commissionPerClient,
                Date: transactionDateTime.toISOString(), // استخدام التاريخ والوقت الصحيح
                Notes: commissionForm.notes
              }
            ]
          };
        }
        return client;
      })
    );
    
    // تطبيق نفس التغييرات على القائمة المفلترة
    setFilteredClients(prevFiltered => 
      prevFiltered.map(client => {
        if (selectedClients.includes(client.PassportID)) {
          const commissionPerClient = finalCommissionAmount / selectedClients.length;
          return {
            ...client,
            commissionStatus: 'مدفوعة',
            totalPaidCommission: (client.totalPaidCommission || 0) + commissionPerClient,
            commissions: [
              ...(client.commissions || []),
              {
                Amount: commissionPerClient,
                Date: transactionDateTime.toISOString(), // استخدام التاريخ والوقت الصحيح
                Notes: commissionForm.notes
              }
            ]
          };
        }
        return client;
      })
    );
    
    // تحديث الإحصائيات أيضاً
    setStats(prevStats => ({
      ...prevStats,
      paidCommission: prevStats.paidCommission + selectedClients.length,
      unpaidCommission: Math.max(0, prevStats.unpaidCommission - selectedClients.length),
      totalCommission: prevStats.totalCommission + finalCommissionAmount
    }));
    
    // 8. عرض رسالة النجاح مع تفاصيل العملية
    if (deductionAmount > 0) {
      toast.success(
        `✅ تم دفع العمولة بنجاح!\n` +
        `💰 إجمالي العمولة: ${formatMoneyValue(finalCommissionAmount)}\n` +
        `➖ خصم سلف/مديونيات: ${formatMoneyValue(deductionAmount)}\n` +
        `💵 صافي المدفوع: ${formatMoneyValue(netPaymentAmount)}\n` +
        `🕐 الوقت: ${transactionDateTime.toLocaleString('ar-EG')}`
      );
    } else {
      toast.success(
        `✅ تم دفع العمولة بنجاح: ${formatMoneyValue(finalCommissionAmount)}\n` +
        `🕐 الوقت: ${transactionDateTime.toLocaleString('ar-EG')}`
      );
    }
    
    // 9. تحديث البيانات بشكل كامل بعد تأخير قصير
    setTimeout(async () => {
      try {
        await fetchDelegateClients(selectedDelegate.DelegateID);
        await fetchDelegateInfo(selectedDelegate.DelegateID);
        await fetchDelegateTransactions(selectedDelegate.DelegateID);
      } catch (updateError) {
        console.error("خطأ في تحديث البيانات بعد دفع العمولة:", updateError);
      }
    }, 1000);
    
    // 10. تصحيح حالة العمولة
    fixCommissionStatus(selectedClients);
    
  } catch (error) {
    console.error('خطأ في دفع العمولة:', error);
    
    // محاولة استخراج تفاصيل الخطأ من الاستجابة
    let errorMessage = error.message;
    
    // سجل أي معلومات إضافية للتشخيص
    console.log("معلومات طريقة الدفع عند حدوث الخطأ:", {
      paymentMethod: selectedPaymentMethod,
      delegateId: selectedDelegate?.DelegateID,
      clientsCount: selectedClients.length
    });
    
    toast.error(`حدث خطأ: ${errorMessage}`);
  } finally {
    setLoading(false);
  }
 };


// دالة لتصحيح حالة العمولة
const fixCommissionStatus = async (clientIds) => {
  try {
    const response = await fetch(`${config.API_BASE_URL}/api/fix-commission-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ clientIds })
    });
    
    const result = await response.json();
    
    if (response.ok) {
      console.log("تم تصحيح حالة العمولة:", result);
    } else {
      console.error("خطأ في تصحيح حالة العمولة:", result.error);
    }
  } catch (error) {
    console.error("خطأ في طلب تصحيح حالة العمولة:", error);
  }
};






// إضافة معاملة جديدة للمندوب
const handleTransactionSubmit = async (e) => {
e.preventDefault();

if (!transactionForm.amount || transactionForm.amount <= 0) {
  toast.error('يرجى إدخال مبلغ المعاملة');
  return;
}

try {
  setLoading(true);
  
  // إنشاء طلب إضافة المعاملة
  const transactionData = {
    TransactionType: transactionForm.type,
    Amount: toSafeNumber(transactionForm.amount),
    Notes: transactionForm.notes,
    Date: transactionForm.date,
    CreatedBy: getCurrentUser()
  };

  // إرسال الطلب إلى الخادم
  const response = await fetch(`${config.API_BASE_URL}/api/delegates/${selectedDelegate.DelegateID}/transactions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(transactionData)
  });

  // تحليل الاستجابة
  const responseData = await response.json();

  // التحقق من حالة الاستجابة
  if (!response.ok) {
    throw new Error(responseData.error || responseData.details || 'فشل في إضافة المعاملة');
  }

  // إعادة تعيين النموذج
  setTransactionForm({
    type: 'إيداع',
    amount: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });
  
  // تحديث البيانات بعد الإضافة
  await Promise.all([
    fetchDelegateInfo(selectedDelegate.DelegateID),
    fetchDelegateTransactions(selectedDelegate.DelegateID)
  ]);
  
  setShowTransactionForm(false);
  
  toast.success('تم إضافة المعاملة بنجاح');
  
} catch (error) {
  console.error('خطأ في إضافة المعاملة:', error);
  toast.error(`حدث خطأ: ${error.message}`);
} finally {
  setLoading(false);
}
};

// حذف معاملة
const handleDeleteTransaction = async (transactionId) => {
if (!window.confirm('هل أنت متأكد من حذف هذه المعاملة؟')) {
  return;
}

try {
  setLoading(true);
  
  // إرسال طلب الحذف
  const response = await fetch(`${config.API_BASE_URL}/api/delegates/transactions/${transactionId}`, {
    method: 'DELETE'
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'فشل في حذف المعاملة');
  }
  
  // تحديث البيانات بعد الحذف من كل الأماكن المرتبطة
  await Promise.all([
    fetchDelegateInfo(selectedDelegate.DelegateID),
    fetchDelegateTransactions(selectedDelegate.DelegateID),
    fetchDebts(),
    fetchDelegateClients(selectedDelegate.DelegateID)
  ]);

  if (selectedTransaction && Number(selectedTransaction.TransactionID) === Number(transactionId)) {
    setShowTransactionModal(false);
    setSelectedTransaction(null);
  }
  
  toast.success('تم حذف المعاملة بنجاح وتمت مزامنة السلف/المديونيات والخزينة');
} catch (error) {
  console.error('خطأ في حذف المعاملة:', error);
  toast.error(`حدث خطأ: ${error.message}`);
} finally {
  setLoading(false);
}
};




// تحديد كل المعاملات
const handleSelectAllTransactions = () => {
  if (allTransactionsSelected) {
    setSelectedTransactions([]);
    setAllTransactionsSelected(false);
  } else {
    const filteredTransactions = applyTransactionDateFilter();
    setSelectedTransactions(filteredTransactions.map(t => t.TransactionID));
    setAllTransactionsSelected(true);
  }
};

// تحديد معاملة واحدة
const handleSelectTransaction = (transactionId) => {
  if (selectedTransactions.includes(transactionId)) {
    setSelectedTransactions(prev => prev.filter(id => id !== transactionId));
  } else {
    setSelectedTransactions(prev => [...prev, transactionId]);
  }
};

// حذف المعاملات المحددة
// حذف المعاملات المحددة - محسن للسرعة
const handleDeleteSelectedTransactions = async () => {
  if (selectedTransactions.length === 0) {
    toast.error('يرجى تحديد معاملة واحدة على الأقل');
    return;
  }

  const confirmMessage = `هل أنت متأكد من حذف ${selectedTransactions.length} معاملة؟

⚠️ تنبيه: هذا الإجراء سيؤثر على:
- أرصدة المندوب
- حالة العملاء المرتبطين
- المعاملات البنكية المرتبطة

ولا يمكن التراجع عنه.`;

  if (!window.confirm(confirmMessage)) return;

  setLoading(true);
  let successCount = 0;
  let errorCount = 0;
  const errors = [];

  console.log(`🔥 بدء حذف ${selectedTransactions.length} معاملة`);

  // حذف المعاملات في مجموعات صغيرة لتحسين الأداء
  const batchSize = 3;
  for (let i = 0; i < selectedTransactions.length; i += batchSize) {
    const batch = selectedTransactions.slice(i, i + batchSize);
    
    await Promise.all(batch.map(async (transactionId) => {
      try {
        const response = await fetch(`${config.API_BASE_URL}/api/delegates/transactions/${transactionId}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        });
        
        const responseData = await response.json();
        
        if (response.ok && responseData.success) {
          successCount++;
          console.log(`✅ تم حذف المعاملة ${transactionId}`);
        } else {
          errorCount++;
          const errorMsg = responseData.error || responseData.details || 'خطأ غير معروف';
          errors.push(`${transactionId}: ${errorMsg}`);
          console.error(`❌ فشل حذف المعاملة ${transactionId}:`, errorMsg);
        }
        
      } catch (error) {
        errorCount++;
        errors.push(`${transactionId}: ${error.message}`);
        console.error(`❌ خطأ في حذف المعاملة ${transactionId}:`, error);
      }
    }));
    
    // تأخير قصير بين المجموعات
    if (i + batchSize < selectedTransactions.length) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  }

  // تحديث البيانات مرة واحدة فقط
  try {
    await Promise.all([
      fetchDelegateInfo(selectedDelegate.DelegateID),
      fetchDelegateTransactions(selectedDelegate.DelegateID)
    ]);
    
    // إعادة تحميل العملاء فقط إذا كان هناك معاملات عمولة محذوفة
    if (successCount > 0) {
      await fetchDelegateClients(selectedDelegate.DelegateID);
    }
  } catch (updateError) {
    console.error('❌ خطأ في تحديث البيانات:', updateError);
  }
  
  setLoading(false);
  setSelectedTransactions([]);
  setAllTransactionsSelected(false);
  
  // عرض النتائج
  if (successCount > 0) {
    toast.success(`✅ تم حذف ${successCount} معاملة بنجاح`);
  }
  
  if (errorCount > 0) {
    const errorMsg = errors.length <= 3 
      ? errors.join('\n')
      : `${errors.slice(0, 3).join('\n')}\n... و ${errors.length - 3} أخطاء أخرى`;
      
    toast.error(`❌ فشل في حذف ${errorCount} معاملة:\n${errorMsg}`, {
      duration: 6000
    });
  }
  
  console.log(`📊 نتائج الحذف: نجح ${successCount} - فشل ${errorCount}`);
};







// دالة تغيير فلتر تاريخ المعاملات
const handleTransactionDateFilterChange = (e) => {
  const { name, value } = e.target;
  setTransactionDateFilter(prev => ({
    ...prev,
    [name]: value
  }));
};

// دالة لتطبيق فلتر التاريخ على المعاملات
const applyTransactionDateFilter = () => {
  let filtered = [...delegateTransactions];
  
  if (transactionDateFilter.from) {
    filtered = filtered.filter(transaction => 
      new Date(transaction.Date) >= new Date(transactionDateFilter.from)
    );
  }
  
  if (transactionDateFilter.to) {
    filtered = filtered.filter(transaction => 
      new Date(transaction.Date) <= new Date(transactionDateFilter.to)
    );
  }
  
  return filtered;
};





// حساب العمولات المستحقة
// بديل أفضل - استخدام toast مع خيارات مخصصة
const calculateTotalCommission = () => {
  if (selectedClients.length === 0) {
    toast.error('يرجى تحديد عميل واحد على الأقل');
    return;
  }

  // حساب المبلغ الإجمالي المستحق لجميع العملاء المحددين
  let totalAmount = 0;
  const clientDetails = [];

  selectedClients.forEach(clientId => {
    const clientInfo = clients.find(c => c.PassportID === clientId);
    if (clientInfo) {
      const calculatedAmount = calculateCommission(clientInfo);
      if (calculatedAmount) {
        totalAmount += calculatedAmount;
        clientDetails.push({
          id: clientInfo.PassportID,
          name: clientInfo.NameArabic,
          amount: calculatedAmount,
          company: clientInfo.CompanyName || '-'
        });
      }
    }
  });

  if (totalAmount > 0) {
    // تحديث نموذج العمولة بالمبلغ الإجمالي
    setCommissionForm({
      ...commissionForm,
      amount: totalAmount.toString()
    });
    
    // تعيين بيانات التفاصيل وعرض النافذة المخصصة
    setCommissionDetails({
      clients: clientDetails,
      totalAmount: totalAmount
    });
    setShowCommissionDetails(true);
  } else {
    // استخدام toast مع ستايل تحذير مخصص
    toast('لم يتم العثور على مبالغ مستحقة للعملاء المحددين', {
      icon: '⚠️',
      style: {
        background: '#FEF3C7',
        color: '#92400E',
        border: '1px solid #F59E0B'
      },
      duration: 4000,
    });
  }
};

// عرض تفاصيل سجل عمولات العميل
const showClientCommissionHistory = (client) => {
if (!client.commissions || client.commissions.length === 0) {
  toast.info('لا يوجد سجل عمولات لهذا العميل');
  return;
}

// يمكنك هنا إضافة منطق لعرض نافذة منبثقة لعرض سجل العمولات
const historyText = client.commissions.map(comm => 
  `${new Date(comm.Date).toLocaleDateString('ar-EG')}: ${comm.Amount} ج.م - ${comm.Notes || 'بدون ملاحظات'}`
).join('\n');

alert(`سجل عمولات ${client.NameArabic}:\n${historyText}`);
};

// عرض تفاصيل المعاملة
const showTransactionDetails = (transaction) => {
setSelectedTransaction(transaction);
setShowTransactionModal(true);
};




// دالة عرض سجل التحويلات
// دالة عرض سجل معاملات العمولة
const handleViewCommissionTransactions = (clientId) => {
  const client = clients.find(c => c.PassportID === clientId);
  if (client) {
    setSelectedClientForTransactions(client);
    setShowCommissionTransactionsModal(true);
  }
};

// دالة إعادة تخصيص العمولة
// في دالة handleReassignCommission
// دالة إعادة تخصيص العمولة
const handleReassignCommission = async (client) => {
  try {
    console.log('بدء إعداد إعادة التخصيص...');
    
    // تحميل طرق الدفع إذا لم تكن محملة من نفس المسار الموحد المستخدم في التعديل
    if (!paymentMethods || paymentMethods.length === 0) {
      console.log('تحميل طرق الدفع...');
      const loadedMethods = await fetchPaymentMethods();
      if (!loadedMethods || loadedMethods.length === 0) {
        toast.error('لا توجد حسابات مصرفية متاحة. أضف حسابًا مصرفيًا من صفحة الخزينة أولًا.');
        return;
      }
    }
    
    // إعداد بيانات إعادة التخصيص
    const reassignData = {
      clientId: client.PassportID,
      clientName: client.NameArabic,
      originalDelegateId: client.commissionHistory.delegateId,
      originalDelegateName: client.commissionHistory.delegateName,
      currentDelegateId: selectedDelegate.DelegateID,
      currentDelegateName: selectedDelegate.DelegateName,
      amount: client.commissionHistory.amount,
      originalCommissionDate: client.commissionHistory.paidDate
    };
    
    setDebtConfirmData(reassignData);
    setShowDebtConfirmModal(true);
    
  } catch (error) {
    console.error('خطأ في إعداد إعادة التخصيص:', error);
    toast.error('حدث خطأ أثناء إعداد العملية');
  }
};


// أضف هذه الدالة بعد دالة handleReassignCommission:

const executeReassignWithDebt = async (paymentMethodForNew, updatedDebtData = null) => {
  try {
    const finalDebtData = updatedDebtData || debtConfirmData;
    
    console.log('بيانات إعادة التخصيص:', {
      finalDebtData,
      paymentMethodForNew
    });
    
    const response = await fetch(`${config.API_BASE_URL}/api/reassign-commission-with-debt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...finalDebtData,
        newPaymentMethod: {
          id: paymentMethodForNew.id,
          name: paymentMethodForNew.name || paymentMethodForNew.Name,
          type: paymentMethodForNew.type,
          accountNumber: paymentMethodForNew.accountNumber
        }
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'فشل في إعادة تخصيص العمولة');
    }
    
    const result = await response.json();
    console.log('نتيجة إعادة التخصيص:', result);
    
    let successMessage = `تم إعادة تخصيص العمولة بمبلغ ${finalDebtData.amount} ج.م وتسجيل سلفة على المندوب ${finalDebtData.originalDelegateName}`;
    
    if (result.bankTransactionId) {
      successMessage += `\n✅ تم إنشاء معاملة بنكية رقم: ${result.bankTransactionId}`;
    }
    
    toast.success(successMessage);
    
    // إغلاق النافذة أولاً
    setShowDebtConfirmModal(false);
    setDebtConfirmData(null);
    
    // تحديث العملاء محلياً
    setClients(prevClients => 
      prevClients.map(client => {
        if (client.PassportID === finalDebtData.clientId) {
          return {
            ...client,
            commissionHistory: { isPaidByOtherDelegate: false },
            commissionStatus: 'مدفوعة',
            commissionPaymentDate: new Date().toISOString()
          };
        }
        return client;
      })
    );
    
    setFilteredClients(prevFiltered => 
      prevFiltered.map(client => {
        if (client.PassportID === finalDebtData.clientId) {
          return {
            ...client,
            commissionHistory: { isPaidByOtherDelegate: false },
            commissionStatus: 'مدفوعة',
            commissionPaymentDate: new Date().toISOString()
          };
        }
        return client;
      })
    );
    
    // إعادة تحميل البيانات من الخادم
    setTimeout(async () => {
      await fetchDelegateClients(selectedDelegate.DelegateID);
      await fetchDelegateInfo(selectedDelegate.DelegateID);
      await fetchDelegateTransactions(selectedDelegate.DelegateID);
      
      // تحديث أرصدة المندوب الأصلي أيضاً
      try {
        await fetch(`${config.API_BASE_URL}/api/delegates/${finalDebtData.originalDelegateId}/update-balances`, {
          method: 'POST'
        });
      } catch (updateError) {
        console.error('خطأ في تحديث أرصدة المندوب الأصلي:', updateError);
      }
    }, 500);
    
  } catch (error) {
    console.error('خطأ في إعادة تخصيص العمولة:', error);
    toast.error('حدث خطأ أثناء إعادة تخصيص العمولة: ' + error.message);
  }
};

// عرض معلومات العميل
const showClientDetails = (client) => {
 // إنشاء عنصر div للنافذة المنبثقة
 const modalContainer = document.createElement('div');
 modalContainer.className = 'fixed inset-0 bg-gray-600 bg-opacity-70 overflow-y-auto h-full w-full z-[70] flex items-center justify-center';
 modalContainer.id = 'clientDetailsModal';

 // قسم تحذير العمولة المدفوعة مسبقاً
 const commissionWarningSection = client.commissionHistory?.isPaidByOtherDelegate ? `
   <div class="mb-4 bg-yellow-50 border-r-4 border-yellow-400 p-4">
     <div class="flex">
       <div class="flex-shrink-0">
         <svg class="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
           <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
         </svg>
       </div>
       <div class="mr-3">
         <h3 class="text-sm font-medium text-yellow-800">تم دفع العمولة مسبقاً</h3>
         <div class="mt-2 text-sm text-yellow-700">
           <p><strong>المندوب الأصلي:</strong> ${client.commissionHistory.delegateName}</p>
           <p><strong>تاريخ الدفع:</strong> ${new Date(client.commissionHistory.paidDate).toLocaleDateString('ar-EG')}</p>
           <p><strong>المبلغ المدفوع:</strong> ${client.commissionHistory.amount} ج.م</p>
         </div>
         <div class="mt-3 flex space-x-2 rtl:space-x-reverse">
           <button id="viewHistoryBtn" class="text-sm bg-white border border-yellow-300 text-yellow-800 px-3 py-1 rounded hover:bg-yellow-50">
  عرض سجل المعاملات
</button>
           <button id="reassignBtn" class="text-sm bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700">
             إعادة تخصيص العمولة
           </button>
         </div>
       </div>
     </div>
   </div>
 ` : '';

 // إنشاء محتوى النافذة المنبثقة
 modalContainer.innerHTML = `
   <div class="relative mx-auto p-0 border w-full max-w-md shadow-lg rounded-lg bg-white overflow-hidden">
     <div class="bg-gradient-to-r from-blue-600 to-indigo-700 py-4 px-6">
       <div class="flex justify-between items-center">
         <h3 class="text-xl font-bold text-white">معلومات العميل</h3>
         <button id="closeModalBtn" class="text-white hover:text-gray-200 transition-colors">
           <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
         </button>
       </div>
     </div>
     
     <div class="px-6 py-5">
       ${commissionWarningSection}
       
       <div class="mb-6">
         <div class="flex items-center justify-between mb-4">
           <div class="flex items-center">
             <div class="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center ml-3">
               <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-blue-600"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
             </div>
             <div>
               <h4 class="text-lg font-bold text-gray-900">${client.NameArabic || '-'}</h4>
               <p class="text-sm text-gray-500">${client.CompanyName || '-'}</p>
             </div>
           </div>
         </div>
       </div>

       <div class="mb-6">
         <h5 class="text-sm font-medium text-gray-500 mb-3">بيانات الهوية</h5>
         <div class="grid grid-cols-2 gap-4">
           <div class="bg-gray-50 rounded-md p-3">
             <p class="text-xs text-gray-500 mb-1">رقم الجواز</p>
             <p class="text-sm font-medium">${client.PassportNumber || '-'}</p>
           </div>
           <div class="bg-gray-50 rounded-md p-3">
             <p class="text-xs text-gray-500 mb-1">الهوية الوطنية</p>
             <p class="text-sm font-medium">${client.NationalID || '-'}</p>
           </div>
         </div>
       </div>

       <div class="mb-6">
         <h5 class="text-sm font-medium text-gray-500 mb-3">تفاصيل السفر</h5>
         <div class="grid grid-cols-2 gap-4">
           <div class="bg-gray-50 rounded-md p-3">
             <p class="text-xs text-gray-500 mb-1">مدة السفرة</p>
             <p class="text-sm font-medium">${client.TravelDuration || '-'}</p>
           </div>
           <div class="bg-gray-50 rounded-md p-3">
             <p class="text-xs text-gray-500 mb-1">تاريخ السفر</p>
             <p class="text-sm font-medium">${client.TravelDate ? new Date(client.TravelDate).toLocaleDateString('ar-EG') : '-'}</p>
           </div>
         </div>
       </div>
       
       <div class="mb-6">
         <h5 class="text-sm font-medium text-gray-500 mb-3">المهنة</h5>
         <div class="bg-gray-50 rounded-md p-3">
           <p class="text-sm font-medium">${client.PassportProfession || '-'}</p>
         </div>
       </div>

       <div class="mb-6">
         <h5 class="text-sm font-medium text-gray-500 mb-3">تفاصيل العمولة</h5>
         <div class="grid grid-cols-2 gap-4">
           <div class="bg-indigo-50 border border-indigo-100 rounded-md p-3">
             <p class="text-xs text-gray-500 mb-1">العمولة المستحقة</p>
             <p class="text-sm font-medium text-indigo-700">${client.calculatedCommission ? `${client.calculatedCommission} ج.م` : '-'}</p>
           </div>
           <div class="bg-green-50 border border-green-100 rounded-md p-3">
             <p class="text-xs text-gray-500 mb-1">العمولة المدفوعة</p>
             <p class="text-sm font-medium text-green-700">${client.totalPaidCommission ? `${client.totalPaidCommission} ج.م` : '-'}</p>
           </div>
         </div>
         
         <div class="mt-4 bg-gray-50 rounded-md p-3">
           <p class="text-xs text-gray-500 mb-1">حالة العمولة</p>
           <div class="flex items-center">
             ${client.commissionStatus === 'مدفوعة' 
               ? `<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                   <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ml-1"><polyline points="20 6 9 17 4 12"></polyline></svg>
                   مدفوعة
                 </span>` 
               : (client.commissionStatus === 'غير مدفوعة'
                 ? `<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                     <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ml-1"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                     غير مدفوعة
                   </span>`
                 : `<span class="text-sm text-gray-500">-</span>`
               )
             }
           </div>
         </div>
       </div>

       <div class="mt-6 flex justify-end">
         <button id="confirmBtn" class="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-md transition-colors">
           موافق
         </button>
       </div>
     </div>
   </div>
 `;

 // إضافة النافذة المنبثقة إلى DOM
 document.body.appendChild(modalContainer);

 // إضافة مستمعي الأحداث للأزرار
 document.getElementById('closeModalBtn').addEventListener('click', () => {
   document.getElementById('clientDetailsModal').remove();
 });

 document.getElementById('confirmBtn').addEventListener('click', () => {
   document.getElementById('clientDetailsModal').remove();
 });

 // إضافة مستمعي الأحداث للأزرار الجديدة
 if (client.commissionHistory?.isPaidByOtherDelegate) {
 document.getElementById('viewHistoryBtn')?.addEventListener('click', () => {
  document.getElementById('clientDetailsModal').remove();
  handleViewCommissionTransactions(client.PassportID);
});
   
   document.getElementById('reassignBtn')?.addEventListener('click', () => {
     document.getElementById('clientDetailsModal').remove();
     handleReassignCommission(client);
   });
 }

 // إضافة مستمع حدث للنقر خارج النافذة لإغلاقها
 modalContainer.addEventListener('click', (e) => {
   if (e.target === modalContainer) {
     modalContainer.remove();
   }
 });

 // في حالة فشل الطريقة الجديدة، استخدم Alert كخطة بديلة
 if (!document.getElementById('clientDetailsModal')) {
   alert(`معلومات العميل:
   الاسم: ${client.NameArabic || '-'}
   رقم الجواز: ${client.PassportNumber || '-'}
   الهوية الوطنية: ${client.NationalID || '-'}
   الشركة: ${client.CompanyName || '-'}
   مدة السفر: ${client.TravelDuration || '-'}
   تاريخ السفر: ${client.TravelDate ? new Date(client.TravelDate).toLocaleDateString('ar-EG') : '-'}
   المهنة: ${client.PassportProfession || '-'}
   العمولة المستحقة: ${client.calculatedCommission ? `${client.calculatedCommission} ج.م` : '-'}
   العمولة المدفوعة: ${client.totalPaidCommission ? `${client.totalPaidCommission} ج.م` : '-'}
   حالة العمولة: ${client.commissionStatus || '-'}
   `);
 }
};




// دوال التصدير المتقدم - أضف قبل useEffect

// دالة تحميل مكتبة SheetJS ديناميكياً - محدثة
const loadSheetJSLibrary = () => {
  return new Promise((resolve, reject) => {
    if (typeof window.XLSX !== 'undefined') {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
    script.onload = () => {
      if (typeof window.XLSX !== 'undefined' && window.XLSX.utils) {
        resolve();
      } else {
        reject(new Error('فشل في تحميل مكتبة Excel'));
      }
    };
    script.onerror = () => reject(new Error('فشل في تحميل مكتبة Excel'));
    document.head.appendChild(script);
  });
};

// دالة إنشاء ملف Excel منسق بصيغة xlsx
const createFormattedExcelFile = async (dataToExport, delegateInfo, dataType, stats) => {
  try {
    // تحميل مكتبة SheetJS إذا لم تكن محملة
    await loadSheetJSLibrary();

    const XLSX = window.XLSX;
    
    // إنشاء workbook جديد
    const wb = XLSX.utils.book_new();
    
    // تحضير البيانات للصف
    let worksheetData = [];
    
    // إضافة العنوان الرئيسي
    worksheetData.push([`تقرير المندوب: ${selectedDelegate?.DelegateName}`]);
    worksheetData.push([`تاريخ التقرير: ${new Date().toLocaleDateString('ar-EG')}`]);
    worksheetData.push([]); // صف فارغ
    
    // إضافة معلومات المندوب
    worksheetData.push(['معلومات المندوب']);
    worksheetData.push(['اسم المندوب', selectedDelegate?.DelegateName || '']);
    worksheetData.push(['رقم الهاتف', selectedDelegate?.PhoneNumber || '']);
    worksheetData.push(['إجمالي المسحوبات', `${formatMoneyValue(delegateInfo?.TotalWithdrawals || 0)}`]);
    worksheetData.push(['إجمالي العملاء', stats.totalClients]);
    worksheetData.push(['العملاء المسافرين', stats.traveledClients]);
    worksheetData.push([]); // صف فارغ
    
    if (dataType === 'clients') {
      // إضافة عنوان قائمة العملاء
      worksheetData.push(['قائمة عملاء المندوب']);
      worksheetData.push([]); // صف فارغ
      
      // إضافة رؤوس الأعمدة للعملاء
      worksheetData.push([
        'م',
        'اسم العميل',
        'الشركة',
        'قديم شركات',
        'رقم الجواز',
        'مدة السفر',
        'تاريخ السفر',
        'العمولة المستحقة',
        'حالة العمولة'
      ]);
      
      // إضافة بيانات العملاء
      dataToExport.forEach((client, index) => {
        // تحديد هل هو قديم شركات
        let isOldClient = false;
        try {
          if (client.PreviousCompanies) {
            let prevCompanies;
            if (typeof client.PreviousCompanies === 'string') {
              prevCompanies = JSON.parse(client.PreviousCompanies);
            } else {
              prevCompanies = client.PreviousCompanies;
            }
            
            isOldClient = Array.isArray(prevCompanies) && prevCompanies.some(prevCompanyId => 
              String(prevCompanyId) === String(client.CompanyID)
            );
          }
        } catch (e) {
          isOldClient = false;
        }
        
        const oldCompanyValue = isOldClient && client.CompanyName ? client.CompanyName : 'لا';
        
        worksheetData.push([
          index + 1,
          client.NameArabic || '',
          client.CompanyName || '',
          oldCompanyValue,
          client.PassportNumber || '',
          client.TravelDuration || '',
          client.TravelDate ? new Date(client.TravelDate).toLocaleDateString('ar-EG') : '-',
          client.calculatedCommission ? `${client.calculatedCommission}` : '-',
          client.commissionStatus || '-'
        ]);
      });
      
      // إضافة ملخص إحصائيات العملاء
      worksheetData.push([]); // صف فارغ
      worksheetData.push(['ملخص إحصائيات العملاء']);
      worksheetData.push(['إجمالي العملاء', dataToExport.length]);
      
      const traveledCount = dataToExport.filter(client => client.TravelDate).length;
      worksheetData.push(['العملاء المسافرين', traveledCount]);
      
      const paidCount = dataToExport.filter(client => client.commissionStatus === 'مدفوعة').length;
      const unpaidCount = dataToExport.filter(client => 
        client.TravelDate && client.commissionStatus !== 'مدفوعة'
      ).length;
      
      worksheetData.push(['العملاء بحالة مدفوعة', paidCount]);
      worksheetData.push(['العملاء بحالة غير مدفوعة', unpaidCount]);
      
    } else if (dataType === 'transactions') {
      // إضافة عنوان قائمة المعاملات
      worksheetData.push(['سجل معاملات المندوب']);
      worksheetData.push([]); // صف فارغ
      
      // إضافة رؤوس الأعمدة للمعاملات
      worksheetData.push([
        'م',
        'التاريخ',
        'الوقت',
        'نوع المعاملة',
        'المبلغ (ج.م)',
        'الرصيد (ج.م)',
        'الملاحظات',
        'العملاء المرتبطين',
        'طريقة الدفع'
      ]);
      
      let totalDeposits = 0;
      let totalWithdrawals = 0;
      
      // إضافة بيانات المعاملات
      dataToExport.forEach((transaction, index) => {
        // حساب عدد العملاء المرتبطين
        let clientsCount = 0;
        let paymentMethodName = '-';
        
        try {
          if (transaction.RelatedClients) {
            const relatedClients = typeof transaction.RelatedClients === 'string' 
              ? JSON.parse(transaction.RelatedClients)
              : transaction.RelatedClients;
              
            if (relatedClients && relatedClients.clients && Array.isArray(relatedClients.clients)) {
              clientsCount = relatedClients.clients.length;
            }
            
            // استخراج طريقة الدفع
            if (relatedClients && relatedClients.paymentMethod && relatedClients.paymentMethod.name) {
              paymentMethodName = relatedClients.paymentMethod.name;
            }
          }
        } catch (e) {
          console.error("خطأ في تحليل بيانات العملاء المرتبطين:", e);
        }
        
        // تجميع المبالغ حسب نوع المعاملة
        if (transaction.TransactionType === 'إيداع') {
          totalDeposits += parseFloat(transaction.Amount);
        } else if (transaction.TransactionType === 'سحب') {
          totalWithdrawals += parseFloat(transaction.Amount);
        }
        
        // تنسيق التاريخ والوقت
        const txDate = new Date(transaction.Date);
        const formattedDate = txDate.toLocaleDateString('ar-EG');
        const formattedTime = txDate.toLocaleTimeString('ar-EG');
        
        worksheetData.push([
          index + 1,
          formattedDate,
          formattedTime,
          transaction.TransactionType,
          `${transaction.Amount}`,
          `${transaction.Balance}`,
          transaction.Notes || '',
          clientsCount > 0 ? `${clientsCount} عميل` : '-',
          paymentMethodName
        ]);
      });
      
      // إضافة ملخص المعاملات
      worksheetData.push([]); // صف فارغ
      worksheetData.push(['ملخص المعاملات']);
      worksheetData.push(['إجمالي عدد المعاملات', dataToExport.length]);
      worksheetData.push(['إجمالي الإيداعات', `${totalDeposits.toFixed(2)} ج.م`]);
      worksheetData.push(['إجمالي المسحوبات', `${totalWithdrawals.toFixed(2)} ج.م`]);
      worksheetData.push(['صافي العمليات', `${(totalDeposits - totalWithdrawals).toFixed(2)} ج.م`]);
    }
    
    // إضافة تذييل
    worksheetData.push([]); // صف فارغ
    worksheetData.push(['تم إنشاء هذا التقرير بواسطة نظام إدارة حسابات المندوبين']);
    worksheetData.push([`تاريخ التصدير: ${new Date().toLocaleDateString('ar-EG')} ${new Date().toLocaleTimeString('ar-EG')}`]);
    
    // إنشاء الورقة
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // تحديد عروض الأعمدة
    const colWidths = [];
    for (let C = 0; C <= 8; ++C) {
      let maxWidth = 10;
      worksheetData.forEach((row, R) => {
        if (row[C]) {
          const cellLength = String(row[C]).length;
          if (cellLength > maxWidth) maxWidth = cellLength;
        }
      });
      colWidths[C] = { width: Math.min(maxWidth + 2, 50) };
    }
    ws['!cols'] = colWidths;
    
    // إضافة الورقة إلى الكتاب
    const sheetName = dataType === 'clients' ? 'عملاء المندوب' : 'معاملات المندوب';
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    
    // تصدير الملف
    const fileName = `تقرير_${dataType === 'clients' ? 'عملاء' : 'معاملات'}_${selectedDelegate?.DelegateName}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    toast.success('تم تصدير التقرير بصيغة xlsx بنجاح!');
    
  } catch (error) {
    console.error('خطأ في التصدير:', error);
    toast.error('حدث خطأ أثناء التصدير: ' + error.message);
  }
};

// دالة تنسيق التاريخ للعرض في Excel
const formatDateForExcel = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// إنشاء HTML مع تنسيق Excel كامل للمندوبين
const createDelegateExcelHTML = (data, delegateInfo, dataType, stats) => {
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('ar-EG', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long'
  });

  let tableContent = '';
  let summaryText = '';
  
  if (dataType === 'clients') {
    summaryText = `إجمالي العملاء ${data.length} / المسافرين ${stats.traveledClients} / العمولات المدفوعة ${stats.paidCommission}`;
    
    // رؤوس جدول العملاء
    tableContent = `
      <tr>
        <td class="table-header">م</td>
        <td class="table-header">اسم العميل</td>
        <td class="table-header">الشركة</td>
        <td class="table-header">قديم شركات</td>
        <td class="table-header">رقم الجواز</td>
        <td class="table-header">مدة السفر</td>
        <td class="table-header">تاريخ السفر</td>
        <td class="table-header">العمولة المستحقة</td>
        <td class="table-header">حالة العمولة</td>
      </tr>
    `;
    
    // بيانات العملاء
    data.forEach((client, index) => {
      // تحديد هل هو قديم شركات
      let isOldClient = false;
      try {
        if (client.PreviousCompanies) {
          let prevCompanies;
          if (typeof client.PreviousCompanies === 'string') {
            prevCompanies = JSON.parse(client.PreviousCompanies);
          } else {
            prevCompanies = client.PreviousCompanies;
          }
          if (Array.isArray(prevCompanies)) {
            isOldClient = prevCompanies.some(id => String(id) === String(client.CompanyID));
          }
        }
      } catch (e) {
        isOldClient = false;
      }
      
      tableContent += `
        <tr>
          <td class="row-number">${index + 1}</td>
          <td class="data-cell">${client.NameArabic || ''}</td>
          <td class="data-cell">${client.CompanyName || ''}</td>
          <td class="data-cell">${isOldClient && client.CompanyName ? client.CompanyName : 'لا'}</td>
          <td class="data-cell">${client.PassportNumber || ''}</td>
          <td class="data-cell">${client.TravelDuration || ''}</td>
          <td class="date-cell">${client.TravelDate ? formatDateForExcel(client.TravelDate) : '-'}</td>
          <td class="amount-cell">${client.calculatedCommission ? `${client.calculatedCommission}` : '-'}</td>
          <td class="type-cell">${client.commissionStatus || '-'}</td>
        </tr>
      `;
    });
    
  } else if (dataType === 'transactions') {
    summaryText = `إجمالي المعاملات ${data.length} / إجمالي المسحوبات ${formatMoneyValue(delegateInfo?.TotalWithdrawals || 0)}`;
    
    // رؤوس جدول المعاملات
    tableContent = `
      <tr>
        <td class="table-header">م</td>
        <td class="table-header">التاريخ</td>
        <td class="table-header">الوقت</td>
        <td class="table-header">نوع المعاملة</td>
        <td class="table-header">المبلغ (ج.م)</td>
        <td class="table-header">الرصيد (ج.م)</td>
        <td class="table-header">الملاحظات</td>
        <td class="table-header">العملاء المرتبطين</td>
      </tr>
    `;
    
    // بيانات المعاملات
    data.forEach((transaction, index) => {
      // حساب عدد العملاء المرتبطين
      let clientsCount = 0;
      try {
        if (transaction.RelatedClients) {
          const relatedClients = typeof transaction.RelatedClients === 'string' 
            ? JSON.parse(transaction.RelatedClients)
            : transaction.RelatedClients;
            
          if (relatedClients && relatedClients.clients && Array.isArray(relatedClients.clients)) {
            clientsCount = relatedClients.clients.length;
          }
        }
      } catch (e) {
        console.error("خطأ في تحليل العملاء المرتبطين:", e);
      }
      
      const txDate = new Date(transaction.Date);
      const formattedTime = txDate.toLocaleTimeString('ar-EG');
      
      tableContent += `
        <tr>
          <td class="row-number">${index + 1}</td>
          <td class="date-cell">${formatDateForExcel(transaction.Date)}</td>
          <td class="data-cell">${formattedTime}</td>
          <td class="type-cell">${transaction.TransactionType}</td>
          <td class="amount-cell">${parseInt(transaction.Amount).toLocaleString()}</td>
          <td class="amount-cell">${parseInt(transaction.Balance).toLocaleString()}</td>
          <td class="data-cell">${transaction.Notes || '-'}</td>
          <td class="data-cell">${clientsCount > 0 ? `${clientsCount} عميل` : '-'}</td>
        </tr>
      `;
    });
  }

  return `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta charset="UTF-8">
      <!--[if gte mso 9]>
      <xml>
        <x:ExcelWorkbook>
          <x:ExcelWorksheets>
            <x:ExcelWorksheet>
              <x:Name>تقرير المندوب</x:Name>
              <x:WorksheetOptions>
                <x:Print>
                  <x:ValidPrinterInfo/>
                  <x:PaperSizeIndex>9</x:PaperSizeIndex>
                  <x:HorizontalResolution>600</x:HorizontalResolution>
                  <x:VerticalResolution>600</x:VerticalResolution>
                  <x:Scale>100</x:Scale>
                  <x:FitToPage/>
                  <x:FitWidth>1</x:FitWidth>
                  <x:FitHeight>32767</x:FitHeight>
                  <x:Orientation>Portrait</x:Orientation>
                </x:Print>
                <x:Selected/>
                <x:ProtectContents>False</x:ProtectContents>
                <x:ProtectObjects>False</x:ProtectObjects>
                <x:ProtectScenarios>False</x:ProtectScenarios>
              </x:WorksheetOptions>
            </x:ExcelWorksheet>
          </x:ExcelWorksheets>
        </x:ExcelWorkbook>
      </xml>
      <![endif]-->
      <style>
        table { 
          border-collapse: collapse; 
          width: 100%; 
          direction: rtl;
          font-family: Calibri, Arial, sans-serif;
          page-break-inside: avoid;
        }
        
        td {
          border: 2px solid #000000 !important;
          padding: 8px !important;
          vertical-align: middle !important;
          text-align: center !important;
          color: #000000 !important;
          font-weight: normal !important;
        }
        
        .header-main {
          background-color: #ADD8E6 !important;
          font-size: 22pt !important;
          font-weight: bold !important;
          text-align: center !important;
          padding: 12px !important;
          height: 45px !important;
          color: #000000 !important;
        }
        
        .summary-row {
          background-color: #ADD8E6 !important;
          font-size: 22pt !important;
          font-weight: bold !important;
          text-align: center !important;
          padding: 12px !important;
          height: 45px !important;
          color: #000000 !important;
        }
        
        .table-header {
          background-color: #D3D3D3 !important;
          font-size: 22pt !important;
          font-weight: bold !important;
          text-align: center !important;
          padding: 10px !important;
          height: 40px !important;
          color: #000000 !important;
        }
        
        .data-cell {
          font-size: 18pt !important;
          text-align: center !important;
          padding: 8px !important;
          height: 35px !important;
          background-color: #FFFFFF !important;
          color: #000000 !important;
          font-weight: normal !important;
        }
        
        .row-number {
          background-color: #F0F0F0 !important;
          font-weight: bold !important;
          font-size: 18pt !important;
          color: #000000 !important;
        }
        
        .date-cell {
          font-weight: bold !important;
          font-size: 18pt !important;
          background-color: #FFFFFF !important;
          color: #000000 !important;
        }
        
        .type-cell {
          font-size: 18pt !important;
          background-color: #FFFFFF !important;
          color: #000000 !important;
          font-weight: normal !important;
        }
        
        .amount-cell {
          font-weight: bold !important;
          font-size: 18pt !important;
          background-color: #FFFFFF !important;
          color: #000000 !important;
        }
      </style>
    </head>
    <body>
      <table>
        <!-- تاريخ التقرير -->
        <tr>
          <td colspan="9" class="header-main">
            تقرير المندوب: ${delegateInfo?.DelegateName || selectedDelegate?.DelegateName} - ${formattedDate}
          </td>
        </tr>
        
        <!-- الملخص -->
        <tr>
          <td colspan="9" class="summary-row">
            ${summaryText}
          </td>
        </tr>
        
        <!-- رؤوس الجدول والبيانات -->
        ${tableContent}
      </table>
    </body>
    </html>
  `;
};

// دالة الحصول على البيانات للتصدير
const getExportData = () => {
  let dataToExport = [];
  
  if (exportOptions.dataType === 'clients') {
    dataToExport = [...filteredClients];
    
    // تطبيق فلاتر إضافية حسب الخيارات
    if (exportOptions.selectedStatuses.length > 0 && !exportOptions.selectedStatuses.includes('all')) {
      if (exportOptions.selectedStatuses.includes('traveled')) {
        dataToExport = dataToExport.filter(client => client.TravelDate);
      }
      if (exportOptions.selectedStatuses.includes('paid')) {
        dataToExport = dataToExport.filter(client => client.commissionStatus === 'مدفوعة');
      }
      if (exportOptions.selectedStatuses.includes('unpaid')) {
        dataToExport = dataToExport.filter(client => client.commissionStatus === 'غير مدفوعة');
      }
    }
    
    // فلترة حسب التاريخ المخصص
    if (exportOptions.dateRange === 'custom') {
      if (exportOptions.customStartDate) {
        dataToExport = dataToExport.filter(client => 
          !client.TravelDate || new Date(client.TravelDate) >= new Date(exportOptions.customStartDate)
        );
      }
      if (exportOptions.customEndDate) {
        dataToExport = dataToExport.filter(client => 
          !client.TravelDate || new Date(client.TravelDate) <= new Date(exportOptions.customEndDate)
        );
      }
    }
    
  } else if (exportOptions.dataType === 'transactions') {
    dataToExport = [...delegateTransactions];
    
    // فلترة حسب التاريخ المخصص للمعاملات
    if (exportOptions.dateRange === 'custom') {
      if (exportOptions.customStartDate) {
        dataToExport = dataToExport.filter(transaction => 
          new Date(transaction.Date) >= new Date(exportOptions.customStartDate)
        );
      }
      if (exportOptions.customEndDate) {
        dataToExport = dataToExport.filter(transaction => 
          new Date(transaction.Date) <= new Date(exportOptions.customEndDate)
        );
      }
    }
  }
  
  return dataToExport;
};

// تصدير إلى Excel المتقدم للمندوبين - محدث لصيغة xlsx
const exportToExcelAdvanced = async () => {
  try {
    const dataToExport = getExportData();
    
    if (dataToExport.length === 0) {
      toast.error('لا توجد بيانات للتصدير');
      return;
    }
    
    // استخدام دالة إنشاء ملف Excel المنسق الجديدة
    await createFormattedExcelFile(
      dataToExport, 
      delegateInfo, 
      exportOptions.dataType,
      stats
    );
    
  } catch (error) {
    console.error('خطأ في التصدير:', error);
    toast.error('حدث خطأ أثناء التصدير');
  }
};

// تصدير إلى PDF للمندوبين
// استبدال دالة تصدير PDF بالتنسيق الجديد
const exportToPDF = () => {
  // إعداد صفحة الطباعة
  const printContent = document.createElement('div');
  printContent.dir = 'rtl';
  printContent.className = 'print-content';

  // إضافة معلومات المندوب بشكل أنيق
  const delegateInfo = document.createElement('div');
  delegateInfo.innerHTML = `
    <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #3B82F6; padding-bottom: 20px;">
      <h1 style="font-size: 24px; color: #1F2937; margin-bottom: 5px;">تقرير المندوب: ${selectedDelegate.DelegateName}</h1>
      <p style="font-size: 12px; color: #6B7280;">تم إنشاء التقرير بتاريخ: ${new Date().toLocaleDateString('ar-EG')} الساعة ${new Date().toLocaleTimeString('ar-EG')}</p>
    </div>
    
    <div style="display: flex; justify-content: space-between; margin-bottom: 30px; background-color: #F3F4F6; padding: 15px; border-radius: 8px;">
      <div style="flex: 1;">
        <div style="margin-bottom: 10px;">
          <p style="font-size: 12px; color: #6B7280; margin-bottom: 3px;">اسم المندوب</p>
          <p style="font-size: 16px; font-weight: bold; color: #1F2937;">${selectedDelegate.DelegateName}</p>
        </div>
        <div>
          <p style="font-size: 12px; color: #6B7280; margin-bottom: 3px;">رقم الهاتف</p>
          <p style="font-size: 16px; color: #1F2937;">${selectedDelegate.PhoneNumber || '-'}</p>
        </div>
      </div>
      
      <div style="flex: 1; text-align: center; border-right: 1px solid #D1D5DB; border-left: 1px solid #D1D5DB; padding: 0 15px;">
        <div style="margin-bottom: 10px;">
          <p style="font-size: 12px; color: #6B7280; margin-bottom: 3px;">إجمالي العملاء</p>
          <p style="font-size: 18px; font-weight: bold; color: #1F2937;">${stats.totalClients}</p>
        </div>
        <div>
          <p style="font-size: 12px; color: #6B7280; margin-bottom: 3px;">العملاء المسافرين</p>
          <p style="font-size: 18px; color: #1F2937;">${stats.traveledClients || 0}</p>
        </div>
      </div>
      
        <div style="flex: 1; text-align: left;">
        <div style="margin-bottom: 10px;">
          <p style="font-size: 12px; color: #6B7280; margin-bottom: 3px;">إجمالي المسحوبات</p>
          <p style="font-size: 18px; font-weight: bold; color: #DC2626;">${window.delegateInfo?.TotalWithdrawals || delegateInfo?.TotalWithdrawals || 0} ج.م</p>
        </div>
        <div>
          <p style="font-size: 12px; color: #6B7280; margin-bottom: 3px;">عدد المعاملات</p>
          <p style="font-size: 18px; color: #1F2937;">${delegateTransactions?.length || 0}</p>
        </div>
      </div>
    </div>
  `;
  printContent.appendChild(delegateInfo);

  // إضافة محتوى التبويب النشط
  if (activeTab === 'clients') {
    // عنوان وتاريخ الفلترة
    const clientsHeader = document.createElement('div');
    clientsHeader.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
        <h2 style="font-size: 18px; color: #1F2937; margin: 0;">قائمة عملاء المندوب</h2>
        <div style="font-size: 12px; color: #6B7280;">عدد العملاء: ${filteredClients.length}</div>
      </div>
    `;
    printContent.appendChild(clientsHeader);
    
    // طباعة قائمة العملاء
    const clientsTable = document.createElement('table');
    clientsTable.style.width = '100%';
    clientsTable.style.borderCollapse = 'collapse';
    clientsTable.style.marginBottom = '20px';
    clientsTable.style.fontSize = '12px';
    
    // إضافة رأس الجدول
    clientsTable.innerHTML = `
      <thead>
        <tr style="background-color: #F9FAFB;">
          <th style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">الاسم</th>
          <th style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">الشركة</th>
          <th style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">قديم شركات</th>
          <th style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">رقم الجواز</th>
          <th style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">مدة السفر</th>
          <th style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">تاريخ السفر</th>
          <th style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">حالة العمولة</th>
        </tr>
      </thead>
      <tbody>
    `;
    // إضافة صفوف الجدول
    filteredClients.forEach((client, index) => {
      // معالجة حالة "قديم شركات" بشكل مشابه للواجهة
      let oldCompanyCell = '-';
      if (client.PreviousCompanies) {
        try {
          let prevCompanies;
          if (typeof client.PreviousCompanies === 'string') {
            prevCompanies = JSON.parse(client.PreviousCompanies);
          } else {
            prevCompanies = client.PreviousCompanies;
          }
          
          const isOldCompany = Array.isArray(prevCompanies) && prevCompanies.some(prevCompanyId => 
            String(prevCompanyId) === String(client.CompanyID)
          );
          
          if (isOldCompany && client.CompanyName) {
            oldCompanyCell = `<span style="display: inline-block; padding: 2px 6px; background-color: #FEF3C7; color: #92400E; border-radius: 9999px; font-size: 11px;">${client.CompanyName}</span>`;
          }
        } catch (e) {
          oldCompanyCell = '-';
        }
      }
      
      // معالجة حالة العمولة
      let commissionStatusCell = '-';
      if (client.TravelDate) {
        if (client.commissionStatus === 'مدفوعة') {
          commissionStatusCell = `<span style="display: inline-block; padding: 2px 6px; background-color: #D1FAE5; color: #065F46; border-radius: 9999px; font-size: 11px;">✓ مدفوعة</span>`;
        } else {
          commissionStatusCell = `<span style="display: inline-block; padding: 2px 6px; background-color: #FEF3C7; color: #92400E; border-radius: 9999px; font-size: 11px;">✗ غير مدفوعة</span>`;
        }
      }
      
      // معالجة تاريخ السفر
      let travelDateCell = '-';
      if (client.TravelDate) {
        travelDateCell = `<span style="display: inline-block; padding: 2px 6px; background-color: #DCFCE7; color: #166534; border-radius: 9999px; font-size: 11px;">${new Date(client.TravelDate).toLocaleDateString('ar-EG')}</span>`;
      }
      
      // إضافة صف بلون خلفية مختلف للصفوف البديلة
      const rowStyle = index % 2 === 0 ? '' : 'background-color: #F9FAFB;';
      
      clientsTable.innerHTML += `
        <tr style="${rowStyle}">
          <td style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">${client.NameArabic || ''}</td>
          <td style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">${client.CompanyName || ''}</td>
          <td style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">${oldCompanyCell}</td>
          <td style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">${client.PassportNumber || ''}</td>
          <td style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">${client.TravelDuration || ''}</td>
          <td style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">${travelDateCell}</td>
          <td style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">${commissionStatusCell}</td>
        </tr>
      `;
    });
    
    clientsTable.innerHTML += '</tbody>';
    printContent.appendChild(clientsTable);
  } else if (activeTab === 'transactions') {
    // تطبيق فلترة التاريخ على المعاملات
    const filteredTransactions = applyTransactionDateFilter ? applyTransactionDateFilter() : delegateTransactions;
    
    // عنوان وتاريخ الفلترة
    const transactionsHeader = document.createElement('div');
    transactionsHeader.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
        <h2 style="font-size: 18px; color: #1F2937; margin: 0;">سجل معاملات المندوب</h2>
        <div style="font-size: 12px; color: #6B7280;">عدد المعاملات: ${filteredTransactions.length}</div>
      </div>
      ${(transactionDateFilter && (transactionDateFilter.from || transactionDateFilter.to)) ? 
        `<div style="font-size: 11px; color: #6B7280; margin-bottom: 15px; padding: 5px 10px; background-color: #F3F4F6; border-radius: 4px; display: inline-block;">
          الفترة: ${transactionDateFilter.from ? new Date(transactionDateFilter.from).toLocaleDateString('ar-EG') : 'البداية'} إلى ${transactionDateFilter.to ? new Date(transactionDateFilter.to).toLocaleDateString('ar-EG') : 'النهاية'}
        </div>` : ''
      }
    `;
    printContent.appendChild(transactionsHeader);
    
    // طباعة سجل المعاملات
    const transactionsTable = document.createElement('table');
    transactionsTable.style.width = '100%';
    transactionsTable.style.borderCollapse = 'collapse';
    transactionsTable.style.marginBottom = '20px';
    transactionsTable.style.fontSize = '12px';
    
    // إضافة رأس الجدول
    transactionsTable.innerHTML = `
      <thead>
        <tr style="background-color: #F9FAFB;">
          <th style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">التاريخ</th>
          <th style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">نوع المعاملة</th>
          <th style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">المبلغ</th>
          <th style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">الرصيد</th>
          <th style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">الملاحظات</th>
          <th style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">العملاء المرتبطين</th>
        </tr>
      </thead>
      <tbody>
    `;
    
    // إضافة صفوف الجدول
    filteredTransactions.forEach((transaction, index) => {
      // حساب عدد العملاء المرتبطين
      let clientsCount = 0;
      try {
        if (transaction.RelatedClients) {
          const relatedClients = typeof transaction.RelatedClients === 'string' 
            ? JSON.parse(transaction.RelatedClients)
            : transaction.RelatedClients;
            
          if (relatedClients && relatedClients.clients && Array.isArray(relatedClients.clients)) {
            clientsCount = relatedClients.clients.length;
          }
        }
      } catch (e) {
        console.error("خطأ في تحليل العملاء المرتبطين:", e);
      }
      
      // نوع المعاملة بألوان مختلفة
      let transactionTypeCell = '';
      if (transaction.TransactionType === 'إيداع') {
        transactionTypeCell = `<span style="display: inline-block; padding: 2px 6px; background-color: #D1FAE5; color: #065F46; border-radius: 9999px; font-size: 11px;">${transaction.TransactionType}</span>`;
      } else if (transaction.TransactionType === 'سحب') {
        transactionTypeCell = `<span style="display: inline-block; padding: 2px 6px; background-color: #FEE2E2; color: #B91C1C; border-radius: 9999px; font-size: 11px;">${transaction.TransactionType}</span>`;
      } else {
        transactionTypeCell = `<span style="display: inline-block; padding: 2px 6px; background-color: #DBEAFE; color: #1E40AF; border-radius: 9999px; font-size: 11px;">${transaction.TransactionType}</span>`;
      }
      
      // عدد العملاء المرتبطين
      let clientsCountCell = clientsCount > 0 
        ? `<span style="display: inline-block; padding: 2px 6px; background-color: #E0E7FF; color: #4F46E5; border-radius: 9999px; font-size: 11px;">${clientsCount} عميل</span>`
        : '-';
      
      // لون خلفية الصف
      const rowStyle = index % 2 === 0 ? '' : 'background-color: #F9FAFB;';
      
      transactionsTable.innerHTML += `
        <tr style="${rowStyle}">
          <td style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">
            <div>${new Date(transaction.Date).toLocaleDateString('ar-EG')}</div>
            <div style="font-size: 10px; color: #6B7280;">${new Date(transaction.Date).toLocaleTimeString('ar-EG')}</div>
          </td>
          <td style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">${transactionTypeCell}</td>
          <td style="border: 1px solid #E5E7EB; padding: 8px; text-align: right; font-weight: bold; ${transaction.TransactionType === 'سحب' ? 'color: #B91C1C;' : 'color: #047857;'}">${transaction.Amount} ج.م</td>
          <td style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">${transaction.Balance} ج.م</td>
          <td style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">${transaction.Notes || '-'}</td>
          <td style="border: 1px solid #E5E7EB; padding: 8px; text-align: right;">${clientsCountCell}</td>
        </tr>
      `;
    });
    
    transactionsTable.innerHTML += '</tbody>';
    printContent.appendChild(transactionsTable);
    
    // إضافة ملخص إحصائي للمعاملات
    const transactionsSummary = document.createElement('div');
    
    // حساب إجماليات المعاملات
    const depositTotal = filteredTransactions.reduce((sum, tx) => 
      tx.TransactionType === 'إيداع' ? sum + parseFloat(tx.Amount) : sum, 0
    );
    
    const withdrawalTotal = filteredTransactions.reduce((sum, tx) => 
      tx.TransactionType === 'سحب' ? sum + parseFloat(tx.Amount) : sum, 0
    );
    
    transactionsSummary.innerHTML = `
      <div style="display: flex; justify-content: space-between; margin-top: 20px; padding: 15px; background-color: #F3F4F6; border-radius: 8px;">
        <div style="text-align: center; flex: 1;">
          <p style="font-size: 12px; color: #6B7280; margin-bottom: 3px;">إجمالي الإيداعات</p>
          <p style="font-size: 16px; font-weight: bold; color: #047857;">${depositTotal.toFixed(2)} ج.م</p>
        </div>
        <div style="text-align: center; flex: 1; border-right: 1px solid #D1D5DB; border-left: 1px solid #D1D5DB;">
          <p style="font-size: 12px; color: #6B7280; margin-bottom: 3px;">إجمالي السحوبات</p>
          <p style="font-size: 16px; font-weight: bold; color: #B91C1C;">${withdrawalTotal.toFixed(2)} ج.م</p>
        </div>
        <div style="text-align: center; flex: 1;">
          <p style="font-size: 12px; color: #6B7280; margin-bottom: 3px;">صافي الرصيد</p>
          <p style="font-size: 16px; font-weight: bold; color: ${depositTotal - withdrawalTotal >= 0 ? '#047857' : '#B91C1C'};">${(depositTotal - withdrawalTotal).toFixed(2)} ج.م</p>
        </div>
      </div>
    `;
    
    printContent.appendChild(transactionsSummary);
  }
  
  // إضافة تذييل للتقرير
  const footer = document.createElement('div');
  footer.innerHTML = `
    <div style="text-align: center; margin-top: 30px; border-top: 1px solid #E5E7EB; padding-top: 15px; font-size: 10px; color: #9CA3AF;">
      <p>تم إنشاء هذا التقرير بواسطة نظام إدارة حسابات المندوبين</p>
      <p>${new Date().toLocaleDateString('ar-EG')} - ${new Date().toLocaleTimeString('ar-EG')}</p>
    </div>
  `;
  printContent.appendChild(footer);
  
  // إنشاء نافذة جديدة للطباعة
  const printWindow = window.open('', '_blank');
  printWindow.document.write(`
    <html>
      <head>
        <title>تقرير المندوب: ${selectedDelegate.DelegateName}</title>
        <meta charset="UTF-8">
        <style>
          @media print {
            body { 
              font-family: Tahoma, Arial, sans-serif; 
              direction: rtl; 
              padding: 20px;
              color: #1F2937;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 20px;
              page-break-inside: auto;
            }
            tr { page-break-inside: avoid; page-break-after: auto; }
            th, td { 
              border: 1px solid #E5E7EB; 
              padding: 8px; 
              text-align: right; 
              font-size: 12px;
            }
            th { background-color: #F9FAFB; font-weight: bold; }
            h1 { text-align: center; color: #1F2937; }
            h2 { margin-top: 30px; margin-bottom: 10px; color: #1F2937; }
            @page { size: A4; margin: 10mm; }
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
    </html>
  `);
  
  printWindow.document.close();
  
  // تنفيذ الطباعة
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 500);
};

// تنفيذ التصدير حسب النوع المختار
const executeExport = () => {
  if (exportOptions.format === 'excel') {
    exportToExcelAdvanced();
  } else if (exportOptions.format === 'pdf') {
    exportToPDF();
  }
  setIsExportModalOpen(false);
};

// دالة عرض التقرير المتقدم
const showAdvancedExport = () => {
  if (!selectedDelegate) {
    toast.error('يرجى اختيار مندوب أولاً');
    return;
  }
  setIsExportModalOpen(true);
};



// تصدير البيانات إلى Excel مع تنسيق مبهر
const handleExport = () => {
  if (!selectedDelegate) {
    toast.error('يرجى اختيار مندوب أولاً');
    return;
  }

  try {
    // تحديد نوع الملف المصدر (CSV أو اكسل)
    const fileType = 'csv'; // يمكن تغييرها إلى 'xlsx' إذا تم دعم مكتبة xlsx
    
    // إنشاء بيانات للتصدير
    const exportData = [];
    
    // إضافة عنوان التقرير
    exportData.push(['تقرير المندوب', selectedDelegate.DelegateName]);
    exportData.push(['تاريخ التقرير', new Date().toLocaleDateString('ar-EG')]);
    exportData.push(['', '']);
    
    // إضافة معلومات المندوب
    exportData.push(['معلومات المندوب', '']);
    exportData.push(['اسم المندوب', selectedDelegate.DelegateName]);
    exportData.push(['رقم الهاتف', delegateInfo?.PhoneNumber || '']);
    exportData.push(['إجمالي المسحوبات', `${formatMoneyValue(delegateInfo?.TotalWithdrawals || 0)}`]);
    exportData.push(['إجمالي العملاء', stats.totalClients]);
    exportData.push(['العملاء المسافرين', stats.traveledClients || 0]);
    exportData.push(['', '']);
    
    // إضافة بيانات العملاء
    if (activeTab === 'clients') {
      // إذا كانت هناك فلترة مطبقة، أضف معلومات الفلترة
      if (filters && !filters.showAll) {
        let filterType = '';
        if (filters.onlyTraveled) filterType = 'المسافرين فقط';
        else if (filters.onlyPaid) filterType = 'تم دفع العمولة';
        else if (filters.onlyUnpaid) filterType = 'لم يتم دفع العمولة';
        
        exportData.push(['فلترة العملاء', filterType]);
        exportData.push(['', '']);
      }
      
      exportData.push(['قائمة العملاء', '', '', '', '', '', '']);
      exportData.push(['م', 'الاسم', 'الشركة', 'قديم شركات', 'رقم الجواز', 'مدة السفر', 'تاريخ السفر', 'حالة العمولة']);
      
      filteredClients.forEach((client, index) => {
        // معالجة حالة "قديم شركات" بشكل مشابه للواجهة
        let isOldCompany = false;
        if (client.PreviousCompanies) {
          try {
            let prevCompanies;
            if (typeof client.PreviousCompanies === 'string') {
              prevCompanies = JSON.parse(client.PreviousCompanies);
            } else {
              prevCompanies = client.PreviousCompanies;
            }
            
            isOldCompany = Array.isArray(prevCompanies) && prevCompanies.some(prevCompanyId => 
              String(prevCompanyId) === String(client.CompanyID)
            );
          } catch (e) {
            isOldCompany = false;
          }
        }
        
        const oldCompanyValue = isOldCompany && client.CompanyName ? client.CompanyName : 'لا';
        
        exportData.push([
          index + 1,
          client.NameArabic || '',
          client.CompanyName || '',
          oldCompanyValue,
          client.PassportNumber || '',
          client.TravelDuration || '',
          client.TravelDate ? new Date(client.TravelDate).toLocaleDateString('ar-EG') : '-',
          client.commissionStatus || '-'
        ]);
      });
      
      // إضافة ملخص بعد قائمة العملاء
      exportData.push(['', '']);
      exportData.push(['ملخص إحصائيات العملاء', '']);
      exportData.push(['إجمالي العملاء', filteredClients.length]);
      
      // عدد العملاء المسافرين
      const traveledCount = filteredClients.filter(client => client.TravelDate).length;
      exportData.push(['العملاء المسافرين', traveledCount]);
      
      // عدد العملاء بحالة دفع العمولة
      const paidCount = filteredClients.filter(client => client.commissionStatus === 'مدفوعة').length;
      const unpaidCount = filteredClients.filter(client => 
        client.TravelDate && client.commissionStatus !== 'مدفوعة'
      ).length;
      
      exportData.push(['العملاء بحالة مدفوعة', paidCount]);
      exportData.push(['العملاء بحالة غير مدفوعة', unpaidCount]);
    }
    
    // إضافة بيانات المعاملات
    if (activeTab === 'transactions') {
      // تطبيق فلترة التاريخ على المعاملات
      const filteredTransactions = applyTransactionDateFilter ? applyTransactionDateFilter() : delegateTransactions;
      
      // إذا كانت هناك فلترة تاريخ مطبقة، أضف معلومات الفلترة
      if (transactionDateFilter && (transactionDateFilter.from || transactionDateFilter.to)) {
        exportData.push(['فلترة المعاملات حسب التاريخ', '']);
        if (transactionDateFilter.from) {
          exportData.push(['من تاريخ', new Date(transactionDateFilter.from).toLocaleDateString('ar-EG')]);
        }
        if (transactionDateFilter.to) {
          exportData.push(['إلى تاريخ', new Date(transactionDateFilter.to).toLocaleDateString('ar-EG')]);
        }
        exportData.push(['', '']);
      }
      
      exportData.push(['سجل المعاملات', '', '', '', '', '', '', '']);
      exportData.push(['م', 'التاريخ', 'الوقت', 'نوع المعاملة', 'المبلغ', 'الرصيد', 'الملاحظات', 'العملاء المرتبطين']);
      
      let totalDeposits = 0;
      let totalWithdrawals = 0;
      
      filteredTransactions.forEach((transaction, index) => {
        // حساب عدد العملاء المرتبطين
        let clientsCount = 0;
        try {
          if (transaction.RelatedClients) {
            const relatedClients = typeof transaction.RelatedClients === 'string' 
              ? JSON.parse(transaction.RelatedClients)
              : transaction.RelatedClients;
              
            if (relatedClients && relatedClients.clients && Array.isArray(relatedClients.clients)) {
              clientsCount = relatedClients.clients.length;
            }
          }
        } catch (e) {
          console.error("خطأ في تحليل العملاء المرتبطين:", e);
        }
        
        // تجميع المبالغ حسب نوع المعاملة
        if (transaction.TransactionType === 'إيداع') {
          totalDeposits += parseFloat(transaction.Amount);
        } else if (transaction.TransactionType === 'سحب') {
          totalWithdrawals += parseFloat(transaction.Amount);
        }
        
        // تنسيق التاريخ والوقت
        const txDate = new Date(transaction.Date);
        const formattedDate = txDate.toLocaleDateString('ar-EG');
        const formattedTime = txDate.toLocaleTimeString('ar-EG');
        
        exportData.push([
          index + 1,
          formattedDate,
          formattedTime,
          transaction.TransactionType,
          `${transaction.Amount} ج.م`,
          `${transaction.Balance} ج.م`,
          transaction.Notes || '',
          clientsCount > 0 ? `${clientsCount} عميل` : '-'
        ]);
      });
      
      // إضافة ملخص المعاملات
      exportData.push(['', '']);
      exportData.push(['ملخص المعاملات', '']);
      exportData.push(['إجمالي عدد المعاملات', filteredTransactions.length]);
      exportData.push(['إجمالي الإيداعات', `${totalDeposits.toFixed(2)} ج.م`]);
      exportData.push(['إجمالي المسحوبات', `${totalWithdrawals.toFixed(2)} ج.م`]);
      exportData.push(['صافي العمليات', `${(totalDeposits - totalWithdrawals).toFixed(2)} ج.م`]);
    }
    
    // إضافة تذييل
    exportData.push(['', '']);
    exportData.push(['تم إنشاء هذا التقرير بواسطة نظام إدارة حسابات المندوبين', '']);
    exportData.push([`تاريخ التصدير: ${new Date().toLocaleDateString('ar-EG')} ${new Date().toLocaleTimeString('ar-EG')}`, '']);
    
    // تحويل البيانات إلى تنسيق CSV
    let csvContent = "data:text/csv;charset=utf-8,\uFEFF"; // إضافة BOM لدعم العربية
    
    exportData.forEach(row => {
      // معالجة الفواصل في البيانات
      const processedRow = row.map(cell => {
        // تحويل القيم إلى نصوص
        const cellStr = String(cell || '');
        // إذا احتوى النص على فاصلة أو اقتباس أو سطر جديد، نحيطه باقتباس
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          // استبدال علامات الاقتباس المزدوجة بعلامتي اقتباس متتاليتين
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      });
      
      const csvRow = processedRow.join(',');
      csvContent += csvRow + "\r\n";
    });
    
    // إنشاء رابط تنزيل
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    
    // تحديد اسم الملف بناءً على التبويب النشط
    const tabName = activeTab === 'clients' ? 'العملاء' : 'المعاملات';
    link.setAttribute("download", `تقرير_${tabName}_${selectedDelegate.DelegateName}_${new Date().toLocaleDateString('ar-EG').replace(/\//g, '-')}.csv`);
    
    document.body.appendChild(link);
    
    // تنزيل الملف
    link.click();
    document.body.removeChild(link);
    
    toast.success('تم تصدير البيانات بنجاح');
  } catch (error) {
    console.error('خطأ في تصدير البيانات:', error);
    toast.error('حدث خطأ أثناء تصدير البيانات');
  }
};
// طباعة التقرير بشكل مبهر
const handlePrint = () => {
 const printWindow = window.open('', '_blank');
 const dataToExport = getExportData();
 
 const htmlContent = `
   <!DOCTYPE html>
   <html dir="rtl" lang="ar">
   <head>
     <meta charset="UTF-8">
     <title>تقرير المندوب: ${selectedDelegate?.DelegateName}</title>
     <style>
       @page {
         size: A4;
         margin: 8mm 5mm;
       }
       
       * { 
         margin: 0; 
         padding: 0; 
         box-sizing: border-box; 
       }
       
       body { 
         font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
         direction: rtl;
         background: white !important;
         color: black !important;
         font-size: 16px;
         line-height: 1.4;
         padding: 20px;
         -webkit-print-color-adjust: exact !important;
         print-color-adjust: exact !important;
       }
       
       .header {
         text-align: center;
         margin-bottom: 30px;
         border-bottom: 3px solid black !important;
         padding-bottom: 20px;
       }
       
       .delegate-name { 
         font-size: 32px;
         font-weight: bold;
         color: black !important;
         margin-bottom: 10px;
       }
       
       .report-title { 
         font-size: 24px;
         color: black !important;
         margin-bottom: 10px;
       }
       
       .report-date { 
         color: black !important;
         font-size: 18px;
       }
       
       .summary-section {
         background: white !important;
         border: 2px solid black !important;
         border-radius: 8px;
         padding: 20px;
         margin-bottom: 30px;
       }
       
       .summary-title {
         font-size: 24px;
         font-weight: bold;
         color: black !important;
         margin-bottom: 15px;
         text-align: center;
         border-bottom: 2px solid black !important;
         padding-bottom: 8px;
       }
       
       .summary-grid {
         display: grid;
         grid-template-columns: repeat(3, 1fr);
         gap: 20px;
         text-align: center;
       }
       
       .summary-item {
         background: white !important;
         padding: 15px;
         border-radius: 6px;
         border: 2px solid black !important;
       }
       
       .summary-label {
         font-weight: bold;
         color: black !important;
         margin-bottom: 5px;
         font-size: 16px;
       }
       
       .summary-value {
         font-size: 22px;
         font-weight: bold;
         color: black !important;
       }
       
       .table-container {
         margin-top: 20px;
         overflow-x: auto;
       }
       
       table { 
         width: 100%; 
         border-collapse: collapse;
         background: white !important;
         border: 3px solid black !important;
       }
       
       th, td { 
         padding: 10px 6px;
         text-align: center;
         border: 2px solid black !important;
         font-size: 14px;
         color: black !important;
         background: white !important;
       }
       
       th { 
         background: white !important;
         font-weight: bold;
         color: black !important;
         font-size: 16px;
         border: 3px solid black !important;
       }
       
       tr {
         background: white !important;
       }
       
       tr:nth-child(even) { 
         background: white !important;
       }
       
       tr:nth-child(odd) { 
         background: white !important;
       }
       
       .footer {
         margin-top: 30px;
         text-align: center;
         color: black !important;
         font-size: 14px;
         border-top: 2px solid black !important;
         padding-top: 15px;
       }
       
       @media print {
         * {
           background: white !important;
           color: black !important;
           -webkit-print-color-adjust: exact !important;
           print-color-adjust: exact !important;
         }
         
         body { 
           padding: 10px;
           font-size: 14px;
           background: white !important;
           color: black !important;
         }
         
         .delegate-name {
           font-size: 28px;
           color: black !important;
         }
         
         .report-title {
           font-size: 22px;
           color: black !important;
         }
         
         .report-date {
           font-size: 16px;
           color: black !important;
         }
         
         .summary-title {
           font-size: 22px;
           color: black !important;
         }
         
         .summary-label {
           font-size: 14px;
           color: black !important;
         }
         
         .summary-value {
           font-size: 20px;
           color: black !important;
         }
         
         th, td { 
           padding: 8px 4px; 
           font-size: 12px;
           color: black !important;
           background: white !important;
           border: 2px solid black !important;
         }
         
         th {
           font-size: 14px;
           color: black !important;
           background: white !important;
           border: 3px solid black !important;
         }
         
         table {
           page-break-inside: avoid;
           background: white !important;
           border: 3px solid black !important;
         }
         
         tr {
           page-break-inside: avoid;
           page-break-after: auto;
           background: white !important;
         }
         
         .summary-section {
           background: white !important;
           border: 2px solid black !important;
           margin-bottom: 20px;
         }
         
         .summary-item {
           background: white !important;
           border: 2px solid black !important;
         }
         
         .header { 
           margin-bottom: 20px; 
         }
         
         @page {
           margin: 8mm 3mm;
         }
       }
     </style>
   </head>
   <body>
     <div class="header">
       <div class="delegate-name">تقرير المندوب: ${selectedDelegate?.DelegateName}</div>
       <div class="report-title">تقرير ${exportOptions.dataType === 'clients' ? 'العملاء' : 'المعاملات'}</div>
       <div class="report-date">
         تاريخ التقرير: ${new Date().toLocaleDateString('ar-EG', {
           year: 'numeric',
           month: 'long',
           day: 'numeric'
         })} | 
        عدد ${exportOptions.dataType === 'clients' ? 'العملاء' : 'المعاملات'}: ${dataToExport.length} | إجمالي المسحوبات: ${formatMoneyValue(delegateInfo?.TotalWithdrawals || 0)}
       </div>
     </div>

     ${exportOptions.includeSummary ? `
     <div class="summary-section">
       <div class="summary-title">الملخص</div>
       <div class="summary-grid">
         <div class="summary-item">
           <div class="summary-label">إجمالي العملاء</div>
           <div class="summary-value">${stats.totalClients}</div>
         </div>
         <div class="summary-item">
           <div class="summary-label">العملاء المسافرين</div>
           <div class="summary-value">${stats.traveledClients}</div>
         </div>
         <div class="summary-item">
           <div class="summary-label">العمولات المدفوعة</div>
           <div class="summary-value">${stats.paidCommission}</div>
         </div>
       </div>
     </div>
     ` : ''}

     <div class="table-container">
       <table>
         <thead>
           ${exportOptions.dataType === 'clients' ? `
             <tr>
               <th>الاسم</th>
               <th>الشركة</th>
               <th>رقم الجواز</th>
               <th>مدة السفر</th>
               <th>تاريخ السفر</th>
               <th>حالة العمولة</th>
             </tr>
           ` : `
             <tr>
               <th>التاريخ</th>
               <th>النوع</th>
               <th>المبلغ</th>
               <th>الرصيد</th>
               <th>الملاحظات</th>
             </tr>
           `}
         </thead>
         <tbody>
           ${exportOptions.dataType === 'clients' 
             ? dataToExport.map(client => `
                 <tr>
                   <td>${client.NameArabic}</td>
                   <td>${client.CompanyName || '-'}</td>
                   <td>${client.PassportNumber || '-'}</td>
                   <td>${client.TravelDuration || '-'}</td>
                   <td>${client.TravelDate ? formatDate(client.TravelDate) : '-'}</td>
                   <td>${client.commissionStatus || '-'}</td>
                 </tr>
               `).join('')
             : dataToExport.map(transaction => `
                 <tr>
                   <td>${formatDate(transaction.Date)}</td>
                   <td>${transaction.TransactionType}</td>
                   <td>${transaction.Amount} ج.م</td>
                   <td>${transaction.Balance} ج.م</td>
                   <td>${transaction.Notes || '-'}</td>
                 </tr>
               `).join('')
           }
         </tbody>
       </table>
     </div>

     <div class="footer">
       تم إنشاء هذا التقرير بواسطة نظام إدارة حسابات المندوبين | 
       ${new Date().toLocaleString('ar-EG')}
     </div>
   </body>
   </html>
 `;
 
 printWindow.document.write(htmlContent);
 printWindow.document.close();
 
 printWindow.onload = () => {
   printWindow.focus();
   printWindow.print();
   printWindow.close();
 };
};

useEffect(() => {
  // تحميل البيانات الثابتة مرة واحدة فقط عند فتح الصفحة.
  fetchDelegates();
  fetchCompanies();
  fetchCommissionSettings();
  fetchGlobalSettings();
  fetchCategories();
}, []);

useEffect(() => {
  // تحديث بيانات المندوب الحالي فقط بدون إعادة تحميل كل بيانات الصفحة.
  const selectedDelegateId = selectedDelegate?.DelegateID;
  if (!selectedDelegateId) return undefined;

  const interval = setInterval(() => {
    Promise.all([
      fetchDelegateClients(selectedDelegateId),
      fetchDelegateInfo(selectedDelegateId),
      fetchDelegateTransactions(selectedDelegateId)
    ]).catch(error => {
      console.error('خطأ في التحديث التلقائي:', error);
    });
  }, 60000);

  return () => clearInterval(interval);
}, [selectedDelegate?.DelegateID]);


  // استماع للتغييرات في إعدادات العمولات
  useEffect(() => {
    if (settings.length > 0) {
      updateCommissionValues();
    }
  }, [settings, globalSettings]);
  
  // إضافة useEffect جديد للفلترة الافتراضية
  useEffect(() => {
    if (clients.length > 0) {
      // تطبيق الفلتر الافتراضي (المسافرين غير المدفوعة عمولتهم)
      let defaultFiltered = clients.filter(client => 
        client.TravelDate && 
        (client.commissionStatus === 'غير مدفوعة' || !client.commissionStatus)
      );
      
      setFilteredClients(defaultFiltered);
      
      // حساب الإحصائيات المحسنة
      const totalClients = clients.length;
      const traveledClients = clients.filter(client => client.TravelDate).length;
      const paidCommission = clients.filter(client => client.commissionStatus === 'مدفوعة').length;
      const unpaidCommission = clients.filter(client => 
        client.TravelDate && (client.commissionStatus === 'غير مدفوعة' || !client.commissionStatus)
      ).length;
      const totalCommission = clients.reduce((sum, client) => sum + (client.totalPaidCommission || 0), 0);
  
      setStats({
        totalClients,
        traveledClients,
        paidCommission,
        unpaidCommission,
        totalCommission
      });
    }
  }, [clients]); // هذا useEffect جديد تماماً، أضفه بعد اللي موجود

// تحميل طرق الدفع والتصنيفات عند فتح نافذة دفع العمولة من نفس المسار الموحد
useEffect(() => {
  if (showCommissionForm) {
    setSelectedCategoryId(null);
    setSelectedPaymentMethod(null);
    setFilteredPaymentMethods([]);

    fetchCategories();
    fetchPaymentMethods();
  }
}, [showCommissionForm]);



 useEffect(() => {
  if (showDebtsModal && selectedDelegate) {
    fetchDebts();
    fetchDelegateTransactions(selectedDelegate.DelegateID);
    fetchDebtOperationPaymentMethods(debtOperationForm.categoryId);
  }
}, [showDebtsModal, selectedDelegate]);



  // تنسيق التاريخ
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('ar-EG');
    } catch (e) {
      return '-';
    }
  };

  const getDebtOperationTransactions = () => {
    if (!Array.isArray(delegateTransactions)) return [];
    return delegateTransactions.filter(tx => {
      const related = parseTransactionRelatedData(tx);
      const opType = related.operationType || related.financialOperationType || '';
      return ['advance', 'repay_advance', 'manual_debt'].includes(opType)
        || related.createdFrom === 'delegate_financial_operation'
        || related.createdFrom === 'manual_debt_without_treasury'
        || String(tx.TransactionType || '').includes('سلفة')
        || String(tx.TransactionType || '').includes('مديونية');
    });
  };

  const getDebtOperationInfo = (tx) => {
    const related = parseTransactionRelatedData(tx);
    const details = Array.isArray(related.operationDetails) && related.operationDetails.length > 0
      ? related.operationDetails
      : (Array.isArray(related.debtDetails) && related.debtDetails.length > 0
        ? related.debtDetails
        : (Array.isArray(related.paymentDetails) ? related.paymentDetails : []));
    const opType = related.operationType || related.financialOperationType || '';
    const amount = toSafeNumber(related.actualPayment ?? related.amount ?? tx.Amount);
    const remaining = details.reduce((sum, item) => sum + toSafeNumber(item.afterRemainingAmount ?? item.remainingAmount), 0);
    return {
      related,
      details,
      opType,
      label: related.operationLabel || tx.TransactionType || 'عملية سلفة/مديونية',
      treasuryEffect: related.treasuryEffect || (related.doesAffectTreasury === false ? 'بدون خزينة' : (related.bankDirection === 'out' ? 'سحب من الحساب' : 'إيداع في الحساب')),
      amount,
      remaining,
      paymentMethodName: related.paymentMethod?.name || related.paymentMethod?.Name || '-'
    };
  };

  const startEditDebtOperationTransaction = async (tx) => {
    const related = parseTransactionRelatedData(tx);
    const opType = related.operationType || related.financialOperationType || '';

    if (opType === 'manual_debt' || related.createdFrom === 'manual_debt_without_treasury' || related.doesAffectTreasury === false) {
      // المديونية اليدوية لا يوجد لها حركة خزينة، لذلك لا نطلب طريقة دفع عند التعديل
      setEditingTransaction(tx);
      setEditTransactionForm({
        type: tx.TransactionType,
        amount: String(tx.Amount ?? ''),
        notes: tx.Notes || '',
        date: new Date(tx.Date || new Date()).toISOString().split('T')[0],
        paymentMethodId: '',
        clientDetails: {
          name: `المندوب: ${selectedDelegate?.DelegateName || ''}`,
          nationalId: `معاملة رقم: ${tx.TransactionID}`,
          companyName: 'إدارة السلف والمديونيات',
          delegateName: selectedDelegate?.DelegateName || '',
          phoneNumbers: selectedDelegate?.PhoneNumber || ''
        }
      });
      setShowEditTransactionModal(true);
      return;
    }

    await startEditTransaction(tx);
  };

  const deleteDebtOperationTransaction = async (tx) => {
    const info = getDebtOperationInfo(tx);
    const message = `هل أنت متأكد من حذف هذه العملية؟\n\nرقم المعاملة: #${tx.TransactionID}\nالنوع: ${info.label}\nالمبلغ: ${formatMoneyValue(info.amount)}\n\nسيتم عكس أثرها تلقائياً من إدارة السلف/المديونيات والخزينة إن كانت مرتبطة بالخزينة.`;
    if (!window.confirm(message)) return;
    await handleDeleteTransaction(tx.TransactionID);
  };

  const editTransactionRelatedData = editingTransaction ? parseTransactionRelatedData(editingTransaction) : {};
  const editTransactionOperationType = editTransactionRelatedData.operationType || editTransactionRelatedData.financialOperationType || '';
  const editTransactionAffectsTreasury = !!editingTransaction && !(
    editTransactionRelatedData.doesAffectTreasury === false ||
    editTransactionOperationType === 'manual_debt' ||
    editTransactionRelatedData.createdFrom === 'manual_debt_without_treasury'
  );

  const renderLoading = () => (
    <div className="flex justify-center items-center p-8 w-full">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      <span className="mr-4 text-gray-600">جاري تحميل البيانات بسرعة...</span>
    </div>
  );
  
  const renderEmpty = (message = 'لا توجد بيانات متاحة') => (
    <div className="bg-white rounded-lg shadow-sm p-10 text-center">
      <div className="flex flex-col items-center justify-center">
        <div className="text-gray-400 rounded-full bg-gray-100 p-3 mb-4">
          <Clipboard size={30} />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{message}</h3>
        <p className="text-sm text-gray-500">لا توجد بيانات لعرضها حالياً</p>
      </div>
    </div>
  );

  const renderError = (message = 'حدث خطأ أثناء تحميل البيانات') => (
    <div className="bg-white rounded-lg shadow-sm p-10 text-center">
      <div className="flex flex-col items-center justify-center">
        <div className="text-red-400 rounded-full bg-red-50 p-3 mb-4">
          <AlertCircle size={30} />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{message}</h3>
        <p className="text-sm text-gray-500 mb-4">تعذر تحميل البيانات، يرجى المحاولة مرة أخرى</p>
        <button 
          onClick={handleRefresh}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm flex items-center"
        >
          <RefreshCw size={16} className="ml-2" />
          إعادة المحاولة
        </button>
      </div>
    </div>
  );

  const renderClientsList = () => {
    if (loading) return renderLoading();
    if (filteredClients.length === 0) return renderEmpty('لا يوجد عملاء متاحين');

    return (
      <div>
        <div className="md:hidden space-y-3">
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-3 flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm font-semibold text-blue-800">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAllClients}
                className="h-5 w-5 text-blue-600 border-blue-300 rounded"
              />
              تحديد كل المتاح
            </label>
            <span className="text-xs bg-white text-blue-700 px-3 py-1 rounded-full">{selectedClients.length} محدد</span>
          </div>
          {filteredClients.map((client, index) => {
            const commissionDue = calculateCommission(client);
            let isOldClient = false;
            try {
              if (client.PreviousCompanies) {
                const prevCompanies = typeof client.PreviousCompanies === 'string' ? JSON.parse(client.PreviousCompanies) : client.PreviousCompanies;
                if (Array.isArray(prevCompanies)) {
                  isOldClient = prevCompanies.some(id => String(id) === String(client.CompanyID));
                }
              }
            } catch (e) { isOldClient = false; }

            const canSelectClient = client.TravelDate && client.commissionStatus !== 'مدفوعة';

            return (
              <div key={client.PassportID} className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="h-1.5 bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-500" />
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">{index + 1}</span>
                        {client.TravelDate ? (
                          client.commissionStatus === 'مدفوعة' ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">مدفوعة</span>
                          ) : (
                            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700">غير مدفوعة</span>
                          )
                        ) : (
                          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-600">لم يسافر</span>
                        )}
                      </div>
                      <h3 className="font-bold text-gray-900 text-lg leading-7 break-words">{client.NameArabic}</h3>
                      <p className="text-sm text-gray-500 mt-1">جواز: {client.PassportNumber || '-'}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedClients.includes(client.PassportID)}
                      onChange={() => handleSelectClient(client.PassportID)}
                      disabled={!canSelectClient}
                      className={`h-6 w-6 text-blue-600 border-gray-300 rounded-lg flex-shrink-0 ${!canSelectClient ? 'opacity-30' : ''}`}
                    />
                  </div>

                  {client.commissionHistory?.isPaidByOtherDelegate && (
                    <div className="mb-3 rounded-2xl bg-yellow-50 border border-yellow-200 p-3 text-xs text-yellow-800">
                      عمولة مدفوعة عند: <b>{client.commissionHistory.delegateName}</b>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-gray-50 rounded-2xl p-3">
                      <p className="text-xs text-gray-400 mb-1">الشركة</p>
                      <p className="text-sm font-bold text-gray-800 break-words">{client.CompanyName || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-2xl p-3">
                      <p className="text-xs text-gray-400 mb-1">مدة السفر</p>
                      <p className="text-sm font-bold text-gray-800">{client.TravelDuration || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-2xl p-3">
                      <p className="text-xs text-gray-400 mb-1">تاريخ السفر</p>
                      <p className="text-sm font-bold text-gray-800">{client.TravelDate ? formatDate(client.TravelDate) : '-'}</p>
                    </div>
                    <div className="bg-indigo-50 rounded-2xl p-3">
                      <p className="text-xs text-indigo-500 mb-1">العمولة</p>
                      <p className="text-sm font-bold text-indigo-700">{commissionDue ? `${commissionDue} ج.م` : '-'}</p>
                    </div>
                  </div>

                  {isOldClient && client.CompanyName && (
                    <div className="mb-3 text-xs bg-yellow-50 text-yellow-800 border border-yellow-100 rounded-xl p-2">
                      قديم شركات: {client.CompanyName}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => showClientDetails(client)}
                      className="flex items-center justify-center gap-1 bg-blue-50 text-blue-700 rounded-2xl py-2.5 text-sm font-semibold"
                    >
                      <Eye size={16} /> التفاصيل
                    </button>
                    {client.commissions && client.commissions.length > 0 && (
                      <button
                        onClick={() => showClientCommissionHistory(client)}
                        className="flex items-center justify-center gap-1 bg-purple-50 text-purple-700 rounded-2xl py-2.5 text-sm font-semibold"
                      >
                        <FileText size={16} /> سجل العمولات
                      </button>
                    )}
                    {client.commissionHistory?.isPaidByOtherDelegate && (
                      <button
                        onClick={() => handleViewCommissionTransactions(client.PassportID)}
                        className="flex items-center justify-center gap-1 bg-green-50 text-green-700 rounded-2xl py-2.5 text-sm font-semibold"
                      >
                        <Activity size={16} /> سجل الدفع
                      </button>
                    )}
                    {client.commissionHistory?.isPaidByOtherDelegate && (
                      <button
                        onClick={() => handleReassignCommission(client)}
                        className="flex items-center justify-center gap-1 bg-red-50 text-red-700 rounded-2xl py-2.5 text-sm font-semibold"
                      >
                        <RefreshCw size={16} /> إعادة تخصيص
                      </button>
                    )}
                    {client.TravelDate && client.commissionStatus === 'غير مدفوعة' && canDo('دفع عمولة') && (
                      <button
                        onClick={() => {
                          setSelectedClients([client.PassportID]);
                          setShowCommissionForm(true);
                        }}
                        className="col-span-2 flex items-center justify-center gap-1 bg-green-600 text-white rounded-2xl py-3 text-sm font-bold"
                      >
                        <DollarSign size={16} /> دفع عمولة هذا العميل
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-center">
                <div className="flex justify-center">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAllClients}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                </div>
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">#</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">اسم العميل</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الشركة</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">قديم شركات</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">رقم الجواز</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">مدة السفر</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">تاريخ السفر</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">العمولة المستحقة</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">حالة العمولة</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">الإجراءات</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredClients.map((client, index) => {
              // حساب العمولة المستحقة
              const commissionDue = calculateCommission(client);
              
              // تحديد هل هو قديم شركات
              let isOldClient = false;
              try {
                if (client.PreviousCompanies) {
                  let prevCompanies;
                  if (typeof client.PreviousCompanies === 'string') {
                    prevCompanies = JSON.parse(client.PreviousCompanies);
                  } else {
                    prevCompanies = client.PreviousCompanies;
                  }
                  
                  if (Array.isArray(prevCompanies)) {
                    isOldClient = prevCompanies.some(id => String(id) === String(client.CompanyID));
                  }
                }
              } catch (e) {
                isOldClient = false;
              }
              
              return (
                <tr key={client.PassportID} className="hover:bg-gray-50">
                  <td className="px-3 py-4 text-center">
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.PassportID)}
                        onChange={() => handleSelectClient(client.PassportID)}
                        disabled={!client.TravelDate || client.commissionStatus === 'مدفوعة'}
                        className={`h-4 w-4 text-blue-600 border-gray-300 rounded ${!client.TravelDate || client.commissionStatus === 'مدفوعة' ? 'opacity-30' : ''}`}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {index + 1}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {client.NameArabic}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {client.CompanyName || '-'}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
  <div className="text-sm text-gray-900">
    {isOldClient && client.CompanyName ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        {client.CompanyName}
      </span>
    ) : '-'}
  </div>
</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {client.PassportNumber || '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {client.TravelDuration || '-'}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {client.TravelDate ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {new Date(client.TravelDate).toLocaleDateString('ar-EG')}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium">
                      {commissionDue ? (
                        <span className="text-indigo-600">{commissionDue} ج.م</span>
                      ) : '-'}
                    </div>
                  </td>


<td className="px-4 py-4 whitespace-nowrap">
  {client.TravelDate ? (
    client.commissionStatus === 'مدفوعة' ? (
      <div>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Check size={12} className="ml-1" />
          مدفوعة
        </span>
        {/* التحقق من المندوب الأصلي - الكود ده صحيح */}
        {client.commissionHistory?.isPaidByOtherDelegate && (
          <div className="mt-1">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800 cursor-pointer" 
                  title={`مدفوعة في ${new Date(client.commissionHistory.paidDate).toLocaleDateString('ar-EG')} بمبلغ ${client.commissionHistory.amount} ج.م`}>
              <AlertCircle size={10} className="ml-1" />
              عند: {client.commissionHistory.delegateName}
            </span>
          </div>
        )}
      </div>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
        <X size={12} className="ml-1" />
        غير مدفوعة
      </span>
    )
  ) : (
    <span className="text-sm text-gray-500">-</span>
  )}
</td>
<td className="px-4 py-4 whitespace-nowrap text-center">
  <div className="flex items-center justify-center space-x-2 gap-1">
    <button
      onClick={() => showClientDetails(client)}
      className="text-blue-600 hover:text-blue-800 p-1.5 hover:bg-blue-50 rounded-full transition duration-200"
      title="عرض التفاصيل"
    >
      <Eye size={18} />
    </button>
    
    {/* أزرار العمولة المتضاربة */}
    {client.commissionHistory?.isPaidByOtherDelegate && (
      <>
        <button
          onClick={() => handleViewCommissionTransactions(client.PassportID)}
          className="text-green-600 hover:text-green-800 p-1.5 hover:bg-green-50 rounded-full transition duration-200"
          title="سجل معاملات العمولة"
        >
          <Activity size={18} />
        </button>
        <button
          onClick={() => handleReassignCommission(client)}
          className="text-red-600 hover:text-red-800 p-1.5 hover:bg-red-50 rounded-full transition duration-200"
          title="إعادة تخصيص العمولة"
        >
          <RefreshCw size={18} />
        </button>
      </>
    )}
                          {client.commissions && client.commissions.length > 0 && (
                            <button
                              onClick={() => showClientCommissionHistory(client)}
                              className="text-purple-600 hover:text-purple-800 p-1.5 hover:bg-purple-50 rounded-full transition duration-200"
                              title="سجل العمولات"
                            >
                              <FileText size={18} />
                            </button>
                          )}
                          {client.TravelDate && client.commissionStatus === 'غير مدفوعة' && canDo('دفع عمولة') && (
                            <button
                              onClick={() => {
                                setSelectedClients([client.PassportID]);
                                setShowCommissionForm(true);
                              }}
                              className="text-green-600 hover:text-green-800 p-1.5 hover:bg-green-50 rounded-full transition duration-200"
                              title="دفع عمولة"
                            >
                              <Plus size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
      </div>
        );
      };

  const renderCardView = () => {
    if (loading) return renderLoading();
    if (filteredClients.length === 0) return renderEmpty('لا يوجد عملاء متاحين');

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-md:space-y-0">
        {filteredClients.map(client => {
          const commissionDue = calculateCommission(client);
          
          return (
            <div key={client.PassportID} className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow max-md:rounded-3xl">
              <div className="border-b border-gray-100 bg-gray-50 px-4 py-3 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="bg-blue-100 p-2 rounded-full">
                    <User size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">{client.NameArabic}</h3>
                    <p className="text-xs text-gray-500">{client.CompanyName || 'غير محدد'}</p>
                  </div>
                </div>
                
                <div className="flex gap-1">
                  <input
                    type="checkbox"
                    checked={selectedClients.includes(client.PassportID)}
                    onChange={() => handleSelectClient(client.PassportID)}
                    disabled={!client.TravelDate || client.commissionStatus === 'مدفوعة'}
                    className={`h-4 w-4 text-blue-600 border-gray-300 rounded ${!client.TravelDate || client.commissionStatus === 'مدفوعة' ? 'opacity-30' : ''}`}
                  />
                </div>
              </div>
              
              <div className="p-4">
                <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                  <div className="bg-gray-50 p-2 rounded">
                    <p className="text-gray-500 mb-1">رقم الجواز</p>
                    <p className="font-medium">{client.PassportNumber || '-'}</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <p className="text-gray-500 mb-1">تاريخ السفر</p>
                    <p className="font-medium">{client.TravelDate ? formatDate(client.TravelDate) : '-'}</p>
                  </div>
                </div>
                
                <div className="flex justify-between items-center mb-3 bg-indigo-50 p-2 rounded">
                  <p className="text-xs text-gray-700">العمولة المستحقة:</p>
                  <p className="font-medium text-indigo-700 text-sm">{commissionDue ? `${commissionDue} ج.م` : '-'}</p>
                </div>
                
                <div className="flex justify-between items-center max-md:flex-col max-md:items-stretch max-md:gap-3">
                  <div>
                    {client.TravelDate ? (
                      client.commissionStatus === 'مدفوعة' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <Check size={12} className="ml-1" />
                          مدفوعة
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          <X size={12} className="ml-1" />
                          غير مدفوعة
                        </span>
                      )
                    ) : (
                      <span className="text-xs text-gray-500">لم يسافر بعد</span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => showClientDetails(client)}
                      className="text-blue-600 hover:text-blue-800 p-1.5 hover:bg-blue-50 rounded-full transition duration-200"
                      title="عرض التفاصيل"
                    >
                      <Eye size={16} />
                    </button>
                    {client.commissions && client.commissions.length > 0 && (
                      <button
                        onClick={() => showClientCommissionHistory(client)}
                        className="text-purple-600 hover:text-purple-800 p-1.5 hover:bg-purple-50 rounded-full transition duration-200"
                        title="سجل العمولات"
                      >
                        <FileText size={16} />
                      </button>
                    )}
                    {client.TravelDate && client.commissionStatus === 'غير مدفوعة' && canDo('دفع عمولة') && (
                      <button
                        onClick={() => {
                          setSelectedClients([client.PassportID]);
                          setShowCommissionForm(true);
                        }}
                        className="text-green-600 hover:text-green-800 p-1.5 hover:bg-green-50 rounded-full transition duration-200"
                        title="دفع عمولة"
                      >
                        <Plus size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTransactionsList = () => {
    if (loading) return renderLoading();
    
    // معالجة وجود دالة فلترة دائمًا، بغض النظر عن النتائج
    const filteredTransactions = applyTransactionDateFilter();
    
    return (
      <div>
        {/* قسم فلاتر المعاملات - يظهر دائمًا */}
        <div className="mb-4 flex justify-between items-center max-md:flex-col max-md:items-stretch max-md:gap-2">
          <div className="flex gap-2 max-md:grid max-md:grid-cols-2">
            <button
              onClick={() => setShowTransactionFilters(!showTransactionFilters)}
              className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded transition-colors"
            >
              <Filter size={16} />
              <span>{showTransactionFilters ? 'إخفاء الفلاتر' : 'فلترة حسب التاريخ'}</span>
            </button>
            
            {/* زر حذف المعاملات المحددة */}
            {selectedTransactions.length > 0 && canDo('حذف معاملة') && (
              <button
                onClick={handleDeleteSelectedTransactions}
                className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded transition-colors"
              >
                <Trash2 size={16} />
                <span>حذف المحدد ({selectedTransactions.length})</span>
              </button>
            )}
          </div>
          
          <div className="text-sm text-gray-500 max-md:bg-gray-50 max-md:rounded-xl max-md:px-3 max-md:py-2">
            إجمالي المعاملات: {filteredTransactions.length} من {delegateTransactions.length}
          </div>
        </div>
        
        {/* حقول فلترة التاريخ */}
        {showTransactionFilters && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">من تاريخ</label>
                <div className="relative">
                  <input
                    type="date"
                    name="from"
                    value={transactionDateFilter.from}
                    onChange={handleTransactionDateFilterChange}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm max-md:rounded-xl max-md:py-3"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">إلى تاريخ</label>
                <div className="relative">
                  <input
                    type="date"
                    name="to"
                    value={transactionDateFilter.to}
                    onChange={handleTransactionDateFilterChange}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm max-md:rounded-xl max-md:py-3"
                  />
                </div>
              </div>
            </div>
            <div className="mt-2 flex justify-end">
              <button
                onClick={() => setTransactionDateFilter({ from: '', to: '' })}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                إعادة ضبط الفلاتر
              </button>
            </div>
          </div>
        )}
  
        {/* عرض رسالة عندما لا توجد نتائج للفلتر، مع إبقاء ظهور فلاتر التاريخ */}
        {filteredTransactions.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-10 text-center">
            <div className="flex flex-col items-center justify-center">
              <div className="text-gray-400 rounded-full bg-gray-100 p-3 mb-4">
                <Clipboard size={30} />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد معاملات تطابق معايير البحث</h3>
              <p className="text-sm text-gray-500 mb-3">جرب تغيير معايير البحث أو إعادة ضبط الفلاتر</p>
              {(transactionDateFilter.from || transactionDateFilter.to) && (
                <button
                  onClick={() => setTransactionDateFilter({ from: '', to: '' })}
                  className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  إعادة ضبط فلاتر التاريخ
                </button>
              )}
            </div>
          </div>
        ) : (
          // جدول المعاملات - يظهر فقط عندما توجد نتائج
          <>
            <div className="md:hidden space-y-3">
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-3 flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-semibold text-blue-800">
                  <input
                    type="checkbox"
                    checked={allTransactionsSelected}
                    onChange={handleSelectAllTransactions}
                    className="h-5 w-5 text-blue-600 border-blue-300 rounded"
                  />
                  تحديد كل المعاملات
                </label>
                <span className="text-xs bg-white text-blue-700 px-3 py-1 rounded-full">{selectedTransactions.length} محدد</span>
              </div>
              {filteredTransactions.map((transaction, index) => {
                let relatedClientsData = null;
                let clientsCount = 0;
                try {
                  if (transaction.RelatedClients) {
                    relatedClientsData = typeof transaction.RelatedClients === 'string' ? JSON.parse(transaction.RelatedClients) : transaction.RelatedClients;
                    if (relatedClientsData?.clients && Array.isArray(relatedClientsData.clients)) clientsCount = relatedClientsData.clients.length;
                  }
                } catch (e) {
                  console.error("خطأ في تحليل بيانات العملاء المرتبطين:", e);
                }
                const txType = String(transaction.TransactionType || '');
                let paymentName = '-';
                try {
                  if (relatedClientsData?.paymentMethod?.name) paymentName = relatedClientsData.paymentMethod.name;
                } catch (e) {}

                return (
                  <div key={transaction.TransactionID} className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className={`h-1.5 ${txType === 'إيداع' ? 'bg-green-500' : txType === 'سحب' ? 'bg-red-500' : 'bg-blue-500'}`} />
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-700 text-xs font-bold">{index + 1}</span>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${txType === 'إيداع' ? 'bg-green-100 text-green-700' : txType === 'سحب' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                              {txType || 'معاملة'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">{formatDate(transaction.Date)}</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={selectedTransactions.includes(transaction.TransactionID)}
                          onChange={() => handleSelectTransaction(transaction.TransactionID)}
                          className="h-6 w-6 text-blue-600 border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <div className="bg-indigo-50 rounded-2xl p-3">
                          <p className="text-xs text-indigo-500 mb-1">المبلغ</p>
                          <p className="text-base font-bold text-indigo-700">{transaction.Amount} ج.م</p>
                        </div>
                        <div className="bg-green-50 rounded-2xl p-3">
                          <p className="text-xs text-green-500 mb-1">إجمالي المدفوع</p>
                          <p className="text-base font-bold text-green-700">{transaction.Balance} ج.م</p>
                        </div>
                        <div className="bg-gray-50 rounded-2xl p-3">
                          <p className="text-xs text-gray-400 mb-1">العملاء</p>
                          <p className="text-sm font-bold text-gray-800">{clientsCount > 0 ? `${clientsCount} عميل` : '-'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-2xl p-3">
                          <p className="text-xs text-gray-400 mb-1">طريقة الدفع</p>
                          <p className="text-sm font-bold text-gray-800 break-words">{paymentName}</p>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-2xl p-3 mb-3">
                        <p className="text-xs text-gray-400 mb-1">الملاحظات</p>
                        <p className="text-sm text-gray-800 break-words">{transaction.Notes || '-'}</p>
                      </div>

                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                        <button
                          onClick={() => showTransactionDetails(transaction)}
                          className="flex items-center justify-center gap-1 bg-blue-50 text-blue-700 rounded-2xl py-2.5 text-sm font-semibold"
                        >
                          <Eye size={16} /> التفاصيل
                        </button>
                        <button
                          onClick={() => startEditTransaction(transaction)}
                          className="flex items-center justify-center gap-1 bg-green-50 text-green-700 rounded-2xl py-2.5 text-sm font-semibold"
                        >
                          <Edit size={16} /> تعديل
                        </button>
                        {canDo('حذف معاملة') && (
                          <button
                            onClick={() => handleDeleteTransaction(transaction.TransactionID)}
                            className="flex items-center justify-center gap-1 bg-red-50 text-red-700 rounded-2xl py-2.5 text-sm font-semibold col-span-2"
                          >
                            <Trash2 size={16} /> حذف
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-center">
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={allTransactionsSelected}
                        onChange={handleSelectAllTransactions}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">#</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">التاريخ</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">نوع المعاملة</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">المبلغ</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">إجمالي العمولات المدفوعة</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">ملاحظات</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">العملاء المرتبطين</th>
<th className="px-4 py-3 text-right text-xs font-medium text-gray-500">طريقة الدفع</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">أضيف بواسطة</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransactions.map((transaction, index) => {
                  // محاولة تحليل بيانات العملاء المرتبطين
                  let relatedClientsData = null;
                  let clientsCount = 0;
                  
                  try {
                    if (transaction.RelatedClients) {
                      if (typeof transaction.RelatedClients === 'string') {
                        relatedClientsData = JSON.parse(transaction.RelatedClients);
                      } else {
                        relatedClientsData = transaction.RelatedClients;
                      }
                      
                      if (relatedClientsData && relatedClientsData.clients && Array.isArray(relatedClientsData.clients)) {
                        clientsCount = relatedClientsData.clients.length;
                      }
                    }
                  } catch (e) {
                    console.error("خطأ في تحليل بيانات العملاء المرتبطين:", e);
                  }
                  
                  return (
                    <tr key={transaction.TransactionID} className="hover:bg-gray-50">
                      <td className="px-3 py-4 text-center">
                        <div className="flex justify-center">
                          <input
                            type="checkbox"
                            checked={selectedTransactions.includes(transaction.TransactionID)}
                            onChange={() => handleSelectTransaction(transaction.TransactionID)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(transaction.Date)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(transaction.Date).toLocaleTimeString('ar-EG')}
                        </div>
                      </td>
                  


                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                        ${transaction.TransactionType === 'إيداع' ? 'bg-green-100 text-green-800' : 
                          transaction.TransactionType === 'سحب' ? 'bg-red-100 text-red-800' : 
                          transaction.TransactionType.includes('خصم كامل') ? 'bg-orange-100 text-orange-800' :
                          transaction.TransactionType.includes('خصم جزئي') ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'}`
                        }
                      >
                        {transaction.TransactionType.includes('خصم') ? (
                          <div className="flex items-center">
                            <AlertCircle size={12} className="ml-1" />
                            {transaction.TransactionType}
                          </div>
                        ) : transaction.TransactionType}
                      </span>
                    </td>

<td className="px-4 py-4 whitespace-nowrap">
  <div className="text-sm font-medium text-gray-900">
    {(() => {
      try {
        if (transaction.RelatedClients) {
          const relatedData = JSON.parse(transaction.RelatedClients);
          if (relatedData.actualPayment !== undefined && relatedData.debtReduction !== undefined) {
            return (
              <div>
                <span className="font-bold text-indigo-600">{transaction.Amount} ج.م</span>
                {relatedData.debtReduction > 0 && (
                  <div className="text-xs text-red-600 mt-1">
                    خُصم من سلف/مديونيات: {relatedData.debtReduction} ج.م
                  </div>
                )}
                {relatedData.actualPayment > 0 && (
                  <div className="text-xs text-green-600">
                    مدفوع فعلياً: {relatedData.actualPayment} ج.م
                  </div>
                )}
                {relatedData.actualPayment === 0 && relatedData.debtReduction > 0 && (
                  <div className="text-xs text-gray-600">
                    لم يُدفع أي مبلغ فعلي
                  </div>
                )}
              </div>
            );
          }
        }
      } catch (e) {
        // للمعاملات القديمة
      }
      return transaction.Amount + " ج.م";
    })()}
  </div>
</td>
                     
<td className="px-4 py-4 whitespace-nowrap">
  <div className="text-sm font-medium text-green-600">
    {transaction.Balance} ج.م
  </div>
  <div className="text-xs text-gray-500">
    إجمالي المدفوع فعلياً للمندوب
  </div>
</td>
                     <td className="px-4 py-4">
  <div className="text-sm text-gray-900 max-w-xs">
    {(() => {
      try {
        if (transaction.RelatedClients) {
          const relatedData = JSON.parse(transaction.RelatedClients);
          if (relatedData.debtDetails && relatedData.debtDetails.length > 0) {
            return (
              <div>
                <div className="font-medium mb-1">
                  عمولة {relatedData.commissionRequested} ج.م
                </div>
                {relatedData.debtDetails.map((detail, index) => (
                  <div key={index} className="text-xs text-gray-600 mb-1">
                    <span className="text-red-600">خُصم {detail.deductedAmount} ج.م</span> من {detail.causeInfo}
                    <br />
                    <span className="text-gray-500">متبقي من هذا الدين: {detail.remainingAmount} ج.م</span>
                  </div>
                ))}
                {relatedData.actualPayment > 0 ? (
                  <div className="text-xs text-green-600 font-medium">
                    💰 مدفوع للمندوب: {relatedData.actualPayment} ج.م
                  </div>
                ) : (
                  <div className="text-xs text-gray-600 font-medium">
                    ❌ لم يُدفع أي مبلغ للمندوب
                  </div>
                )}
              </div>
            );
          }
        }
      } catch (e) {
        // للمعاملات القديمة
      }
      return transaction.Notes || '-';
    })()}
  </div>
</td>
{/* عمود العملاء المرتبطين */}
<td className="px-4 py-4 whitespace-nowrap">
  {clientsCount > 0 ? (
    <div className="text-sm text-gray-900">
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
        {clientsCount} عميل
      </span>
    </div>
  ) : (
    <span className="text-sm text-gray-500">-</span>
  )}
</td>

{/* عمود طريقة الدفع */}
<td className="px-4 py-4 whitespace-nowrap">
  {(() => {
    try {
      if (transaction.RelatedClients) {
        const relatedData = typeof transaction.RelatedClients === 'string' 
          ? JSON.parse(transaction.RelatedClients)
          : transaction.RelatedClients;
        
        if (relatedData.paymentMethod && relatedData.paymentMethod.name) {
          const paymentMethod = relatedData.paymentMethod;
          if (!isRealBankAccountPaymentMethod(paymentMethod)) {
            return (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">
                طريقة دفع قديمة غير مفعلة
              </span>
            );
          }
          return (
            <div className="text-sm text-gray-900">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100">
                🏦 {paymentMethod.name}
              </span>
            </div>
          );
        }
      }
    } catch (e) {
      console.error("خطأ في عرض طريقة الدفع:", e);
    }
    return <span className="text-sm text-gray-500">-</span>;
  })()}
</td>

                      {/* أضيف بواسطة */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <User size={15} className="text-gray-400 shrink-0" />
                          <span className="text-sm text-gray-700 font-medium">
                            {transaction.CreatedByFullName || transaction.CreatedBy || '-'}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2 gap-1">
                          <button
                            onClick={() => showTransactionDetails(transaction)}
                            className="text-blue-600 hover:text-blue-800 p-1.5 hover:bg-blue-50 rounded-full transition duration-200"
                            title="عرض التفاصيل"
                          >
                            <Eye size={18} />
                          </button>

                          <button
                            onClick={() => startEditTransaction(transaction)}
                            className="text-green-600 hover:text-green-800 p-1.5 hover:bg-green-50 rounded-full transition duration-200"
                            title="تعديل المعاملة"
                          >
                            <Edit size={18} />
                          </button>



                          {canDo('حذف معاملة') && (
                          <button
                            onClick={() => handleDeleteTransaction(transaction.TransactionID)}
                            className="text-red-600 hover:text-red-800 p-1.5 hover:bg-red-50 rounded-full transition duration-200"
                            title="حذف المعاملة"
                          >
                            <Trash2 size={18} />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    );
  };

  const renderStats = () => {
    if (loading) return renderLoading();

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* إحصائيات العملاء */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-3 bg-blue-100 rounded-full">
                <User size={20} className="text-blue-600" />
              </div>
              <h4 className="text-lg font-medium text-gray-900">إحصائيات العملاء</h4>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">إجمالي العملاء:</span>
                <span className="font-medium">{stats.totalClients}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">العملاء المسافرين:</span>
                <span className="font-medium">{stats.traveledClients}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">نسبة المسافرين:</span>
                <span className="font-medium">
                  {stats.totalClients > 0 ? Math.round((stats.traveledClients / stats.totalClients) * 100) : 0}%
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">العمولات المدفوعة:</span>
                <span className="font-medium">{stats.paidCommission}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">العمولات غير المدفوعة:</span>
                <span className="font-medium">{stats.unpaidCommission}</span>
              </div>
            </div>
          </div>
          
       
          
          {/* معلومات إضافية */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-3 bg-indigo-100 rounded-full">
                <Info size={20} className="text-indigo-600" />
              </div>
              <h4 className="text-lg font-medium text-gray-900">معلومات إضافية</h4>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">اسم المندوب:</span>
                <span className="font-medium">{selectedDelegate?.DelegateName}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">رقم الهاتف:</span>
                <span className="font-medium">{selectedDelegate?.PhoneNumber || '-'}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">عدد الشركات:</span>
                <span className="font-medium">
                  {new Set(clients.map(client => client.CompanyID).filter(Boolean)).size}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">المعاملات:</span>
                <span className="font-medium">{delegateTransactions.length}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* الشركات الأكثر تعاملاً */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-3 bg-purple-100 rounded-full">
              <Building size={20} className="text-purple-600" />
            </div>
            <h4 className="text-lg font-medium text-gray-900">الشركات الأكثر تعاملاً</h4>
          </div>
          
          <div className="md:hidden text-sm text-gray-500 bg-gray-50 rounded-2xl p-4 mb-3">
            جدول الشركات يظهر كاملًا على الكمبيوتر، وعلى الموبايل استخدم شاشة الإحصائيات المختصرة بالأعلى.
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">#</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">اسم الشركة</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">عدد العملاء</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">المسافرين</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">نسبة المسافرين</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  // حساب إحصائيات الشركات
                  const companyStats = {};
                  
                  clients.forEach(client => {
                    if (client.CompanyID && client.CompanyName) {
                      if (!companyStats[client.CompanyID]) {
                        companyStats[client.CompanyID] = {
                          name: client.CompanyName,
                          total: 0,
                          traveled: 0
                        };
                      }
                      
                      companyStats[client.CompanyID].total++;
                      
                      if (client.TravelDate) {
                        companyStats[client.CompanyID].traveled++;
                      }
                    }
                  });
                  
                  // تحويل البيانات إلى مصفوفة وترتيبها
                  const sortedCompanies = Object.keys(companyStats)
                    .map(companyId => ({
                      id: companyId,
                      ...companyStats[companyId]
                    }))
                    .sort((a, b) => b.total - a.total);
                  
                  return sortedCompanies.slice(0, 5).map((company, index) => (
                    <tr key={company.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {company.name}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {company.total}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {company.traveled}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {company.total > 0 ? Math.round((company.traveled / company.total) * 100) : 0}%
                        </div>
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // المكون الرئيسي للصفحة
  return (
    <div className="min-h-screen bg-gray-50 py-2 max-md:py-0 max-md:overflow-x-hidden" dir="rtl">
  <div className="max-w-full mx-auto px-2 sm:px-3 lg:px-4 max-md:px-0">
        {/* رأس الصفحة */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-3 max-md:rounded-none max-md:p-3 max-md:sticky max-md:top-0 max-md:z-20">

          <div className="flex justify-between items-center max-md:flex-col max-md:items-stretch max-md:gap-3">
            <h1 className="text-2xl font-bold text-gray-900 max-md:text-xl max-md:text-center">حسابات المندوبين</h1>
            <div className="flex gap-2 items-center max-md:grid max-md:grid-cols-3 max-md:w-full">
              {canDo('إعدادات العمولة') && (
              <button 
                onClick={() => setShowCommissionSettings(true)}
                className="flex items-center justify-center gap-1 text-gray-600 bg-gray-100 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors max-md:px-2 max-md:text-xs"
              >
                <Settings size={16} />
                <span>إعدادات العمولات</span>
              </button>
              )}
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors max-md:bg-gray-100 max-md:rounded-lg"
              >
                <RefreshCw 
                  size={20} 
                  className={refreshing ? 'animate-spin' : ''}
                />
              </button>
              <button 
                onClick={() => navigate('/')}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2.5 rounded-lg transition duration-200 max-md:px-2 max-md:text-xs"
              >
                العودة للرئيسية
              </button>
            </div>
          </div>
        </div>

     {/* اختيار المندوب - التصميم المحسّن */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6 max-md:rounded-none max-md:shadow-sm max-md:mb-3 max-md:p-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">اختر المندوب</h2>
            
            {/* حقل البحث */}
            <div className="flex-1 max-w-md max-md:max-w-none max-md:w-full">
              <div className="relative">
                <input
                  type="text"
                  placeholder="ابحث عن مندوب بالاسم أو رقم الهاتف..."
                  value={search}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>
          
          {loading && !selectedDelegate ? renderLoading() : (
            <>
              {/* المندوب المحدد */}
              {selectedDelegate && (
                <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-600 rounded-full">
                        <User size={20} className="text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{selectedDelegate.DelegateName}</h3>
                        <p className="text-sm text-gray-600">{selectedDelegate.PhoneNumber}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedDelegate(null)}
                      className="text-gray-500 hover:text-gray-700 p-1 hover:bg-white rounded-full transition-colors"
                      title="إلغاء الاختيار"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>
              )}
              
              {/* قائمة المناديب المدمجة */}
              {!selectedDelegate && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 max-h-64 overflow-y-auto max-md:max-h-[45vh] max-md:pb-2">
                  {delegates
                    .filter(delegate => 
                      delegate.DelegateName.toLowerCase().includes(search.toLowerCase()) ||
                      delegate.PhoneNumber?.includes(search)
                    )
                    .map(delegate => (
                      <div 
                        key={delegate.DelegateID}
                        className="p-3 border border-gray-200 rounded-lg cursor-pointer transition-all hover:shadow-md hover:border-blue-400 hover:bg-blue-50"
                        onClick={() => handleSelectDelegate(delegate)}
                      >
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-blue-100 rounded-full flex-shrink-0">
                            <User size={16} className="text-blue-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-medium text-gray-900 text-sm truncate">{delegate.DelegateName}</h3>
                            <p className="text-xs text-gray-500 truncate">{delegate.PhoneNumber}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
              
              {/* رسالة عدم وجود نتائج */}
              {!selectedDelegate && delegates.filter(d => 
                d.DelegateName.toLowerCase().includes(search.toLowerCase()) ||
                d.PhoneNumber?.includes(search)
              ).length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">لا توجد نتائج تطابق البحث</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* قسم العملاء يظهر فقط عند اختيار مندوب */}
        {selectedDelegate && (
          <>
            {/* إحصائيات المندوب */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6 max-md:rounded-none max-md:shadow-sm max-md:p-3 max-md:mb-3">
              <div className="flex justify-between items-center mb-6 max-md:flex-col max-md:items-stretch max-md:gap-3 max-md:mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    <span>حساب المندوب: </span>
                    <span className="text-blue-600">{selectedDelegate.DelegateName}</span>
                  </h2>
                  {delegateInfo?.PhoneNumber && (
                    <p className="text-gray-500 text-sm mt-1">
                      رقم الهاتف: {delegateInfo.PhoneNumber}
                    </p>
                  )}
                </div>
                
                <div className="flex gap-2 max-md:grid max-md:grid-cols-2">
                 <button 
                  onClick={showAdvancedExport}
                  className="flex items-center justify-center gap-1 text-gray-600 bg-gray-100 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors max-md:px-2 max-md:text-xs"
                >
                  <FileSpreadsheet size={16} />
                  <span>تصدير متقدم</span>
                </button>
                  
                  <button 
                    onClick={handlePrint}
                    className="flex items-center justify-center gap-1 text-gray-600 bg-gray-100 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors max-md:px-2 max-md:text-xs"
                  >
                    <Printer size={16} />
                    <span>طباعة</span>
                  </button>
                </div>
              </div>


{/* المعلومات المالية للمندوب */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
  <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200 flex justify-between items-center">
    <div>
      <h3 className="text-sm font-medium text-gray-500">إجمالي العمولات المدفوعة</h3>
      <p className="text-2xl font-bold text-gray-900">{formatMoneyValue(delegateInfo?.TotalWithdrawals || 0)}</p>
      <p className="text-xs text-green-600 mt-1">المبلغ الإجمالي المدفوع للمندوب</p>
    </div>
    <div className="p-2 bg-green-100 rounded-full">
      <DollarSign size={20} className="text-green-600" />
    </div>
  </div>
  
  <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-4 rounded-lg border border-amber-200 flex justify-between items-center">
    <div>
      <h3 className="text-sm font-medium text-gray-500">إجمالي السلف/المديونيات</h3>
      <p className="text-2xl font-bold text-gray-900">{formatMoneyValue(delegateInfo?.TotalDebts ?? 0)}</p>
      <p className="text-xs text-amber-600 mt-1">المبلغ المستحق على المندوب</p>
    </div>
    <div className="flex items-center gap-2">
      <div className="p-2 bg-amber-100 rounded-full">
        <AlertCircle size={20} className="text-amber-600" />
      </div>
      {(canDo('إدارة السلف') || canDo('إدارة المديونيات')) && (
      <button
        onClick={() => setShowDebtsModal(true)}
        className="p-2 bg-amber-200 hover:bg-amber-300 rounded-full transition-colors"
        title="إدارة السلف والمديونيات"
      >
        <Settings size={18} className="text-amber-700" />
      </button>
      )}
    </div>
  </div>
</div>
              
              {/* شريط التبويب */}
              <div className="border-b border-gray-200 mb-6 max-md:mb-4 max-md:overflow-x-auto max-md:pb-1">
                <div className="flex space-x-6 rtl:space-x-reverse max-md:space-x-2 max-md:rtl:space-x-reverse max-md:min-w-max">
                  <button
                    onClick={() => setActiveTab('clients')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'clients'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-1">
                      <User size={16} />
                      <span>عملاء المندوب</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('transactions')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'transactions'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-1">
                      <CreditCard size={16} />
                      <span>سجل المعاملات</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('stats')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'stats'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-1">
                      <PieChart size={16} />
                      <span>الإحصائيات</span>
                    </div>
                  </button>
                </div>
              </div>
              
              {/* عرض المحتوى حسب التبويب النشط */}
              {activeTab === 'clients' && (
                <div>
                  <div className="flex justify-between items-center mb-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                    <div className="flex items-center space-x-4 rtl:space-x-reverse max-md:flex-wrap max-md:gap-2 max-md:space-x-0 max-md:rtl:space-x-0">
  <h3 className="text-lg font-medium text-gray-900">عملاء المندوب</h3>
  <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full">
    <User size={14} />
    <span>معروض: {filteredClients.length}</span>
  </div>
  <div className="flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full">
    <TrendingUp size={14} />
    <span>مسافرين: {stats.traveledClients}</span>
  </div>
  <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 text-amber-700 rounded-full">
    <Clock size={14} />
    <span>عمولة معلقة: {stats.unpaidCommission}</span>
  </div>
</div>
                    
                    <div className="flex gap-2 max-md:grid max-md:grid-cols-2">
                      {/* وضع العرض */}
                      <div className="bg-gray-100 p-1 rounded-lg flex max-md:hidden">
                        <button
                          onClick={() => setActiveView('card')}
                          className={`p-1.5 rounded ${activeView === 'card' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                          title="عرض بطاقات"
                        >
                          <BarChart2 size={18} className="text-gray-600" />
                        </button>
                        <button
                          onClick={() => setActiveView('table')}
                          className={`p-1.5 rounded ${activeView === 'table' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                          title="عرض جدول"
                        >
                          <Clipboard size={18} className="text-gray-600" />
                        </button>
                      </div>
                      
                      {/* زر دفع العمولة */}
                      {canDo('دفع عمولة') && (
                      <button
                        onClick={() => setShowCommissionForm(true)}
                        className={`flex items-center gap-1 px-4 py-2 rounded-lg 
                          ${selectedClients.length > 0 
                          ? 'bg-green-600 hover:bg-green-700 text-white' 
                          : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
                        disabled={selectedClients.length === 0}
                      >
                        <DollarSign size={16} />
                        <span>دفع عمولة ({selectedClients.length})</span>
                      </button>
                      )}
                      
                      {/* زر تصحيح حالة العمولة */}
                      {canDo('تصحيح حالة العمولة') && (
                      <button
                        onClick={handleFixCommissionStatus}
                        className="flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-lg transition-colors"
                      >
                        <AlertCircle size={16} />
                        <span>تصحيح حالة العمولة</span>
                      </button>
                      )}

                      {/* زر الفلترة */}
                      <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg transition-colors"
                      >
                        <Filter size={16} />
                        <span>تصفية</span>
                        <ChevronDown 
                          size={16} 
                          className={`transition-transform duration-200 ${showFilters ? 'transform rotate-180' : ''}`} 
                        />
                      </button>
                      
                      {/* البحث */}
                      <div className="relative max-md:col-span-2">
                        <input
                          type="text"
                          placeholder="بحث..."
                          value={search}
                          onChange={handleSearch}
                          className="pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 max-md:w-full"
                        />
                        <Search className="absolute right-2 top-2.5 h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  
               {/* خيارات الفلترة المحسنة */}
{showFilters && (
  <div className="mt-4 flex flex-wrap gap-2 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 max-md:grid max-md:grid-cols-2 max-md:text-center">
    <button
      onClick={() => handleFilterChange('onlyTraveledUnpaid')}
      className={`px-3 py-1 rounded-full text-sm ${
        filters.onlyTraveledUnpaid 
        ? 'bg-blue-100 text-blue-700 border border-blue-300' 
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      مسافرين - عمولة غير مدفوعة (افتراضي)
    </button>
    <button
      onClick={() => handleFilterChange('onlyPaid')}
      className={`px-3 py-1 rounded-full text-sm ${
        filters.onlyPaid 
        ? 'bg-green-100 text-green-700 border border-green-300' 
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      العمولة المدفوعة
    </button>
    <button
      onClick={() => handleFilterChange('onlyTraveled')}
      className={`px-3 py-1 rounded-full text-sm ${
        filters.onlyTraveled 
        ? 'bg-indigo-100 text-indigo-700 border border-indigo-300' 
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      كل المسافرين
    </button>
    <button
      onClick={() => handleFilterChange('showAll')}
      className={`px-3 py-1 rounded-full text-sm ${
        filters.showAll 
        ? 'bg-purple-100 text-purple-700 border border-purple-300' 
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      كل العملاء
    </button>
  </div>
)}
                  
                  {/* عرض قائمة العملاء */}
                  {activeView === 'table' ? renderClientsList() : renderCardView()}
                </div>
              )}
              
              {/* عرض سجل المعاملات */}
              {activeTab === 'transactions' && (
                <div>
                  <div className="flex justify-between items-center mb-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                    <div className="flex items-center space-x-4 rtl:space-x-reverse max-md:flex-wrap max-md:gap-2 max-md:space-x-0 max-md:rtl:space-x-0">
                      <h3 className="text-lg font-medium text-gray-900">سجل المعاملات</h3>
                      <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full">
                        <CreditCard size={14} />
                        <span>{delegateTransactions.length} معاملة</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* عرض قائمة المعاملات */}
                  {renderTransactionsList()}
                </div>
              )}
              
              {/* عرض الإحصائيات */}
              {activeTab === 'stats' && renderStats()}
            </div>
          </>
        )}


   {/* نموذج دفع العمولة المعدل - مع تصميم محسن وأوسع */}
{showCommissionForm && (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center max-md:items-end max-md:p-0">
    <div className={`relative mx-auto p-0 border w-full max-w-xl shadow-lg rounded-lg bg-white overflow-hidden ${mobileModalClass}`}>
      <div className="bg-gradient-to-r from-green-600 to-green-700 py-4 px-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-white">
            دفع عمولة للمندوب
          </h3>
          <button
            type="button"
            className="text-white hover:text-gray-200 transition-colors"
            onClick={() => setShowCommissionForm(false)}
          >
            <X size={20} />
          </button>
        </div>
      </div>
      
      <div className={`p-6 ${mobileModalBodyClass}`}>
        <form onSubmit={handleCommissionSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="md:col-span-2">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                العملاء المحددين:
              </label>
              <div className="bg-gray-50 p-3 rounded-md border border-gray-200 max-h-32 overflow-y-auto">
                {selectedClients.length === 0 ? (
                  <p className="text-gray-500 text-sm py-1">لم يتم تحديد أي عميل</p>
                ) : (
                  <ul className="space-y-1">
                    {selectedClients.map(clientId => {
                      const clientInfo = clients.find(c => c.PassportID === clientId);
                      return (
                        <li key={clientId} className="flex items-center text-sm py-1">
                          <div className="h-2 w-2 bg-green-500 rounded-full ml-2"></div>
                          {clientInfo ? clientInfo.NameArabic : `عميل #${clientId}`}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
          
          {/* قسم طرق الدفع - محسن وأبسط */}

<div className="mb-4 md:col-span-2">
  <label className="block text-gray-700 text-sm font-medium mb-2">
    طريقة الدفع (الحسابات المصرفية)
  </label>
  
  {isLoadingPaymentMethods ? (
    <div className="text-center py-4 bg-gray-50 rounded-md">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
      <p className="text-sm text-gray-500">جاري تحميل الحسابات المصرفية...</p>
    </div>
  ) : (
    <div className="space-y-3">
      {/* عرض مباشر لجميع الحسابات المصرفية */}
      <div className="bg-gray-50 p-3 rounded-md">
        <h4 className="text-sm font-medium text-gray-700 mb-2">الحسابات المصرفية المتاحة:</h4>
        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
          {paymentMethods
            .filter(isRealBankAccountPaymentMethod)
            .sort((a, b) => (a.name || a.Name).localeCompare(b.name || b.Name))
            .map(account => (
              <div
                key={account.id}
                className={`p-2 rounded-md border cursor-pointer hover:bg-blue-50 ${
                  selectedPaymentMethod?.id === account.id ? 'bg-blue-50 border-blue-300' : 'bg-white'
                }`}
                onClick={() => {
                  setSelectedPaymentMethod(account);
                  setSelectedCategoryId(account.categoryId || account.CategoryID);
                }}
              >
                <div className="flex flex-col">
                  <span className="font-medium truncate">{account.name || account.Name}</span>
                  <span className="text-xs text-gray-500">{account.accountNumber || '-'}</span>
                </div>
              </div>
            ))
          }
        </div>
        
        {paymentMethods.filter(isRealBankAccountPaymentMethod).length === 0 && (
          <div className="text-center py-4">
            <p className="text-gray-500">لا توجد حسابات مصرفية متاحة</p>
            <p className="text-xs text-gray-400 mt-1">يرجى إضافة حسابات مصرفية أولاً</p>
          </div>
        )}
      </div>
      
      {/* عرض تفاصيل الحساب المختار */}
      {selectedPaymentMethod && (
        <div className="bg-blue-50 p-3 rounded border border-blue-100 mt-3">
          <h4 className="text-sm font-medium text-blue-700 mb-1">الحساب المختار:</h4>
          <p className="text-sm"><span className="font-medium">اسم الحساب:</span> {selectedPaymentMethod.Name || selectedPaymentMethod.name}</p>
          {selectedPaymentMethod.accountNumber && (
            <p className="text-sm"><span className="font-medium">رقم الحساب:</span> {selectedPaymentMethod.accountNumber}</p>
          )}
          <p className="text-sm"><span className="font-medium">النوع:</span> حساب مصرفي</p>
        </div>
      )}
    </div>
  )}
</div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">
                  استخدام المبلغ المحسوب تلقائيًا
                </label>
                <button
                  type="button"
                  onClick={calculateTotalCommission}
                  className="bg-blue-100 text-blue-600 px-3 py-1 rounded-md hover:bg-blue-200 transition-colors text-sm"
                >
                  حساب المبلغ تلقائيًا
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                يمكنك حساب مبلغ العمولة تلقائيًا حسب إعدادات العمولات
              </p>
            </div>
            
            <div>
              <label htmlFor="commission-amount" className="block text-gray-700 text-sm font-medium mb-2">
                مبلغ العمولة (ج.م):
              </label>
              <input
                id="commission-amount"
                type="number"
                value={commissionForm.amount}
                onChange={(e) => setCommissionForm({...commissionForm, amount: e.target.value})}
                className="shadow-sm border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                min="1"
                step="0.01"
              />
            </div>
            
            <div>
              <label htmlFor="commission-date" className="block text-gray-700 text-sm font-medium mb-2">
                تاريخ العمولة:
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <Calendar size={16} className="text-gray-400" />
                </div>
                <input
                  id="commission-date"
                  type="date"
                  value={commissionForm.date}
                  onChange={(e) => setCommissionForm({...commissionForm, date: e.target.value})}
                  className="shadow-sm border border-gray-300 rounded-md w-full py-2 pl-3 pr-10 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label htmlFor="commission-notes" className="block text-gray-700 text-sm font-medium mb-2">
                ملاحظات:
              </label>
              <textarea
                id="commission-notes"
                value={commissionForm.notes}
                onChange={(e) => setCommissionForm({...commissionForm, notes: e.target.value})}
                className="shadow-sm border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 h-20 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="أدخل أي ملاحظات إضافية..."
              />
            </div>
          </div>
          
          <div className="flex items-center justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={() => setShowCommissionForm(false)}
              className="text-gray-500 bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-md"
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md flex items-center gap-1"
              disabled={loading || selectedClients.length === 0 || !selectedPaymentMethod}
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin ml-2"></div>
                  <span>جاري التنفيذ...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>تسجيل الدفع</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
)}

        {/* نافذة تفاصيل العمولة */}
        {showCommissionDetails && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60 flex items-center justify-center">
            <div className="relative mx-auto p-0 border w-full max-w-lg shadow-lg rounded-lg bg-white overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 py-4 px-6">
                <div className="flex justify-between items-center max-md:flex-col max-md:items-stretch max-md:gap-3">
                  <h3 className="text-lg font-medium text-white">
                    تفاصيل حساب العمولة
                  </h3>
                  <button
                    type="button"
                    className="text-white hover:text-gray-200 transition-colors"
                    onClick={() => setShowCommissionDetails(false)}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              
              <div className={`p-6 ${mobileModalBodyClass}`}>
                {/* جدول تفاصيل العملاء */}
                <div className="mb-4 max-h-80 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          العميل
                        </th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          الشركة
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          المبلغ المستحق
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {commissionDetails.clients.map((client) => (
                        <tr key={client.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {client.name}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {client.company}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-left text-indigo-600 font-medium">
                            {client.amount} ج.م
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* ملخص الإجماليات */}
                <div className="bg-gray-50 p-4 rounded-md mb-5">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-500">عدد العملاء:</span>
                    <span className="text-sm font-semibold">{commissionDetails.clients.length}</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-500">إجمالي المبالغ المستحقة:</span>
                    <span className="text-sm font-semibold text-indigo-600">{commissionDetails.totalAmount} ج.م</span>
                  </div>
                  <div className="flex justify-between items-center max-md:flex-col max-md:items-stretch max-md:gap-3">
                    <span className="text-sm font-medium text-gray-500">المبلغ المدفوع:</span>
                    <span className="text-sm font-semibold text-green-600">{commissionForm.amount} ج.م</span>
                  </div>
                </div>
                
                {/* زر الإغلاق */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowCommissionDetails(false)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-md flex items-center gap-1"
                  >
                    <span>موافق</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* نافذة تفاصيل المعاملة */}
        {showTransactionModal && selectedTransaction && (
          <DelegateTransactionDetailsModal 
            transaction={selectedTransaction}
            delegateName={selectedDelegate?.DelegateName}
            onClose={() => setShowTransactionModal(false)}
          />
        )}

        {/* مكون إعدادات العمولات */}
        {showCommissionSettings && (
          <CommissionSettings 
            isOpen={showCommissionSettings}
            onClose={() => {
              setShowCommissionSettings(false);
              
              // إعادة تحميل البيانات بشكل كامل عند إغلاق النافذة
              fetchCommissionSettings();
              fetchGlobalSettings();
              
              // إعادة تحميل العملاء لتحديث العمولات المستحقة
              if (selectedDelegate) {
                fetchDelegateClients(selectedDelegate.DelegateID);
              }
            }}
            onSettingsUpdated={() => {
              fetchGlobalSettings();
              fetchCommissionSettings();
              if (selectedDelegate) {
                fetchDelegateClients(selectedDelegate.DelegateID);
              }
            }}
          />
        )}




        {/* نافذة تصحيح حالة العمولة */}
        {showCommissionStatusModal && (
          <CommissionStatusModal
            isOpen={showCommissionStatusModal}
            onClose={() => setShowCommissionStatusModal(false)}
            clients={clients}
            delegateId={selectedDelegate?.DelegateID}
            onFixComplete={handleFixCommissionComplete}
          />
        )}



      {/* نافذة سجل معاملات العمولة */}
{showCommissionTransactionsModal && selectedClientForTransactions && (
  <ClientCommissionTransactionsModal
    isOpen={showCommissionTransactionsModal}
    onClose={() => {
      setShowCommissionTransactionsModal(false);
      setSelectedClientForTransactions(null);
    }}
    clientId={selectedClientForTransactions.PassportID}
    clientName={selectedClientForTransactions.NameArabic}
  />
)}

        {/* نافذة إعادة تخصيص العمولة */}
      
{showDebtConfirmModal && debtConfirmData && (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center max-md:items-end max-md:p-0">
    <div className={`relative mx-auto p-0 border w-full max-w-2xl shadow-lg rounded-lg bg-white overflow-hidden ${mobileModalClass}`}>
      <div className="bg-gradient-to-r from-orange-600 to-red-700 py-4 px-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-white">
            إعادة تخصيص العمولة
          </h3>
          <button
            onClick={() => {
              setShowDebtConfirmModal(false);
              setDebtConfirmData(null);
            }}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      </div>
      
      <div className={`p-6 ${mobileModalBodyClass}`}>
        <div className="mb-4 bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <Info className="h-5 w-5 text-blue-400" />
            </div>
            <div className="mr-3">
              <h3 className="text-sm font-medium text-blue-800">كيف ستتم العملية؟</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>1. سيتم تسجيل <strong>سلفة</strong> على المندوب الأصلي</p>
                <p>2. سيتم إضافة العمولة للمندوب الجديد</p>
                <p>3. ستُخصم السلف/المديونياتة من عمولات المندوب الأصلي القادمة</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mb-4 bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-800 mb-2">تفاصيل العملية:</h4>
          <div className="text-sm space-y-1">
            <p><strong>العميل:</strong> {debtConfirmData.clientName}</p>
            <p><strong>من المندوب:</strong> {debtConfirmData.originalDelegateName}</p>
            <p><strong>إلى المندوب:</strong> {debtConfirmData.currentDelegateName}</p>
          </div>
        </div>
        
        {/* قسم تحديد المبلغ */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            مبلغ العمولة:
          </label>
          <div className="flex gap-2 mb-2">
            <input 
              type="number"
              id="commissionAmount"
              defaultValue={debtConfirmData.amount}
              className="flex-1 border border-gray-300 rounded px-3 py-2"
              placeholder="أدخل المبلغ يدوياً"
            />
            <button
              onClick={() => {
                // حساب العمولة تلقائياً للعميل
                const client = clients.find(c => c.PassportID === debtConfirmData.clientId);
                if (client) {
                  const calculatedCommission = calculateCommission(client);
                  document.getElementById('commissionAmount').value = calculatedCommission || debtConfirmData.amount;
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              حساب تلقائي
            </button>
          </div>
        </div>
        

{/* قسم طرق الدفع */}
<div className="mb-4">
  <label className="block text-sm font-medium text-gray-700 mb-2">
    طريقة دفع العمولة الجديدة (الحسابات المصرفية):
  </label>
  
  {isLoadingPaymentMethods ? (
    <div className="text-center py-4">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
      <p className="text-sm text-gray-500 mt-2">جاري تحميل الحسابات المصرفية...</p>
    </div>
  ) : (
    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded p-3">
      {/* الحسابات المصرفية فقط */}
      {paymentMethods && paymentMethods.filter(isRealBankAccountPaymentMethod).length > 0 ? (
        paymentMethods.filter(isRealBankAccountPaymentMethod).map(method => (
          <label key={method.id} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
            <input 
              type="radio" 
              name="paymentMethod" 
              value={method.id}
              defaultChecked={paymentMethods.filter(isRealBankAccountPaymentMethod).indexOf(method) === 0}
              className="ml-2"
            />
            <div className="flex items-center">
              <div className="p-1 bg-blue-100 rounded-full ml-2">
                <CreditCard size={16} className="text-blue-600" />
              </div>
              <div>
                <span className="font-medium">{method.Name || method.name}</span>
                {method.accountNumber && (
                  <span className="text-sm text-gray-500 block">
                    {method.accountNumber}
                  </span>
                )}
              </div>
            </div>
          </label>
        ))
      ) : (
        <p className="text-sm text-gray-500 text-center py-2">
          لا توجد حسابات مصرفية متاحة
        </p>
      )}
    </div>
  )}
</div>
        
        <div className="flex justify-end space-x-2 rtl:space-x-reverse">
          <button
            onClick={() => {
              setShowDebtConfirmModal(false);
              setDebtConfirmData(null);
            }}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 max-md:w-full max-md:py-3"
          >
            إلغاء
          </button>
          <button
  onClick={() => {
    const amountInput = document.getElementById('commissionAmount');
    const selectedPaymentRadio = document.querySelector('input[name="paymentMethod"]:checked');
    
    if (!selectedPaymentRadio) {
      toast.error('يرجى اختيار طريقة الدفع');
      return;
    }
    
    const amount = toSafeNumber(amountInput.value) || debtConfirmData.amount;
    
    let selectedMethod = paymentMethods.find(m => String(m.id) === String(selectedPaymentRadio.value) && isRealBankAccountPaymentMethod(m));
    
    if (!selectedMethod) {
      toast.error('طريقة الدفع المحددة غير صحيحة. يجب اختيار حساب مصرفي فعلي فقط.');
      return;
    }
    
    console.log('طريقة الدفع المختارة:', selectedMethod);
    
    // تحديث بيانات التأكيد بالمبلغ الجديد
    const updatedDebtData = {
      ...debtConfirmData,
      amount: amount
    };
    
    executeReassignWithDebt(selectedMethod, updatedDebtData);
  }}
  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
>
  تأكيد إعادة التخصيص
</button>
        </div>
      </div>
    </div>
  </div>
)}


        {/* نافذة التصدير المتقدم - أضف هذا هنا */}
{isExportModalOpen && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
   <div className="bg-white w-full max-w-3xl rounded-xl shadow-2xl p-6 m-4 max-h-[90vh] overflow-y-auto">
     <h2 className="text-2xl font-bold mb-6 flex items-center">
       <Settings className="text-blue-600 ml-2" />
       خيارات التصدير المتقدمة - {selectedDelegate?.DelegateName}
     </h2>
     
     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
       {/* نوع التصدير */}
       <div>
         <h3 className="text-lg font-medium mb-3">نوع التصدير</h3>
         <div className="space-y-2">
         <label className="flex items-center">
  <input
    type="radio"
    value="excel"
    checked={exportOptions.format === 'excel'}
    onChange={(e) => setExportOptions({...exportOptions, format: e.target.value})}
    className="ml-2"
  />
  <FileSpreadsheet size={18} className="ml-2 text-green-600" />
  ملف Excel متقدم (.xlsx) - مُنسق ومُحسن
</label>
           
           <label className="flex items-center">
             <input
               type="radio"
               value="pdf"
               checked={exportOptions.format === 'pdf'}
               onChange={(e) => setExportOptions({...exportOptions, format: e.target.value})}
               className="ml-2"
             />
             <FileText size={18} className="ml-2 text-red-600" />
             ملف PDF
           </label>
         </div>
       </div>
       
       {/* نوع البيانات */}
       <div>
         <h3 className="text-lg font-medium mb-3">البيانات المطلوبة</h3>
         <div className="space-y-2">
           <label className="flex items-center">
             <input
               type="radio"
               value="clients"
               checked={exportOptions.dataType === 'clients'}
               onChange={(e) => setExportOptions({...exportOptions, dataType: e.target.value})}
               className="ml-2"
             />
             <User size={16} className="ml-1 text-blue-600" />
             عملاء المندوب ({filteredClients.length} عميل)
           </label>
           <label className="flex items-center">
             <input
               type="radio"
               value="transactions"
               checked={exportOptions.dataType === 'transactions'}
               onChange={(e) => setExportOptions({...exportOptions, dataType: e.target.value})}
               className="ml-2"
             />
             <CreditCard size={16} className="ml-1 text-purple-600" />
             معاملات المندوب ({delegateTransactions.length} معاملة)
           </label>
         </div>
       </div>
       
       {/* فلتر الحالات للعملاء */}
       {exportOptions.dataType === 'clients' && (
         <div>
           <h3 className="text-lg font-medium mb-3">حالات العملاء</h3>
           <div className="space-y-2">
             <label className="flex items-center">
               <input
                 type="checkbox"
                 checked={exportOptions.selectedStatuses.includes('all')}
                 onChange={(e) => {
                   if (e.target.checked) {
                     setExportOptions({
                       ...exportOptions,
                       selectedStatuses: ['all']
                     });
                   } else {
                     setExportOptions({
                       ...exportOptions,
                       selectedStatuses: []
                     });
                   }
                 }}
                 className="ml-2"
               />
               جميع العملاء
             </label>
             <label className="flex items-center">
               <input
                 type="checkbox"
                 checked={exportOptions.selectedStatuses.includes('traveled')}
                 onChange={(e) => {
                   const newStatuses = e.target.checked
                     ? [...exportOptions.selectedStatuses.filter(s => s !== 'all'), 'traveled']
                     : exportOptions.selectedStatuses.filter(s => s !== 'traveled');
                   setExportOptions({
                     ...exportOptions,
                     selectedStatuses: newStatuses
                   });
                 }}
                 className="ml-2"
               />
               <TrendingUp size={16} className="ml-1 text-green-600" />
               العملاء المسافرين فقط
             </label>
             <label className="flex items-center">
               <input
                 type="checkbox"
                 checked={exportOptions.selectedStatuses.includes('paid')}
                 onChange={(e) => {
                   const newStatuses = e.target.checked
                     ? [...exportOptions.selectedStatuses.filter(s => s !== 'all'), 'paid']
                     : exportOptions.selectedStatuses.filter(s => s !== 'paid');
                   setExportOptions({
                     ...exportOptions,
                     selectedStatuses: newStatuses
                   });
                 }}
                 className="ml-2"
               />
               <Check size={16} className="ml-1 text-blue-600" />
               العمولة مدفوعة
             </label>
             <label className="flex items-center">
               <input
                 type="checkbox"
                 checked={exportOptions.selectedStatuses.includes('unpaid')}
                 onChange={(e) => {
                   const newStatuses = e.target.checked
                     ? [...exportOptions.selectedStatuses.filter(s => s !== 'all'), 'unpaid']
                     : exportOptions.selectedStatuses.filter(s => s !== 'unpaid');
                   setExportOptions({
                     ...exportOptions,
                     selectedStatuses: newStatuses
                   });
                 }}
                 className="ml-2"
               />
               <X size={16} className="ml-1 text-amber-600" />
               العمولة غير مدفوعة
             </label>
           </div>
         </div>
       )}
       
       {/* نطاق البيانات */}
       <div>
         <h3 className="text-lg font-medium mb-3">نطاق التاريخ</h3>
         <div className="space-y-2">
           <label className="flex items-center">
             <input
               type="radio"
               value="all"
               checked={exportOptions.dateRange === 'all'}
               onChange={(e) => setExportOptions({...exportOptions, dateRange: e.target.value})}
               className="ml-2"
             />
             جميع الفترات
           </label>
           <label className="flex items-center">
             <input
               type="radio"
               value="filtered"
               checked={exportOptions.dateRange === 'filtered'}
               onChange={(e) => setExportOptions({...exportOptions, dateRange: e.target.value})}
               className="ml-2"
             />
             البيانات المفلترة الحالية
           </label>
           <label className="flex items-center">
             <input
               type="radio"
               value="custom"
               checked={exportOptions.dateRange === 'custom'}
               onChange={(e) => setExportOptions({...exportOptions, dateRange: e.target.value})}
               className="ml-2"
             />
             فترة مخصصة
           </label>
         </div>
         
         {/* التاريخ المخصص */}
         {exportOptions.dateRange === 'custom' && (
           <div className="mt-3 space-y-2">
             <div>
               <label className="block text-sm text-gray-600 mb-1">من تاريخ</label>
               <input
                 type="date"
                 value={exportOptions.customStartDate}
                 onChange={(e) => setExportOptions({...exportOptions, customStartDate: e.target.value})}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
               />
             </div>
             <div>
               <label className="block text-sm text-gray-600 mb-1">إلى تاريخ</label>
               <input
                 type="date"
                 value={exportOptions.customEndDate}
                 onChange={(e) => setExportOptions({...exportOptions, customEndDate: e.target.value})}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
               />
             </div>
           </div>
         )}
       </div>
       
       {/* خيارات إضافية */}
       <div>
         <h3 className="text-lg font-medium mb-3">خيارات إضافية</h3>
         <div className="space-y-2">
           <label className="flex items-center">
             <input
               type="checkbox"
               checked={exportOptions.includeSummary}
               onChange={(e) => setExportOptions({...exportOptions, includeSummary: e.target.checked})}
               className="ml-2"
             />
             تضمين الملخص الإحصائي
           </label>
           <label className="flex items-center">
             <input
               type="checkbox"
               checked={exportOptions.includeFilters}
               onChange={(e) => setExportOptions({...exportOptions, includeFilters: e.target.checked})}
               className="ml-2"
             />
             تضمين معلومات الفلاتر
           </label>
         </div>
       </div>
       
       {/* معلومات المندوب */}
       <div>
         <h3 className="text-lg font-medium mb-3">معلومات المندوب</h3>
         <div className="bg-blue-50 p-3 rounded-lg">
           <div className="text-sm space-y-1">
             <p><span className="font-medium">الاسم:</span> {selectedDelegate?.DelegateName}</p>
             <p><span className="font-medium">الهاتف:</span> {selectedDelegate?.PhoneNumber || 'غير محدد'}</p>
             <p><span className="font-medium">إجمالي العملاء:</span> {stats.totalClients}</p>
             <p><span className="font-medium">العملاء المسافرين:</span> {stats.traveledClients}</p>
             <p><span className="font-medium">المعاملات:</span> {delegateTransactions.length}</p>
           </div>
         </div>
       </div>
     </div>
     
     {/* معاينة البيانات */}
     <div className="mt-6 p-4 bg-gray-50 rounded-lg">
       <h4 className="font-medium text-gray-700 mb-2">معاينة التصدير:</h4>
       <div className="text-sm text-gray-600 grid grid-cols-2 gap-4">
         <div>
           <p><span className="font-medium">عدد السجلات:</span> {getExportData().length}</p>
           <p><span className="font-medium">نوع البيانات:</span> {exportOptions.dataType === 'clients' ? 'عملاء المندوب' : 'معاملات المندوب'}</p>
         </div>
         <div>
           <p><span className="font-medium">تنسيق الملف:</span> {exportOptions.format === 'excel' ? 'Excel متقدم' : 'PDF'}</p>
           <p><span className="font-medium">الفترة:</span> {
             exportOptions.dateRange === 'all' ? 'جميع الفترات' :
             exportOptions.dateRange === 'filtered' ? 'البيانات المفلترة' :
             'فترة مخصصة'
           }</p>
         </div>
       </div>
       
       {/* تنبيه إذا لم تكن هناك بيانات */}
       {getExportData().length === 0 && (
         <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
           <div className="flex items-center">
             <AlertCircle className="text-amber-600 ml-2" size={20} />
             <span className="text-amber-800 font-medium">تنبيه: لا توجد بيانات تطابق معايير التصدير المحددة</span>
           </div>
         </div>
       )}
     </div>
     
     <div className="flex justify-end gap-3 mt-6">
       <button
         onClick={() => setIsExportModalOpen(false)}
         className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 max-md:w-full max-md:py-3"
       >
         إلغاء
       </button>
       <button
         onClick={executeExport}
         disabled={getExportData().length === 0}
         className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
       >
         <Download size={18} />
         تصدير الآن ({getExportData().length} سجل)
       </button>
     </div>
   </div>
 </div>
)}




        {/* أنماط الطباعة */}
        <style dangerouslySetInnerHTML={{
          __html: `
          @media print {
            button, select, input[type="checkbox"], input[type="text"] {
              display: none !important;
            }
            body, .bg-white {
              background: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .shadow-md, .shadow-lg, .rounded-lg, .rounded-xl {
              box-shadow: none !important;
              border: 1px solid #eee !important;
            }
            @page {
              size: landscape;
            }
          }
          `
        }} />




        {/* نافذة التأكيد المخصصة */}
        {showConfirmDialog && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center max-md:items-end max-md:p-0">
            <div className="relative mx-auto p-0 border w-full max-w-lg shadow-lg rounded-lg bg-white overflow-hidden">
              <div className="bg-gradient-to-r from-amber-600 to-amber-700 py-4 px-6">
                <div className="flex justify-between items-center max-md:flex-col max-md:items-stretch max-md:gap-3">
                  <h3 className="text-lg font-medium text-white">
                    تأكيد العملية
                  </h3>
                </div>
              </div>
              
              <div className={`p-6 ${mobileModalBodyClass}`}>
                <div className="mb-6 whitespace-pre-line text-sm text-gray-700">
                  {confirmMessage}
                </div>
                
                <div className="flex justify-end gap-3 max-md:flex-col-reverse max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:bg-white max-md:border-t max-md:p-3 max-md:z-10">
                  <button
                    onClick={() => handleConfirmResponse(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 max-md:w-full max-md:py-3"
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={() => handleConfirmResponse(true)}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                  >
                    متابعة
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}





        {/* نافذة إدارة السلف */}
{showDebtsModal && (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center max-md:items-end max-md:p-0">
    <div className="relative mx-auto p-0 border w-full max-w-6xl shadow-lg rounded-lg bg-white overflow-hidden max-h-[90vh]">
      <div className="bg-gradient-to-r from-amber-600 to-orange-700 py-4 px-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-white">
            إدارة سلف ومديونيات المندوب: {selectedDelegate?.DelegateName}
          </h3>
          <button
            onClick={() => {
              setShowDebtsModal(false);
              setShowAddDebtForm(false);
              setEditingDebt(null);
            }}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      </div>
      
      <div className="p-6 overflow-y-auto max-h-[80vh]">
        {/* ملخص السلف */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <h4 className="text-red-800 font-medium">السلف/المديونيات النشطة</h4>
            <p className="text-2xl font-bold text-red-900">
              {debts.filter(d => d.Status !== 'مسددة').length}
            </p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
            <h4 className="text-amber-800 font-medium">إجمالي المبلغ المتبقي</h4>
            <p className="text-2xl font-bold text-amber-900">
              {formatMoneyValue(debts.reduce((sum, debt) => sum + (debt.Status !== 'مسددة' ? Number(debt.RemainingAmount || 0) : 0), 0))}
            </p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <h4 className="text-green-800 font-medium">المسددة</h4>
            <p className="text-2xl font-bold text-green-900">
              {debts.filter(d => d.Status === 'مسددة').length}
            </p>
          </div>
        </div>
        
        {/* عمليات مالية دقيقة مرتبطة بالخزينة */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between gap-3 mb-4 max-md:flex-col max-md:items-stretch">
            <div>
              <h4 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Landmark size={20} className="text-blue-600" />
                عمليات السلفة / رد السلفة أو سداد المديونية
              </h4>
              <p className="text-sm text-slate-500 mt-1">أخذ السلفة يسجل سحبًا من الحساب. رد السلفة/سداد المديونية يسجل إيداعًا للحساب. إضافة مديونية بدون خزينة تتم من الزر بالأسفل.</p>
            </div>
            <span className="bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-xs font-bold">دقة حسابية + خزينة</span>
          </div>

          <form onSubmit={handleDelegateFinancialOperation} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">نوع العملية</label>
              <select
                value={debtOperationForm.operationType}
                onChange={(e) => setDebtOperationForm({ ...debtOperationForm, operationType: e.target.value, debtId: '' })}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 bg-white"
              >
                <option value="advance">المندوب أخذ سلفة</option>
                <option value="repay_advance">المندوب رد سلفة / سدد مديونية</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">المبلغ</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={debtOperationForm.amount}
                onChange={(e) => setDebtOperationForm({ ...debtOperationForm, amount: e.target.value })}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5"
                placeholder="0.00"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">التاريخ</label>
              <input
                type="date"
                value={debtOperationForm.date}
                onChange={(e) => setDebtOperationForm({ ...debtOperationForm, date: e.target.value })}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">تصنيف الدفع</label>
              <select
                value={debtOperationForm.categoryId}
                onChange={(e) => handleDebtOperationCategoryChange(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 bg-white"
              >
                <option value="">كل الحسابات</option>
                {categories.map(category => (
                  <option key={category.id || category.CategoryID} value={category.id || category.CategoryID}>
                    {category.name || category.Name}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">طريقة الدفع / الحساب</label>
              <select
                value={debtOperationForm.paymentMethodId}
                onChange={(e) => setDebtOperationForm({ ...debtOperationForm, paymentMethodId: e.target.value })}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 bg-white"
                required
                disabled={isDebtOperationLoading}
              >
                <option value="">اختر الحساب</option>
                {debtOperationPaymentMethods.map(method => (
                  <option key={method.id} value={method.id}>
                    {method.name || method.Name} {method.accountNumber ? `- ${method.accountNumber}` : ''}
                  </option>
                ))}
              </select>
            </div>

            {debtOperationForm.operationType === 'repay_advance' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">سداد بند محدد</label>
                <select
                  value={debtOperationForm.debtId}
                  onChange={(e) => setDebtOperationForm({ ...debtOperationForm, debtId: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 bg-white"
                >
                  <option value="">تلقائي حسب الأولوية</option>
                  {debts.filter(d => d.Status !== 'مسددة' && !d.IsDeleted && Number(d.RemainingAmount || 0) > 0).map(debt => (
                    <option key={debt.DebtID} value={debt.DebtID}>
                      #{debt.DebtID} - {debt.DebtType || (debt.SourceOperationType === 'manual_debt' ? 'مديونية' : 'سلفة')} - متبقي {formatMoneyValue(debt.RemainingAmount)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className={debtOperationForm.operationType === 'repay_advance' ? 'md:col-span-8' : 'md:col-span-10'}>
              <label className="block text-sm font-medium text-gray-700 mb-1">ملاحظات العملية</label>
              <input
                type="text"
                value={debtOperationForm.notes}
                onChange={(e) => setDebtOperationForm({ ...debtOperationForm, notes: e.target.value })}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5"
                placeholder="مثال: سلفة تشغيل / رد جزء من السلفة/المديونية"
              />
            </div>

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={isDebtOperationLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2.5 rounded-xl font-bold flex items-center justify-center gap-2"
              >
                {isDebtOperationLoading ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                تنفيذ العملية
              </button>
            </div>
          </form>
        </div>

        {/* سجل العمليات التفصيلي داخل إدارة السلف والمديونيات */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-6">
          <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 max-md:flex-col max-md:items-stretch">
            <div>
              <h4 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <FileText size={20} className="text-indigo-600" />
                سجل العمليات التفصيلي للسلف والمديونيات
              </h4>
              <p className="text-sm text-slate-500 mt-1">
                كل عملية تتم من هذا الجزء تظهر هنا كمعاملة مستقلة يمكن فتح تفاصيلها، وخصوصًا رد السلفة/سداد المديونية.
              </p>
            </div>
            <button
              onClick={() => selectedDelegate && fetchDelegateTransactions(selectedDelegate.DelegateID)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
            >
              <RefreshCw size={16} /> تحديث العمليات
            </button>
          </div>

          {getDebtOperationTransactions().length === 0 ? (
            <div className="p-6 text-center text-gray-500">لا توجد عمليات سلفة أو مديونية مسجلة حتى الآن</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-white">
                  <tr>
                    <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">رقم المعاملة</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">التاريخ</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">نوع العملية</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">المبلغ</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">أثر الخزينة</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">تفاصيل البنود</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-slate-500">الحساب</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-slate-500">الإجراءات</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {getDebtOperationTransactions().map(tx => {
                    const info = getDebtOperationInfo(tx);
                    return (
                      <tr key={`debt-operation-${tx.TransactionID}`} className="hover:bg-indigo-50/40">
                        <td className="px-4 py-3 text-sm font-bold text-slate-900">#{tx.TransactionID}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">
                          <div>{formatDate(tx.Date)}</div>
                          <div className="text-xs text-slate-400">{new Date(tx.Date).toLocaleTimeString('ar-EG')}</div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${info.opType === 'repay_advance' ? 'bg-green-100 text-green-700' : info.opType === 'manual_debt' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                            {info.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-slate-900">{formatMoneyValue(info.amount)}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{info.treasuryEffect}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">
                          {info.details.length > 0 ? (
                            <div className="space-y-1">
                              {info.details.slice(0, 2).map((detail, index) => (
                                <div key={index} className="text-xs">
                                  <span className="font-bold">#{detail.debtId || '-'}</span> - {detail.debtType || 'بند'} - مبلغ {formatMoneyValue(detail.paidAmount || detail.amount || detail.originalAmount || 0)}
                                  {detail.remainingAmount !== undefined && <span className="text-gray-500"> / متبقي {formatMoneyValue(detail.remainingAmount)}</span>}
                                </div>
                              ))}
                              {info.details.length > 2 && <div className="text-xs text-gray-500">+ {info.details.length - 2} بنود أخرى</div>}
                            </div>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">{info.paymentMethodName}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => showTransactionDetails(tx)}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1"
                              title="عرض معاملة تفصيلية"
                            >
                              <Eye size={14} /> تفاصيل
                            </button>
                            <button
                              onClick={() => startEditDebtOperationTransaction(tx)}
                              className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1"
                              title="تعديل هذه العملية ومزامنتها مع السلفة/المديونية والخزينة"
                            >
                              <Edit size={14} /> تعديل
                            </button>
                            <button
                              onClick={() => deleteDebtOperationTransaction(tx)}
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1"
                              title="حذف هذه العملية وعكس أثرها من السلفة/المديونية والخزينة"
                            >
                              <Trash2 size={14} /> حذف
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* أزرار الإجراءات */}
        <div className="flex justify-between items-center mb-6 max-md:flex-col max-md:items-stretch max-md:gap-3">
          <div>
            <h4 className="text-lg font-medium text-gray-900">قائمة السلف والمديونيات</h4>
            <p className="text-sm text-gray-500 mt-1">
              السلفة النقدية تسجل في الخزينة. أما المديونية اليدوية فتُضاف بدون أي حركة خزينة، وتُخصم لاحقًا من العمولة أو تُسدد من خلال رد سلفة/سداد مديونية.
            </p>
          </div>
          <div className="flex items-center gap-2 max-md:grid max-md:grid-cols-2">
            <button
              onClick={() => {
                setShowAddDebtForm(prev => !prev);
                setEditingDebt(null);
              }}
              className="flex items-center justify-center gap-1 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus size={16} />
              إضافة مديونية بدون خزينة
            </button>
            <button
              onClick={fetchDebts}
              className="flex items-center justify-center gap-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <RefreshCw size={16} />
              تحديث
            </button>
          </div>
        </div>

        {/* نموذج إضافة مديونية يدوية بدون خزينة */}
        {showAddDebtForm && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <h5 className="font-medium text-amber-900 mb-1">إضافة مديونية بدون خزينة</h5>
            <p className="text-xs text-amber-700 mb-3">تستخدم للمديونيات القديمة أو التسويات التي لا تريد تسجيلها كحركة خروج/دخول في الخزينة.</p>
            <form onSubmit={handleAddDebt} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">النوع</label>
                <select
                  value={newDebt.debtType}
                  onChange={(e) => setNewDebt({ ...newDebt, debtType: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 bg-white"
                >
                  <option value="مديونية">مديونية بدون خزينة</option>
                  <option value="سلفة">سلفة قديمة بدون خزينة</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">المبلغ</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={newDebt.amount}
                  onChange={(e) => setNewDebt({ ...newDebt, amount: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5"
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ الإثبات</label>
                <input
                  type="date"
                  value={newDebt.date}
                  onChange={(e) => setNewDebt({ ...newDebt, date: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">الأولوية</label>
                <select
                  value={newDebt.priority}
                  onChange={(e) => setNewDebt({ ...newDebt, priority: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 bg-white"
                >
                  <option value={1}>عادية</option>
                  <option value={2}>متوسطة</option>
                  <option value={3}>عالية</option>
                  <option value={4}>عاجلة</option>
                </select>
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">سبب المديونية</label>
                <input
                  type="text"
                  value={newDebt.reason}
                  onChange={(e) => setNewDebt({ ...newDebt, reason: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5"
                  placeholder="مثال: مديونية قديمة / تسوية حساب سابق"
                  required
                />
              </div>
              <div className="md:col-span-1 flex gap-2">
                <button type="submit" className="w-full bg-amber-600 hover:bg-amber-700 text-white px-3 py-2.5 rounded-xl font-bold">
                  حفظ
                </button>
              </div>
            </form>
          </div>
        )}
        
        {/* نموذج إضافة/تعديل سلفة */}
        {editingDebt && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h5 className="font-medium text-blue-900 mb-3">
              تعديل السلفة/المديونية
            </h5>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              handleEditDebt(editingDebt.DebtID, {
                amount: toSafeNumber(formData.get('amount')),
                reason: formData.get('reason'),
                priority: parseInt(formData.get('priority')),
                updatedBy: getCurrentUser()
              });
            }}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    المبلغ (ج.م) *
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={editingDebt ? editingDebt.Amount : newDebt.amount}
                    onChange={(e) => editingDebt ? 
                      setEditingDebt({...editingDebt, Amount: e.target.value}) :
                      setNewDebt({...newDebt, amount: e.target.value})
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2 max-md:rounded-xl max-md:py-3"
                    placeholder="أدخل المبلغ"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الأولوية
                  </label>
                  <select
                    name="priority"
                    value={editingDebt ? editingDebt.Priority : newDebt.priority}
                    onChange={(e) => editingDebt ?
                      setEditingDebt({...editingDebt, Priority: parseInt(e.target.value)}) :
                      setNewDebt({...newDebt, priority: parseInt(e.target.value)})
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2 max-md:rounded-xl max-md:py-3"
                  >
                    <option value={1}>عادية</option>
                    <option value={2}>متوسطة</option>
                    <option value={3}>عالية</option>
                    <option value={4}>عاجلة</option>
                  </select>
                </div>
                
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الإجراءات
                  </label>
                  <div className="flex gap-2 max-md:grid max-md:grid-cols-2">
                    <button
                      type="submit"
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                    >
                      تحديث
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddDebtForm(false);
                        setEditingDebt(null);
                        setNewDebt({ amount: '', reason: '', priority: 1, debtType: 'مديونية', date: new Date().toISOString().split('T')[0] });
                      }}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded transition-colors"
                    >
                      إلغاء
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  السبب *
                </label>
                <textarea
                  name="reason"
                  value={editingDebt ? editingDebt.Reason : newDebt.reason}
                  onChange={(e) => editingDebt ?
                    setEditingDebt({...editingDebt, Reason: e.target.value}) :
                    setNewDebt({...newDebt, reason: e.target.value})
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 h-20 resize-none"
                  placeholder="اكتب السبب..."
                  required
                />
              </div>
            </form>
          </div>
        )}
        
        {/* جدول السلف/المديونيات النشطة */}
        <div className="mb-8">
          <h5 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
            <AlertCircle size={20} className="text-red-600" />
            السلف/المديونيات النشطة
          </h5>
          
          {debts.filter(d => d.Status !== 'مسددة').length === 0 ? (
            <div className="text-center py-8 bg-green-50 rounded-lg">
              <div className="text-green-600 text-lg">✅ لا توجد سلف أو مديونيات نشطة</div>
              <p className="text-sm text-green-600 mt-1">المندوب لا يوجد عليه أي سلف أو مديونيات حالياً</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">رقم البند</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">النوع</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">المبلغ الأصلي</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">المبلغ المسدد</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">المبلغ المتبقي</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الأولوية</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">السبب</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">تاريخ الإنشاء</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">الإجراءات</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {debts.filter(d => d.Status !== 'مسددة').map((debt) => (
                    <tr key={debt.DebtID} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          #{debt.DebtID}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${debt.DebtType === 'مديونية' || debt.SourceOperationType === 'manual_debt' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                          {debt.DebtType || (debt.SourceOperationType === 'manual_debt' ? 'مديونية' : 'سلفة')}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatMoneyValue(debt.Amount)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                        {formatMoneyValue(debt.PaidAmount || 0)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                        {formatMoneyValue(debt.RemainingAmount)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          debt.Priority === 4 ? 'bg-red-100 text-red-800' :
                          debt.Priority === 3 ? 'bg-orange-100 text-orange-800' :
                          debt.Priority === 2 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {debt.Priority === 4 ? 'عاجلة' :
                           debt.Priority === 3 ? 'عالية' :
                           debt.Priority === 2 ? 'متوسطة' : 'عادية'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate" title={debt.Reason}>
                          {debt.Reason}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(debt.CreatedAt).toLocaleDateString('ar-EG')}
                      </td>

                            <td className="px-4 py-4 whitespace-nowrap text-center">
                              <div className="flex items-center justify-center gap-1">
                                {debt.RelatedTransactionID && (
                                  <button
                                    onClick={() => {
                                      const tx = delegateTransactions.find(t => Number(t.TransactionID) === Number(debt.RelatedTransactionID));
                                      if (tx) showTransactionDetails(tx);
                                      else toast.error('لم يتم العثور على معاملة السجل المرتبطة. اضغط تحديث العمليات أولًا.');
                                    }}
                                    className="text-indigo-600 hover:text-indigo-800 p-1.5 hover:bg-indigo-50 rounded-full transition duration-200"
                                    title="عرض المعاملة التفصيلية"
                                  >
                                    <Eye size={16} />
                                  </button>
                                )}
                                <button
                                  onClick={() => setEditingDebt(debt)}
                                  className="text-blue-600 hover:text-blue-800 p-1.5 hover:bg-blue-50 rounded-full transition duration-200"
                                  title="تعديل"
                                >
                                  <Settings size={16} />
                                </button>
                                <button
                                  disabled={Number(debt.PaidAmount || 0) > 0}
                                  onClick={() => {
                                    if (Number(debt.PaidAmount || 0) > 0) {
                                      toast.error('لا يمكن حذف بند تم سداد جزء منه. احذف أو عدّل معاملات السداد أولًا.');
                                      return;
                                    }
                                    if (window.confirm(`هل أنت متأكد من حذف هذا البند؟\nالمبلغ: ${formatMoneyValue(debt.RemainingAmount)}\nالسبب: ${debt.Reason}`)) {
                                      handleDeleteDebt(debt.DebtID);
                                    }
                                  }}
                                  className={`p-1.5 rounded-full transition duration-200 ${Number(debt.PaidAmount || 0) > 0 ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:text-red-800 hover:bg-red-50'}`}
                                  title={Number(debt.PaidAmount || 0) > 0 ? 'لا يمكن حذف بند تم سداد جزء منه قبل حذف/تعديل السداد' : 'حذف'}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>


                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* سجل السلف والمديونيات */}
        <div>
          <h5 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Check size={20} className="text-green-600" />
            سجل السلف والمديونيات ({debtsHistory.length || debts.length})
          </h5>
          
          {(debtsHistory.length || debts.length) === 0 ? (
            <div className="text-center py-4 bg-gray-50 rounded-lg">
              <p className="text-gray-500">لا يوجد سجل سلف أو مديونيات</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">رقم البند</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">النوع</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الحالة</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الأصل</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">المسدد</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">المتبقي</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">السبب</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">آخر تحديث</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">الإجراءات</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(debtsHistory.length ? debtsHistory : debts).map((debt) => (
                    <tr key={`history-${debt.DebtID}`} className={`hover:bg-gray-50 ${debt.IsDeleted ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                          #{debt.DebtID}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${debt.DebtType === 'مديونية' || debt.SourceOperationType === 'manual_debt' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                          {debt.DebtType || (debt.SourceOperationType === 'manual_debt' ? 'مديونية' : 'سلفة')}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                          debt.IsDeleted ? 'bg-gray-100 text-gray-600' :
                          debt.Status === 'مسددة' ? 'bg-green-100 text-green-700' :
                          debt.Status === 'جزئية' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {debt.IsDeleted ? 'محذوفة' : debt.Status || 'معلقة'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatMoneyValue(debt.Amount)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 font-medium">{formatMoneyValue(debt.PaidAmount || 0)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-red-600 font-medium">{formatMoneyValue(debt.RemainingAmount || 0)}</td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate" title={debt.Reason}>{debt.Reason}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {debt.UpdatedAt ? new Date(debt.UpdatedAt).toLocaleDateString('ar-EG') : new Date(debt.CreatedAt).toLocaleDateString('ar-EG')}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        {!debt.IsDeleted ? (
                          <div className="flex items-center justify-center gap-1">
                            {debt.RelatedTransactionID && (
                              <button
                                onClick={() => {
                                  const tx = delegateTransactions.find(t => Number(t.TransactionID) === Number(debt.RelatedTransactionID));
                                  if (tx) showTransactionDetails(tx);
                                  else toast.error('لم يتم العثور على معاملة السجل المرتبطة. اضغط تحديث العمليات أولًا.');
                                }}
                                className="text-indigo-600 hover:text-indigo-800 p-1.5 hover:bg-indigo-50 rounded-full transition duration-200"
                                title="عرض المعاملة التفصيلية"
                              >
                                <Eye size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setEditingDebt(debt);
                                setShowAddDebtForm(false);
                              }}
                              className="text-blue-600 hover:text-blue-800 p-1.5 hover:bg-blue-50 rounded-full transition duration-200"
                              title="تعديل من السجل"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              disabled={Number(debt.PaidAmount || 0) > 0}
                              onClick={() => {
                                if (Number(debt.PaidAmount || 0) > 0) {
                                  toast.error('لا يمكن حذف بند تم سداد جزء منه. احذف أو عدّل معاملات السداد أولًا.');
                                  return;
                                }
                                if (window.confirm(`هل أنت متأكد من حذف هذا البند من السجل؟\nالمتبقي: ${formatMoneyValue(debt.RemainingAmount || 0)}\nالسبب: ${debt.Reason}`)) {
                                  handleDeleteDebt(debt.DebtID);
                                }
                              }}
                              className={`p-1.5 rounded-full transition duration-200 ${Number(debt.PaidAmount || 0) > 0 ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:text-red-800 hover:bg-red-50'}`}
                              title={Number(debt.PaidAmount || 0) > 0 ? 'لا يمكن حذف بند تم سداد جزء منه قبل حذف/تعديل السداد' : 'حذف من السجل'}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">لا توجد إجراءات</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
)}



 

      </div>




{/* نافذة تعديل المعاملة */}
{showEditTransactionModal && editingTransaction && (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[95] flex items-center justify-center max-md:items-end max-md:p-0">
    <div className={`relative mx-auto p-0 border w-full max-w-2xl shadow-lg rounded-lg bg-white overflow-hidden ${mobileModalClass}`}>
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 py-4 px-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-white">
            تعديل معاملة المندوب #{editingTransaction.TransactionID}
          </h3>
          <button
            onClick={() => {
              setShowEditTransactionModal(false);
              setEditingTransaction(null);
              resetEditTransactionForm();
            }}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      </div>
      
      <div className={`p-6 ${mobileModalBodyClass}`}>
        <form onSubmit={handleEditTransaction}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                نوع المعاملة:
              </label>
              <select
                value={editTransactionForm.type}
                onChange={(e) => setEditTransactionForm({...editTransactionForm, type: e.target.value})}
                className="w-full border border-gray-300 rounded px-3 py-2 max-md:rounded-xl max-md:py-3"
                required
              >
                <option value="">اختر نوع المعاملة</option>
                <option value="سحب">سحب</option>
                <option value="عمولة">عمولة</option>
                <option value="عمولة (خصم كامل من سلف/مديونيات)">عمولة (خصم كامل من سلف/مديونيات)</option>
                <option value="عمولة (مع خصم سلف/مديونيات جزئي)">عمولة (مع خصم سلف/مديونيات جزئي)</option>
                <option value="سلفة للمندوب">سلفة للمندوب</option>
                <option value="مديونية على المندوب">مديونية على المندوب</option>
                <option value="رد سلفة من المندوب">رد سلفة/سداد مديونية من المندوب</option>
              </select>
            </div>
            
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                المبلغ (ج.م):
              </label>
              <input
                type="number"
                value={editTransactionForm.amount}
                onChange={(e) => setEditTransactionForm({...editTransactionForm, amount: e.target.value})}
                className="w-full border border-gray-300 rounded px-3 py-2 max-md:rounded-xl max-md:py-3"
                required
                min="0.01"
                step="0.01"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                تاريخ المعاملة:
              </label>
              <input
                type="date"
                value={editTransactionForm.date}
                onChange={(e) => setEditTransactionForm({...editTransactionForm, date: e.target.value})}
                className="w-full border border-gray-300 rounded px-3 py-2 max-md:rounded-xl max-md:py-3"
                required
              />
            </div>
            
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                {editTransactionAffectsTreasury ? 'طريقة الدفع / الحساب:' : 'طريقة الدفع / الحساب:'}
              </label>
              <select
                value={editTransactionForm.paymentMethodId || ''}
                onChange={(e) => setEditTransactionForm({...editTransactionForm, paymentMethodId: e.target.value})}
                className="w-full border border-gray-300 rounded px-3 py-2 max-md:rounded-xl max-md:py-3"
                required={editTransactionAffectsTreasury}
                disabled={!editTransactionAffectsTreasury}
              >
                <option value="">{editTransactionAffectsTreasury ? 'اختر طريقة الدفع' : 'لا توجد حركة خزينة لهذه العملية'}</option>
                {paymentMethods.filter(isRealBankAccountPaymentMethod).map(method => (
                  <option key={method.id} value={method.id}>
                    {method.Name || method.name} - {method.accountNumber || 'حساب بنكي'}
                  </option>
                ))}
              </select>
              {!editTransactionAffectsTreasury && (
                <p className="mt-1 text-xs text-purple-600 font-medium">
                  هذه مديونية بدون خزينة؛ التعديل سيؤثر على حساب المندوب والسجل فقط.
                </p>
              )}
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              ملاحظات:
            </label>
            <textarea
              value={editTransactionForm.notes}
              onChange={(e) => setEditTransactionForm({...editTransactionForm, notes: e.target.value})}
              className="w-full border border-gray-300 rounded px-3 py-2 h-20 resize-none"
              placeholder="أدخل ملاحظات إضافية..."
            />
          </div>
          
          {/* معلومات العميل للمزامنة */}
          <div className="mb-4 bg-blue-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-blue-800 mb-2">معلومات المزامنة مع النظام البنكي:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-blue-700 mb-1">اسم العميل:</label>
                <input
                  type="text"
                  value={editTransactionForm.clientDetails.name}
                  onChange={(e) => setEditTransactionForm({
                    ...editTransactionForm,
                    clientDetails: {...editTransactionForm.clientDetails, name: e.target.value}
                  })}
                  className="w-full border border-blue-200 rounded px-2 py-1 text-sm"
                  placeholder="المندوب أو اسم العميل"
                />
              </div>
              
              <div>
                <label className="block text-xs text-blue-700 mb-1">رقم الهاتف:</label>
                <input
                  type="text"
                  value={editTransactionForm.clientDetails.phoneNumbers}
                  onChange={(e) => setEditTransactionForm({
                    ...editTransactionForm,
                    clientDetails: {...editTransactionForm.clientDetails, phoneNumbers: e.target.value}
                  })}
                  className="w-full border border-blue-200 rounded px-2 py-1 text-sm"
                  placeholder="رقم الهاتف"
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 max-md:flex-col-reverse max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:bg-white max-md:border-t max-md:p-3 max-md:z-10">
            <button
              type="button"
              onClick={() => {
                setShowEditTransactionModal(false);
                setEditingTransaction(null);
                resetEditTransactionForm();
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 max-md:w-full max-md:py-3"
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 max-md:w-full max-md:py-3"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>جاري التحديث...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>حفظ التعديلات</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
)}



    </div>
  );
};





export default DelegateAccounts;